// Direct imports for optimal tree shaking
import { Scene } from "@babylonjs/core/scene";
import { Engine } from "@babylonjs/core/Engines/engine";
import { ArcRotateCamera } from "@babylonjs/core/Cameras/arcRotateCamera";
import { DirectionalLight } from "@babylonjs/core/Lights/directionalLight";
import { Matrix, Quaternion, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { Node } from "@babylonjs/core/node";
import { ResourceManager, remapAssetRef, remapBehaviorAssetRefs, remapSheetAssetRefs } from "../loaders/ResourceManager";
import { SceneObject, isCmsManagedBehavior } from "./SceneObject";
import { getTransitionManager } from "./TransitionManager";
import { SceneObjectData } from '../scenedata/sceneobjectdata';
import { TraceSceneInfoFile } from '../scenedata/infofiledata';
import { BehaviorData } from '../scenedata/behaviordata';
import type { ProvenanceData } from '../scenedata/behaviors/provenancedata';
import { BoxCollider, COLLIDER_MESH_SUFFIX, SELECTION_MESH_SUFFIX } from "../behaviors/boxcollider";
import { CONTROLS_MESH_SUFFIX } from "../behaviors/video";
import { Renderer } from "../behaviors/renderer";
import { SphereCollider } from "../behaviors/spherecollider";
import { ConfigData, DEFAULT_ENVIRONMENT_IMAGE_URL } from './configdata';
import type { SafeAreaInsets } from '../scenedata/common';
import { IProgressTracker } from '../utils/ProgressTracker';
import { Utility } from '../utils/Utility';
import { DROPPABLE_ASSET_EXTENSIONS, ALL_3D_EXTENSIONS } from '../utils/AssetTypes';
import { SceneCreation, dropAssetPrimitiveData, ensureBehaviorDataForExtension, impliedBehaviorDataForExtension, withImpliedBehaviorData } from '../loaders/SceneCreation';
import { authoringSrc } from "../loaders/restoreBakedSrc";
import { migrateLegacyConstraints } from '../loaders/LegacyConstraints';
import type { LegacyConstraintNode } from '../loaders/LegacyConstraints';
import { SceneCamera } from './Camera';
import { sceneObjectPlaceHolder } from '../json/scenePlaceholder';
import { Logger, LogLevel } from '../utils/Logger';
import { disposedError } from '../utils/abort';
import type { LoadedSceneResult } from '../loaders/plugins/LoadedSceneResult';
import type { ForeignDocumentData } from '../loaders/formats/gltf21/foreign';
import { TraceZipSceneLoaderPlugin } from '../loaders/plugins/TraceZipSceneLoaderPlugin';
import { GltfSceneLoaderPlugin, resolveAgainst } from '../loaders/plugins/GltfSceneLoaderPlugin';
import {
    getSceneFormatConfig,
    getBundleFormatConfig,
    getSceneDocumentFormatConfig,
    isSceneFormatRef,
    isGltfExtension,
    isLegacySceneFormat,
} from '../loaders/plugins/SceneFormatRegistry';
import { isSceneDocument, readSceneDocumentJson } from '../loaders/formats/gltf21/normalize';
import { isValidUid, mintUid } from '../scenedata/uid';
import { isBundleArchive, toZipBytes } from '../loaders/formats/bundle/zipSniff';
import { SCENE_DOCUMENT_ARCHIVE_ENTRIES } from '../scenedata/constants';
import { HDRCubeTexture } from "@babylonjs/core/Materials/Textures/hdrCubeTexture";
import type { BaseTexture } from "@babylonjs/core/Materials/Textures/baseTexture";
import { ScriptRegistry } from '../scripting/ScriptRegistry';
import { SceneObjectIdentityRegistry } from './SceneObjectIdentity';
import { ScriptBehavior } from '../scripting/ScriptBehavior';
import { ContactShadow } from "./ContactShadow";
import { OccluderMode } from "./OccluderMode";
import { AudioManager } from "../managers/AudioManager";
import { RuntimeSelectionManager } from "../managers/RuntimeSelectionManager";
import type { SceneEventSystem } from "./SceneEventSystem";
import { BehaviorGraph } from "../behaviors/behaviorgraph/behaviorgraph";
import { Properties } from "../behaviors/properties";
import type { TraceBehavior } from "../behaviors/tracebehavior";
import { SystemValueProvider } from "./SystemValues";
import { applySubtreeExclusion, type SubtreeExclusionOptions } from "../export/exportExclusion";

// Re-export so existing `import { SceneEventSystem } from './TraceScene'` still works
export type { SceneEventSystem } from "./SceneEventSystem";

// 2024
// Author: Aaron Post

/** Reserved name for the implicit scene root object */
export const SCENE_ROOT_NAME = "Scene";

/** Options for {@link TraceScene.select}. */
export interface SelectOptions {
    /**
     * Hint that this selection change was additive (e.g. ctrl/shift-click).
     * The runtime ignores this — it always tracks a single "primary" object —
     * but it is forwarded on the `trace-viewer:selection-changed` event so an
     * editor-layer multi-selection model can toggle membership instead of
     * replacing the set. Defaults to `false`.
     */
    additive?: boolean;
}

/**
 * How a top-level fetch should treat the HTTP cache.
 *
 * Only the entry point that opens content passes this; the files a document
 * names never do. Those are content-addressed and immutable — cacheable by URL
 * forever is the whole point of naming them by hash (spec §2.1).
 */
export interface AssetFetchOptions {
    /**
     * Revalidate with the origin instead of serving a fresh-by-age cache entry.
     *
     * `GET /assets/{id}/file` answers a **302 the browser may cache for 60
     * seconds** (spec §2.7), and the blob it redirects to is immutable — so a
     * cached redirect is cached *content*. That is the right trade for a
     * reader: an asset edit reaches a scene that references it within a minute.
     * It is the wrong one for the client that just WROTE the asset, which sees
     * its own publish missing and the change appear a minute later.
     *
     * So an editor reopening content it owns asks for this. It is
     * `cache: 'no-cache'` — a conditional request, answered `304` when nothing
     * moved — not a re-download.
     */
    revalidate?: boolean;
}

/** Options for {@link TraceScene.loadAsset} / `TraceViewer.import`. */
export interface ImportOptions {
    /**
     * Only applies to a scene-format file (.trace/.tracex/.zip/.glxf). An
     * import normally keeps the file's contents under a wrapper object that
     * stands in for the file, and the scene's own save just points back at it.
     * With `unpack`, the contents become ordinary objects of this scene
     * instead: they can be edited, and they are written into the scene's save
     * rather than referenced. Single assets (glb, images, video) ignore it.
     */
    unpack?: boolean;
    /**
     * Where an unpack may copy the reference's own folder files to
     * ("Copy to scene", spec §2.6). See {@link UnpackReferenceOptions.intoFolder}.
     * Ignored unless `unpack` is set.
     */
    intoFolder?: UnpackFolderTarget;
}

/**
 * Where {@link TraceScene.unpackReference} puts the bytes it has to bring
 * across — the caller's uploader, because only the caller knows the folder
 * (`POST /scenes/{id}/files` in the editor, a local directory in a tool).
 *
 * The viewer never talks to an API. It hands over a Blob and takes back the
 * relative uri to name it by.
 */
export interface UnpackFolderTarget {
    /**
     * Copy one file into the destination folder and answer the **relative**
     * uri the document should name it by — `assets/<slug>.<hash16>.<ext>`
     * (spec §2.1), whatever the server minted. No caller predicts it.
     *
     * The server content-addresses on upload, so a file the folder already
     * holds costs nothing and comes back under the name it already has.
     */
    upload(file: Blob, suggestedName: string): Promise<string>;
}

/** Options for {@link TraceScene.unpackReference}. */
export interface UnpackReferenceOptions {
    /** Rename whatever object survives the unpack. */
    name?: string;
    /**
     * Behaviors to carry onto the surviving object. The import path uses this
     * to move the caller's behaviors off a wrapper that is about to go away.
     */
    behaviors?: BehaviorData[];
    /**
     * Bring the reference's own folder files into THIS scene's folder — the
     * freeze half of "Copy to scene" (spec §2.6, §3.2).
     *
     * A library asset's files are relative to the ASSET's folder. Dissolving
     * the reference leaves the content on screen with no legal way for this
     * scene to name those bytes: the relative path belongs to another folder,
     * and an absolute storage URL is what the manifest rules refuse (§2.5).
     * With an uploader, each such file is fetched, handed over, and the node
     * re-`src`ed to the relative uri that comes back. Without one, the unpack
     * still happens and the files are reported on
     * {@link UnpackReferenceResult.orphaned}.
     */
    intoFolder?: UnpackFolderTarget;
}

/** Options for {@link TraceScene.placeCopy}. */
export interface PlaceCopyOptions {
    /** Name for the copy's root, made unique among its siblings. */
    name?: string;
    /**
     * A uid for the copy's root, kept instead of a fresh one — a swap keeps
     * the wrapper's identity. The caller retires any other node holding it
     * within the same operation. Ignored in a legacy session, which writes
     * no identity.
     */
    uid?: string;
    /** Behaviors to put on the copy's root. */
    behaviors?: BehaviorData[];
    /** Where the asset's own folder files go; see {@link UnpackReferenceOptions.intoFolder}. */
    intoFolder?: UnpackFolderTarget;
}

/**
 * Where {@link TraceScene.placeCopy} builds its copy, and what it has built.
 * The load fills `made` as it goes, so a load that throws part-way still
 * tells the caller what to take back.
 */
interface CopyPlacement {
    parent: SceneObject;
    /** The placement, then whatever the dissolve promotes out of it. */
    made: SceneObject[];
}

/** What {@link TraceScene.unpackReference} did. */
export interface UnpackReferenceResult {
    /**
     * The object standing where the reference stood — the promoted child when
     * the file held one object, the ex-reference itself otherwise. Selection
     * identity is preserved wherever the reference node itself survives.
     */
    object: SceneObject | null;
    /** False when the object was not a reference, or a reparent policy vetoed the move. */
    unpacked: boolean;
    /** Source-folder uri → this folder's uri, for every file brought across. */
    copied: Map<string, string>;
    /**
     * Files the unpack could not bring across: the content renders now and
     * this scene cannot save it. Non-empty means the caller passed no
     * `intoFolder`, or the bytes were unreachable.
     */
    orphaned: string[];
}

/**
 * One of the optional trailing arguments of an import call — see
 * {@link readImportArgs}.
 */
export type ImportArg = string | BehaviorData[] | ImportOptions | undefined;

/**
 * Sort the optional tail of `TraceScene.loadAsset` / `TraceViewer.import`
 * (`name?`, `behaviors?`, `options?`) by the shape of each argument rather than
 * its position: a string is the name, an array is the behaviors, any other
 * object is the options. First one of a shape wins; nullish arguments are
 * skipped.
 *
 * Position alone cannot decide it. A caller that omits `name` slides the rest
 * one slot left (`loadAsset(blob, 'a.trace', behaviors)`), while a caller with
 * nothing to put there writes it out (`loadAsset(blob, 'a.trace', undefined,
 * undefined, { unpack: true })` — how a host spells it when it has no name to
 * give). Both are valid, both reach the same parameter list, and only the
 * values tell them apart.
 *
 * @internal
 */
export function readImportArgs(args: ImportArg[]): {
    name?: string;
    behaviors?: BehaviorData[];
    options?: ImportOptions;
} {
    const sorted: { name?: string; behaviors?: BehaviorData[]; options?: ImportOptions } = {};
    for (const arg of args) {
        if (typeof arg === 'string') sorted.name ??= arg;
        else if (Array.isArray(arg)) sorted.behaviors ??= arg;
        else if (arg && typeof arg === 'object') sorted.options ??= arg;
    }
    return sorted;
}

export class TraceScene {
    /**
     * Stores parsed info.json data when loading from a .zip trace scene
     * This preserves the original preview metadata for re-export
     * Only populated when the scene was loaded from a .zip containing info.json
     */
    public sourceInfoFile: TraceSceneInfoFile | undefined;

    /**
     * The file-format extension the active scene was loaded from (lowercased,
     * without the leading dot). Empty string when no scene has been loaded.
     *
     * Informational only — the runtime treats all loaded scenes the same.
     * Per-format conventions (e.g. the legacy `.zip` AR Foundation anchor
     * frame) are normalized to the OpenXR anchor frame at the loader
     * boundary.
     *
     * Set internally by the scene loader; read-only from outside.
     */
    public get sourceFormat(): string { return this._sourceFormat; }
    private _sourceFormat: string = '';

    /**
     * The format the SCENE itself was opened from — set by a scene-ESTABLISHING
     * load (`loadFromURL` / `loadFromBlob`) only, and empty for a scene created
     * in the session.
     *
     * Distinct from {@link sourceFormat}, which records the last file handed to
     * the loader and is therefore rewritten by every import and every nested
     * reference. What a session IS cannot be decided by what was last dropped
     * into it, and SCENE_ROOT_SPEC §2.3 "Scope — forward-only" is decided per
     * session: see {@link placementRuleActive}.
     */
    public get sceneSourceFormat(): string { return this._sceneSourceFormat; }
    private _sceneSourceFormat: string = '';

    /**
     * Whether this session follows the reference-placement rule
     * (SCENE_ROOT_SPEC §2.3): a node with a `src` instantiates whatever that
     * `src` resolves to, so import builds the tree reopen builds and a file
     * reference fills its placement in rather than growing a child.
     *
     * **This is the migration gate, and it is temporary.** The rule is part of
     * the glTF 2.1 switch:
     *
     * - a scene opened from a 2.1 document (`.gltf`/`.glb` document, `.glz`)
     *   is a 2.1 session — the rule applies to everything placed into it,
     *   including a legacy `.trace` asset;
     * - a scene opened from a legacy `.zip`/`.trace`/`.tracex`/`.glxf` keeps
     *   today's stand-in import EXACTLY, until the save that converts it to a
     *   folder document (see `dropImportEraReferenceCopies`). A COPY placed
     *   there ({@link placeCopy}) is the exception: it leaves no reference, so
     *   it is built the 2.1 way in every session;
     * - a scene created in the session (no source format yet) is a 2.1 session
     *   too, for EVERY asset placed into it — a legacy `.trace`/`.zip` archive
     *   included. It will be saved as a folder document, and the stand-in
     *   import there would write the referenced root's behaviors into a 2.1
     *   document that the converting save never looks at (that save reads the
     *   scene's source format, and this scene's was never legacy): the ESPN
     *   bug, frozen into new documents forever. Every ESPN asset is a `.trace`,
     *   so this is the common case, not the corner.
     *
     * The one thing that decides it is therefore the SESSION, never the file
     * being placed. Both legacy sides die with the legacy readers (#816): this
     * getter goes with them and the rule becomes unconditional.
     */
    public get placementRuleActive(): boolean {
        const format = this.sceneSourceFormat;
        return format === '' || !isLegacySceneFormat(format);
    }

    /**
     * Whether this session gives its nodes a glTF 2.1 `uid` (issue #837,
     * SCENE_ROOT_SPEC §2.3 "Identity: glTF 2.1 `uid` — now").
     *
     * The same question as {@link placementRuleActive}, asked by the other
     * half of the forward-only cut, and deliberately the same answer: a scene
     * created in the session or opened from a 2.1 document mints identity for
     * every node in it — including nodes that arrived from a legacy `.trace`
     * asset placed into it — and a scene opened from a legacy
     * `.zip`/`.trace`/`.tracex`/`.glxf` gets none anywhere. Its writers are
     * frozen and emit no `uid`, and a legacy document is never stamped: it
     * gains identity when it is saved as a folder document, from the session
     * that does the saving.
     *
     * Named separately because it is a different rule reaching the same gate —
     * a reader looking for "who mints a uid" should not have to know that the
     * placement rule answers it. Both retire with the legacy readers (#816),
     * after which minting is unconditional.
     */
    private get _uidMintingActive(): boolean {
        return this.placementRuleActive;
    }
    // Babylon.js related properties
    private babylonScene: Scene;
    private engine: Engine;
    private canvas: HTMLCanvasElement;
    private camera: ArcRotateCamera | null;

    // Configuration properties
    private config: ConfigData;
    private backgroundColor: string | null;
    private alpha: number | null;

    // Scene data properties
    public sceneName: string;

    /**
     * The document root this session was established with — set by a
     * scene-establishing load and by {@link ensureDocumentRoot}, cleared by
     * {@link clearScene}. See {@link _importMountParent}.
     */
    private _sessionDocumentRoot: SceneObject | null = null;
    /** Array of all SceneObjects in the scene. Use resolveSceneObject() for lookups. */
    private sceneObjects: SceneObject[];
    public resources: ResourceManager;

    /**
     * Implicit scene root object for scene-level behaviors.
     * This is a regular SceneObject that appears in the scene graph and can be selected.
     * Scene-level behaviors (from the `behaviors` HTML attribute) are attached here.
     */
    private _sceneRoot: SceneObject | null = null;

    // Reference to parent TraceViewer element (for event dispatching)
    private _eventSystem: SceneEventSystem | null = null;

    /** Positive-only node→owner cache for {@link findSceneObjectByTransform}.
     *  Entries are re-verified on hit and WeakMap keying lets disposed nodes
     *  fall out naturally — no lifecycle plumbing. */
    private readonly _transformOwnerCache = new WeakMap<Node, SceneObject>();

    /**
     * Optional fetch init options merged into all fetch() calls.
     * Use this to inject Authorization headers for authenticated loading.
     *
     * @example
     * ```typescript
     * traceScene.fetchInit = {
     *   headers: { 'Authorization': 'Bearer <token>' }
     * };
     * ```
     */
    public fetchInit: RequestInit | null = null;

    // Selection state (stores direct reference - names are not globally unique)
    private _selectedObject: SceneObject | null = null;

    // Play mode state (true = interactions active, false = edit mode for selection)
    private _playMode: boolean = true;
    /** Depth-only switch + draw order for occluder materials; fed by play mode and XR state. */
    private readonly _occluderMode: OccluderMode;
    private _playModeInitialized: boolean = false;
    private _renderingStarted = false;

    // Consumer's preferred click-to-select state for edit mode.
    // Play mode forces click-to-select on; exiting restores this value.
    private _editModeClickToSelect: boolean = false;

    // Viewport safe area insets (pixels from each canvas edge)
    private _safeAreaInsets: SafeAreaInsets = { left: 0, top: 0, right: 0, bottom: 0 };

    /**
     * Number of Babylon GUI input controls that currently have focus.
     * Behaviors increment on focus and decrement on blur so that overlapping
     * focus/blur from multiple controls are handled correctly.
     * Check `hasGUIFocus` for a boolean shorthand.
     */
    private _guiFocusCount = 0;

    // Script system
    private _scriptRegistry: ScriptRegistry = new ScriptRegistry();

    // System values for expression evaluation
    private _systemValues: SystemValueProvider = new SystemValueProvider();
    private _scriptsMap: Record<string, string> | undefined;

    /**
     * The half of the opened document Trace did not author — foreign `extras`
     * beside `trace`, foreign `extensions`, `extensionsUsed`/`Required`, and
     * `asset.extras`.
     *
     * The writer re-serialises the whole document on every save, so anything it
     * does not carry is deleted. Holding it here is what makes a save a
     * custodial rewrite of the document rather than a replacement of it
     * (issue #834, CONTENT_MODEL_SPEC §3.6). Set only by a scene-ESTABLISHING
     * load: an import or a nested reference belongs to another document.
     */
    private _foreignDocument: ForeignDocumentData | undefined;

    // Audio
    private _audioManager: AudioManager | null = null;

    // Runtime selection
    private _runtimeSelectionManager: RuntimeSelectionManager | null = null;

    // Stable-identity registry for the undo system. Hands out SceneObjectRef
    // instances that survive Create/Delete undo cycles, so commands can hold a
    // logical identity rather than a raw SceneObject pointer that goes dead
    // when the object is destroyed. Lazily *instantiated* (not lazily imported
    // — the module is value-imported above) so runtime-only consumers that
    // never touch the editor never pay the allocation.
    private _objectIdentityRegistry: SceneObjectIdentityRegistry | null = null;

    /**
     * Stable-identity registry used by the editor undo/redo system. Lazy so
     * runtime-only consumers never instantiate it.
     */
    public get objectIdentityRegistry(): SceneObjectIdentityRegistry {
        if (!this._objectIdentityRegistry) {
            this._objectIdentityRegistry = new SceneObjectIdentityRegistry();
        }
        return this._objectIdentityRegistry;
    }

    /** Access the script registry for this scene */
    public get scriptRegistry(): ScriptRegistry { return this._scriptRegistry; }

    /** Scripts map for export round-tripping */
    public get scriptsMap(): Record<string, string> | undefined { return this._scriptsMap; }

    /**
     * The opened document's foreign payload, for the writer to merge back on
     * the next save (issue #834). Undefined when the document carried none, or
     * when this scene was never opened from a document.
     */
    public get foreignDocument(): ForeignDocumentData | undefined { return this._foreignDocument; }

    /**
     * Register a new script type. Stores the source as an asset blob,
     * adds to the scripts map, and compiles it in the registry.
     */
    public registerScript(typeName: string, assetPath: string, source: string): void {
        // Initialize scripts map if needed
        if (!this._scriptsMap) this._scriptsMap = {};
        this._scriptsMap[typeName] = assetPath;

        // Store source as blob in ResourceManager
        const normalizedPath = assetPath.replace(/^\.\//, '');
        this.resources.addAsset(normalizedPath, new Blob([source], { type: 'text/typescript' }));

        // Compile and register
        this._scriptRegistry.register(typeName, source);
    }

    // Contact shadow ground plane
    private _groundShadowEnabled: boolean = false;
    private _contactShadow: ContactShadow | null = null;

    /**
     * Optional veto for reparenting attempts (Inspector drag-drop, programmatic
     * `setParent`, etc.). Consulted by {@link SceneObject._installParentInterceptor};
     * if the policy returns `false`, the reparent is reverted in place.
     *
     * Used by trace-sandbox in CMS Scene mode to enforce flat scenes — see
     * `CmsManager` in trace-sandbox. Null means "all reparenting allowed".
     */
    private _reparentPolicy: ((child: SceneObject, newParent: SceneObject | null) => boolean) | null = null;

    /** Install or clear the reparent veto. Pass `null` to allow all reparenting. */
    public setReparentPolicy(
        policy: ((child: SceneObject, newParent: SceneObject | null) => boolean) | null,
    ): void {
        this._reparentPolicy = policy;
    }

    /**
     * Listeners for scene TOPOLOGY changes: hierarchy (reparent, create,
     * delete) and names (rename, dedup auto-rename). Fired AFTER the mutation
     * has committed. Coarse by design — no payload; subscribers that need a
     * consistent view should coalesce to a microtask and re-scan (mutations
     * arrive in bursts during loads and composite undos).
     *
     * This is the signal the editor's reference-identity registry derives
     * from (docs-internal/REFERENCE_IDENTITY.md): identity refs are captured
     * while paths are valid, and every topology change re-derives the path
     * strings from those refs.
     */
    private _topologyListeners = new Set<(changedAt?: readonly SceneObject[]) => void>();

    private _structureRevisionValue = 0;

    /**
     * A counter that moves whenever what a subtree HOLDS may have changed:
     * every topology change (create, delete, reparent, rename) and every
     * behavior added to or removed from any object. No payload and no
     * listeners — a cheap cache key for per-frame consumers (the Timeline's
     * Playable list and clock), who compare it with the value they cached at.
     */
    public get structureRevision(): number {
        return this._structureRevisionValue;
    }

    /** @internal Advance {@link structureRevision}. */
    public _bumpStructureRevision(): void {
        this._structureRevisionValue++;
    }

    /**
     * Subscribe to topology changes; returns the unsubscriber. `changedAt`,
     * when given, lists where the change happened: a created or moved object
     * (its subtree moved with it) and the parent an object left. Absent means
     * the change may be anywhere.
     */
    public onTopologyChanged(listener: (changedAt?: readonly SceneObject[]) => void): () => void {
        this._topologyListeners.add(listener);
        return () => this._topologyListeners.delete(listener);
    }

    /**
     * @internal Notify topology listeners. Listener exceptions are contained —
     * a throwing subscriber must not break the mutation that notified it.
     */
    public _emitTopologyChanged(changedAt?: readonly SceneObject[]): void {
        this._bumpStructureRevision();
        // The scene just changed shape, so anything waiting for it to stop
        // changing waits a little longer (see {@link _deferStartEvent}).
        this._armStartFlush();
        for (const listener of this._topologyListeners) {
            try {
                listener(changedAt);
            } catch (err) {
                Logger.warn('[TraceScene] topology listener threw:', err);
            }
        }
    }

    // ── Start-event barrier ─────────────────────────────────────────────────
    //
    // `event/onStart` must not run against a half-built scene. The batch
    // loaders already guarantee it cannot: they create every object, parent
    // every object, and only then attach behaviors. A consumer that builds a
    // scene one object at a time through `createSceneObjectFromData` gets no
    // such guarantee — in play mode (the viewer's default) each object's
    // graphs attach AND start inside its own construction, so object #1's
    // start event runs while objects #2..n do not exist yet.
    //
    // That is how a start-time `trace/setActive` on "./Explanation" came to
    // resolve to nothing and hide its own host: the target was authored 40
    // entries later in the same flat content array.
    //
    // So a graph hands its start event to this queue instead of firing it
    // inline, and the queue drains once the scene has stopped growing: the
    // flush is re-armed by every topology change (every object creation ends
    // in one), bounded by an absolute deadline so a scene that keeps churning
    // still starts.

    /** Start events waiting for scene construction to settle. */
    private _pendingStartEvents = new Set<() => void>();
    private _startFlushTimer: ReturnType<typeof setTimeout> | null = null;
    private _startFlushDeadline: ReturnType<typeof setTimeout> | null = null;
    /** How many object constructions are currently in flight. */
    private _constructionDepth = 0;

    /**
     * Bracket an object construction, so the start barrier can tell "the
     * consumer is between two objects" from "the scene is finished".
     * @internal
     */
    private _beginConstruction(): void {
        this._constructionDepth++;
    }

    /** @internal Pair of {@link _beginConstruction}; call from a `finally`. */
    private _endConstruction(): void {
        this._constructionDepth = Math.max(0, this._constructionDepth - 1);
        // A construction that finished while the settle timer (or its
        // deadline) had already fired has to re-arm it, or nothing will.
        if (this._constructionDepth === 0) this._armStartFlush();
    }

    /** Longest a start event may be held back by continuing scene churn. */
    private static readonly START_FLUSH_MAX_MS = 1000;

    /**
     * Run `fire` once scene construction has settled — the contract behind
     * `event/onStart`. Re-armed by every topology change, so a scene still
     * being built pushes the whole batch's start events past its last object.
     *
     * @internal Called by BehaviorGraph; consumers use `event/onStart`.
     */
    public _deferStartEvent(fire: () => void): void {
        this._pendingStartEvents.add(fire);
        this._armStartFlush();
        if (this._startFlushDeadline === null) {
            this._startFlushDeadline = setTimeout(() => {
                this._startFlushDeadline = null;
                this._flushStartEvents(true);
            }, TraceScene.START_FLUSH_MAX_MS);
        }
    }

    /** Push the settle-flush out by one turn. No-op with nothing pending. */
    private _armStartFlush(): void {
        if (this._pendingStartEvents.size === 0) return;
        if (this._startFlushTimer !== null) clearTimeout(this._startFlushTimer);
        this._startFlushTimer = setTimeout(() => this._flushStartEvents(), 0);
    }

    /**
     * Fire every deferred start event now, in the order they were deferred.
     * The queue is emptied BEFORE firing, so a start event that builds more
     * scene re-arms cleanly instead of re-entering this drain.
     *
     * @internal Exposed so a caller that knows the scene is complete (a test,
     * a host driving its own load) need not wait for the timer.
     * @param force Fire even while a construction is in flight. Only the
     *   absolute deadline passes it: a construction that never settles (a hung
     *   asset fetch) must not hold every start event in the scene forever.
     */
    public _flushStartEvents(force = false): void {
        if (this._pendingStartEvents.size === 0) {
            this._clearStartFlushTimers();
            return;
        }
        // A single object's construction can await real I/O (a font, a
        // texture), and the settle timer would happily fire inside that gap —
        // which is the very failure this barrier exists to prevent. While a
        // construction is in flight, wait for it instead: _endConstruction
        // re-arms the settle timer when the last one finishes, so there is
        // nothing to poll for, and the absolute deadline fires regardless.
        if (this._constructionDepth > 0 && !force) {
            if (this._startFlushTimer !== null) {
                clearTimeout(this._startFlushTimer);
                this._startFlushTimer = null;
            }
            return;
        }
        this._clearStartFlushTimers();
        const pending = [...this._pendingStartEvents];
        this._pendingStartEvents.clear();
        for (const fire of pending) {
            try {
                fire();
            } catch (err) {
                Logger.warn('[TraceScene] deferred start event threw:', err);
            }
        }
    }

    /** Drop everything still waiting — leaving play mode, or disposing. */
    private _cancelStartEvents(): void {
        this._clearStartFlushTimers();
        this._pendingStartEvents.clear();
    }

    private _clearStartFlushTimers(): void {
        if (this._startFlushTimer !== null) {
            clearTimeout(this._startFlushTimer);
            this._startFlushTimer = null;
        }
        if (this._startFlushDeadline !== null) {
            clearTimeout(this._startFlushDeadline);
            this._startFlushDeadline = null;
        }
    }

    /**
     * Listeners for an object's behaviors finishing their build from data
     * (the close of {@link SceneObject.isLoadingBehaviors}), so a system that
     * held off on a half-built object can look at it again.
     */
    private _behaviorsLoadedListeners = new Set<(obj: SceneObject) => void>();

    /** Subscribe to behavior-load completion; returns the unsubscriber. */
    public onBehaviorsLoaded(listener: (obj: SceneObject) => void): () => void {
        this._behaviorsLoadedListeners.add(listener);
        return () => this._behaviorsLoadedListeners.delete(listener);
    }

    /** @internal Notify behavior-load listeners (exceptions contained, same contract as {@link _emitTopologyChanged}). */
    public _emitBehaviorsLoaded(obj: SceneObject): void {
        for (const listener of this._behaviorsLoadedListeners) {
            try {
                listener(obj);
            } catch (err) {
                Logger.warn('[TraceScene] behaviors-loaded listener threw:', err);
            }
        }
    }

    /**
     * Listeners for individual REPARENT events, with payload — unlike the
     * coarse {@link onTopologyChanged}, these fire once per moved object and
     * say WHICH object moved and where from. Emitted by both reparent paths
     * ({@link SceneObject.setParent} and the transform's setParent interceptor
     * that catches Inspector drag-drops), only on an ACTUAL parent change, and
     * AFTER the mutation + name dedup + topology signal have settled.
     *
     * Deliberately NOT fired by construction-time parenting (loaders assign
     * `transform.parent` directly), so scene loads never look like moves —
     * subscribers can treat every event as a live editing gesture. The editor's
     * auto-separate-on-group-entry watcher relies on exactly that.
     */
    private _reparentListeners = new Set<(obj: SceneObject, prevParent: SceneObject | null) => void>();

    /** Subscribe to per-object reparent events; returns the unsubscriber. */
    public onObjectReparented(listener: (obj: SceneObject, prevParent: SceneObject | null) => void): () => void {
        this._reparentListeners.add(listener);
        return () => this._reparentListeners.delete(listener);
    }

    /**
     * @internal Notify reparent listeners (exceptions contained, same contract
     * as {@link _emitTopologyChanged}). `prevParentNode` is the raw previous
     * parent transform; it is resolved to its SceneObject here (null when the
     * object came from the root or from a non-SceneObject node).
     */
    public _emitObjectReparented(obj: SceneObject, prevParentNode: Node | null): void {
        const prevParent = prevParentNode
            ? this.findSceneObjectByTransform(prevParentNode) ?? null
            : null;
        for (const listener of this._reparentListeners) {
            try {
                listener(obj, prevParent);
            } catch (err) {
                Logger.warn('[TraceScene] reparent listener threw:', err);
            }
        }
    }

    /**
     * Listeners for completed Layout solves. Fired by the Layout behavior
     * AFTER a solve pass fully completes for a layout root — transforms
     * applied and the container-size bake done (whether or not it wrote).
     * Payload is the solved root; listeners re-read whatever state they
     * need. Read-only contract: the data-level bake stays silent, this is
     * a lifecycle signal, not a data event.
     *
     * Solves are microtask-batched, so a burst of edits produces one event
     * per root, not one per child. A pass that changed nothing still fires —
     * subscribers may be waiting on ANY completion (late-loading content).
     * A listener that re-invalidates schedules the NEXT batch; it cannot
     * re-enter the pass that notified it.
     *
     * WARNING for re-invalidating listeners: because "changed nothing"
     * still fires, an UNCONDITIONAL invalidate from a listener chains
     * solve → event → invalidate → solve as back-to-back microtasks,
     * forever — starving every macrotask (timers included, so even a
     * settle-wait's maxWaitMs never fires). Re-invalidate only on an
     * actual state change with a convergent fixed point. A debug-mode
     * runaway counter below warns when an emit storm starves the
     * macrotask queue.
     */
    private _layoutSolvedListeners = new Set<(root: SceneObject) => void>();

    /** Emits since the macrotask queue last got a turn (debug watchdog). */
    private _layoutSolveEmitStreak = 0;
    private _layoutSolveStreakResetScheduled = false;
    private static readonly _LAYOUT_SOLVE_RUNAWAY_THRESHOLD = 100;

    /** Subscribe to completed layout solves; returns the unsubscriber. */
    public onLayoutSolved(listener: (root: SceneObject) => void): () => void {
        this._layoutSolvedListeners.add(listener);
        return () => this._layoutSolvedListeners.delete(listener);
    }

    /**
     * @internal Notify layout-solve listeners (exceptions contained, same
     * contract as {@link _emitTopologyChanged}).
     *
     * Debug-mode runaway detection: the streak counter resets from a
     * macrotask — exactly what a re-invalidation microtask loop starves —
     * so a streak past the threshold means some listener is chaining
     * solves with no frame in between, and we warn once per episode.
     */
    public _emitLayoutSolved(root: SceneObject): void {
        if (Logger.level >= LogLevel.DEBUG) {
            this._layoutSolveEmitStreak++;
            if (this._layoutSolveEmitStreak === TraceScene._LAYOUT_SOLVE_RUNAWAY_THRESHOLD) {
                Logger.warn(
                    `[TraceScene] ${this._layoutSolveEmitStreak} layout solves without a macrotask — ` +
                    `an onLayoutSolved listener is re-invalidating unconditionally (runaway microtask chain).`,
                );
            }
            if (!this._layoutSolveStreakResetScheduled) {
                this._layoutSolveStreakResetScheduled = true;
                setTimeout(() => {
                    this._layoutSolveStreakResetScheduled = false;
                    this._layoutSolveEmitStreak = 0;
                }, 0);
            }
        }
        for (const listener of this._layoutSolvedListeners) {
            try {
                listener(root);
            } catch (err) {
                Logger.warn('[TraceScene] layout-solve listener threw:', err);
            }
        }
    }

    /**
     * Listeners for a container whose Hug size the solver actually wrote back
     * into authored data. Unlike {@link onLayoutSolved} this is a DATA signal:
     * it fires only when a bake changed a value, never for a no-op pass, and
     * carries the container it wrote.
     *
     * It exists so the editor can refresh an inspector after a bake without
     * `behaviors/layout.ts` importing editor infrastructure — a runtime/AR
     * viewer must not dispatch editor CustomEvents on every solve. The editor
     * side subscribes (see `editor/layout-bindings.ts`) and translates this
     * into the usual BEHAVIOR_CHANGED event; the runtime leaves it unheard.
     *
     * Same timing contract as {@link onLayoutSolved}: emitted after the pass
     * has fully unwound, so a listener that re-invalidates schedules the next
     * batch instead of re-entering the live one.
     */
    private _layoutSizeBakedListeners = new Set<(container: SceneObject) => void>();

    /** Subscribe to container-size bakes; returns the unsubscriber. */
    public onLayoutSizeBaked(listener: (container: SceneObject) => void): () => void {
        this._layoutSizeBakedListeners.add(listener);
        return () => this._layoutSizeBakedListeners.delete(listener);
    }

    /** @internal Notify size-bake listeners (exceptions contained). */
    public _emitLayoutSizeBaked(container: SceneObject): void {
        for (const listener of this._layoutSizeBakedListeners) {
            try {
                listener(container);
            } catch (err) {
                Logger.warn('[TraceScene] layout size-bake listener threw:', err);
            }
        }
    }

    /**
     * Whether authored children may be inserted under `parent`.
     *
     * False when `parent` is, or sits inside, a scene-format reference
     * subtree (a node whose `src` points at a .trace/.tracex/… file). Such
     * descendants are derived from the referenced scene: an authored child
     * there would be ambiguous with derived content and dropped on export.
     * Asset srcs (primitives, models, images, videos) are valid parents.
     *
     * Enforced by {@link reparentSceneObject}, {@link createSceneObject},
     * {@link createSceneObjectFromData}, and the Inspector drag-drop
     * interceptor. Editors should also use it to gate parent pickers / drop
     * targets up front. Loader-internal parenting of derived children uses
     * `SceneObject.setParent` directly and is unaffected.
     */
    public canParentUnder(parent: SceneObject | null): boolean {
        let walk: SceneObject | null = parent;
        while (walk) {
            if (walk.isSceneReference) return false;
            walk = walk.getParent();
        }
        return true;
    }

    /**
     * @internal Build the veto error for an insertion under a reference
     * subtree, and notify listeners (`trace-viewer:reparent-vetoed`) so
     * editors can surface the reason without wrapping every call site.
     */
    public _vetoReferenceSubtreeParent(childName: string, parent: SceneObject): Error {
        const message =
            `Cannot add "${childName}" under "${parent.name}": children of an imported ` +
            `scene reference are derived from the referenced file and would be lost on ` +
            `export. Choose a parent outside the imported subtree instead.`;
        this._notifyReparentVetoed(childName, parent, message);
        return new Error(message);
    }

    /** @internal Dispatch a reparent-vetoed event (editor toast hook). */
    public _notifyReparentVetoed(childName: string, parent: SceneObject | null, message: string): void {
        this._eventSystem?.dispatchEvent(new CustomEvent('trace-viewer:reparent-vetoed', {
            detail: { childName, parent, message },
            bubbles: true,
            composed: true,
        }));
    }

    /**
     * @internal Called by {@link SceneObject._installParentInterceptor} to ask
     * whether a reparent should proceed. The built-in reference-subtree rule
     * ({@link canParentUnder}) applies first and notifies on veto (editor
     * toast); a custom policy is consulted after and stays silent — CMS
     * flat-scene mode relies on silent reverts. No policy = allow.
     */
    public _isReparentAllowed(child: SceneObject, newParent: SceneObject | null): boolean {
        if (newParent && !this.canParentUnder(newParent)) {
            this._vetoReferenceSubtreeParent(child.name, newParent);
            return false;
        }
        if (!this._reparentPolicy) return true;
        return this._reparentPolicy(child, newParent);
    }

    constructor(o: {
        engine: Engine,
        canvas: HTMLCanvasElement,
        config: ConfigData,
        traceViewer?: SceneEventSystem
    }) {
        // Initialize Babylon properties — Scene is created here so TraceScene
        // owns the full lifecycle (creation → clearing → disposal).
        this.babylonScene = new Scene(o.engine);
        this.engine = o.engine;
        this.canvas = o.canvas;
        this.camera = null;

        // Initialize configuration properties
        this.config = o.config;
        this.backgroundColor = o.config.color;
        this.alpha = o.config.alpha || null;

        // Initialize scene data properties
        this.sceneObjects = [];
        this.resources = new ResourceManager();

        // Default scene name
        this.sceneName = "Scene";

        // Store TraceViewer reference for event dispatching
        this._eventSystem = o.traceViewer || null;

        // Initialize camera and lights
        this.camera = SceneCamera.setupCamera(this.babylonScene);
        this._systemValues.setCamera(this.camera);
        this.setupLights();

        // Record ground shadow preference (creation deferred until scene root exists)
        if (this.config.groundShadow !== false) {
            this._groundShadowEnabled = true;
        }

        // Start in edit mode if configured (editors set play-mode="false" on the element)
        if (o.config.playMode === false) {
            this._playMode = false;
        }

        this._occluderMode = new OccluderMode(this.babylonScene);
        this._occluderMode.setPlaying(this._playMode);
    }

    /** Get the scene event system for dispatching and listening to scene-level events. */
    public getEventSystem(): SceneEventSystem | null {
        return this._eventSystem;
    }

    /**
     * Get the Babylon.js Scene object.
     * The Scene is created once during initialization and persists for the
     * lifetime of the TraceViewer (clearScene() clears content but reuses
     * the same Scene object).
     */
    public getBabylonScene(): Scene {
        return this.babylonScene;
    }

    /**
     * Get the implicit scene root object.
     * The scene root is a regular SceneObject that holds scene-level behaviors.
     * It is created automatically when the scene starts.
     *
     * @returns The scene root SceneObject, or null if scene hasn't started yet
     */
    public getSceneRoot(): SceneObject | null {
        return this._sceneRoot;
    }

    /**
     * The **document root** — the one node of the open document
     * (SCENE_ROOT_SPEC D8): the engine root's single child.
     *
     * This is the file's own root: it carries the root behaviors, the root
     * `active`, and the pivot transform, and it is what an editor treats as
     * "the scene" — selectable, the default parent, renamable. The engine root
     * ({@link getSceneRoot}) is a runtime mount point and is none of those.
     *
     * Null when the session has no single root: an empty scene, or a legacy
     * flat `.zip` whose content is several top-level objects. An editor that
     * wants one either way calls {@link ensureDocumentRoot}. A lone object
     * that owns a file is returned here as the shape states it, until that
     * call creates the root above it.
     */
    public getDocumentRoot(): SceneObject | null {
        const children = this._sceneRoot?.getChildren() ?? [];
        return children.length === 1 ? children[0] : null;
    }

    /**
     * The document root this SESSION was established with — the root of the
     * document that was opened, or the one an editor adopted
     * ({@link ensureDocumentRoot}).
     *
     * Where an import lands (§2.3: the same node a reopen would put it under,
     * so import and reopen build the same tree). Deliberately NOT
     * {@link getDocumentRoot}, which answers from the shape of the tree: in a
     * session assembled object by object — a legacy flat archive, an ad-hoc
     * import — the engine root's single child is the previous import, and
     * nesting the next one inside it is not what anybody asked for. A session
     * that never established a root imports at the mount point, as before.
     */
    private _importMountParent(): SceneObject | undefined {
        return this.getSessionDocumentRoot() ?? undefined;
    }

    /**
     * The document root this SESSION was established with, or null when it
     * never established one (a legacy flat `.zip`, a scene assembled object by
     * object, an empty scene).
     *
     * Distinct from {@link getDocumentRoot}, which answers from the shape of
     * the tree — "the engine root's single child" — and so cannot tell a
     * document's root from the only object somebody happened to import. Both
     * questions are legitimate: the shape one is what an editor and the writer
     * ask ("what is the root of the document I am about to write"), this one is
     * what a legacy `.zip` write and an import ask ("did this session come with
     * a root of its own").
     *
     * Read by the export pipeline, the loader and editors (`documentRoot`).
     */
    public getSessionDocumentRoot(): SceneObject | null {
        const root = this._sessionDocumentRoot;
        if (!root || !this.sceneObjects.includes(root)) return null;
        return root.getParent() === this._sceneRoot ? root : null;
    }

    /**
     * The document root, creating or adopting one if the session has none —
     * the editor's "new scene" / "adopt this session" call (D8).
     *
     * Three cases, one answer:
     *
     * - **one child** — that is the document root; nothing happens. Unless it
     *   is content: a lone object with a `src` of its own, grouped like the
     *   case below. That is a single asset opened as the scene, a legacy
     *   archive's placed model, or a document that is one file with behaviors
     *   (a v4 server wrapper, or a leaf's first edit saved without an adopt).
     *   The load decides that from the file and sets no session root for any
     *   of them (`_loadFromBlob`). A
     *   root the load did establish stays the root, even when it owns a file:
     *   it holds the rest of its document (SCENE_ROOT_SPEC §4).
     * - **no objects** — an empty root is created under the engine root with an
     *   identity transform. A new scene starts with one, so the first import
     *   has somewhere to land and the first save writes a D8 document.
     * - **several top-level objects** (a legacy `.zip` session, a scene built
     *   before D8) — they are grouped under a new root, world transforms
     *   preserved.
     *
     * A LOAD never calls this and neither does {@link clearScene}: when a
     * session starts is the editor's decision, not the loader's. (The one root a
     * load creates is for root-entity data that arrives with a file whose top
     * level is content — see `_loadFromBlob`.)
     */
    public async ensureDocumentRoot(name?: string): Promise<SceneObject> {
        this.initializeSceneRoot();
        const children = this._sceneRoot!.getChildren();
        const [only] = children;
        const isContent = !!only?.src && only !== this.getSessionDocumentRoot();
        if (children.length === 1 && !isContent) {
            this._sessionDocumentRoot = only;
            return only;
        }
        return this._createDocumentRoot(children, name);
    }

    /**
     * A new document root under the engine root, at identity, holding
     * `children` world-preserved; it becomes the session's root.
     */
    private _createDocumentRoot(children: SceneObject[], name?: string): SceneObject {
        const sceneRoot = this._sceneRoot!;
        // The objects it is about to hold are not its siblings: a lone asset
        // shares the file's name, and its root would otherwise get " (1)".
        const siblings = this.getSiblingNamesByTransform(sceneRoot.transform);
        for (const child of children) siblings.delete(child.name);
        const rootName = Utility.generateUniqueName(name ?? this.sceneName ?? SCENE_ROOT_NAME, siblings);
        const documentRoot = SceneCreation.initializeSceneObject(
            { name: rootName, src: undefined, active: true, transform: {}, behaviors: [] },
            this.babylonScene,
            this.resources,
            this.config,
            this,
            this._eventSystem || undefined,
        );
        this.sceneObjects.push(documentRoot);
        this._stampNodeUid(documentRoot, 'created');
        documentRoot.setParent(sceneRoot);

        // World-preserving: the new root sits at identity, so re-parenting the
        // existing top-level objects under it must not move them — and the
        // recomputed local pose is written back into authored data.
        for (const child of children) {
            this.reparentSceneObjectPreservingWorld(child, documentRoot);
        }

        Logger.log(
            `[TraceScene] ensureDocumentRoot: "${rootName}" now holds ${children.length} top-level object(s) `
            + '(SCENE_ROOT_SPEC D8)',
        );
        this._sessionDocumentRoot = documentRoot;
        this._emitTopologyChanged();
        this._emitSceneChanged([rootName]);
        return documentRoot;
    }

    /**
     * Apply externally-stored root behaviors — CMS `metadata`, or a caller's
     * `OpenOptions.behaviors` — to the content's **root entity**
     * (SCENE_ROOT_SPEC §5.1): the document root of a document, and the asset's
     * own object for a single asset (which the load has already handled, since
     * they travel as `extraBehaviors` into its construction).
     *
     * **Whole-set replace, not a per-type merge** (§5.2): metadata is
     * per-instance configuration and is authoritative when present, so the
     * file's own root set is replaced rather than added to.
     *
     * The session's root is the one answer this and the anchor both read
     * (`_sessionDocumentRoot`, set by {@link _loadFromBlob}): when the load
     * gave the session one — the file's own root, or the one the engine created
     * above a lone content node (a legacy file's, or a one-file document's) —
     * they are already there. A legacy flat `.zip` with several top-level
     * objects has none, and there the engine root keeps them, exactly as before
     * D8.
     */
    private async _applyExternalRootBehaviors(
        behaviors: BehaviorData[] | undefined,
    ): Promise<void> {
        if (!behaviors?.length) return;
        if (this._sessionDocumentRoot) return;
        if (this._sceneRoot) await this._replaceRootBehaviors(this._sceneRoot, behaviors);
    }

    /** §5.2's whole-set replace of `target`'s live behaviors with `behaviors`. */
    private async _replaceRootBehaviors(target: SceneObject, behaviors: BehaviorData[]): Promise<void> {
        for (const live of [...target.getBehaviors()]) target.removeBehavior(live.name);
        target._setBehaviorData(structuredClone(behaviors));
        await SceneCreation.applyComponents(target, target.name);
        target._attachBehaviors();
        Logger.log(
            `[TraceScene] external root behaviors applied to "${target.name}": `
            + `${behaviors.map(b => b['@type']).join(', ')} (SCENE_ROOT_SPEC §5.2, whole-set replace)`,
        );
    }

    private initializeSceneRoot(): void {
        if (this._sceneRoot) {
            Logger.log("[TraceScene] Scene root already initialized");
            return;
        }

        const sceneData: SceneObjectData = {
            name: SCENE_ROOT_NAME,
            src: undefined,
            active: true,
            transform: {},
            behaviors: [],
        };

        this._sceneRoot = SceneCreation.initializeSceneObject(
            sceneData,
            this.babylonScene,
            this.resources,
            this.config,
            this,
            this._eventSystem || undefined
        );

        this.sceneObjects.push(this._sceneRoot);

        // Create deferred contact shadow now that the scene root exists
        this._ensureContactShadow();

        Logger.log("[TraceScene] Initialized scene root");
    }

    // ============================================
    // SELECTION API
    // ============================================

    /**
     * Select a scene object, or deselect if null.
     * Emits a `trace-viewer:selection-changed` event from the viewer element.
     *
     * @param target - SceneObject to select, transform path string, or null to deselect
     */
    public select(target: SceneObject | string | null, options?: SelectOptions): void {
        const previousObject = this._selectedObject;

        // Resolve target to SceneObject
        if (target === null) {
            this._selectedObject = null;
        } else if (typeof target === 'string') {
            // Resolve path to SceneObject
            this._selectedObject = this.resolveSceneObject(target) ?? null;
        } else {
            this._selectedObject = target;
        }

        // Emit selection changed event
        this._emitSelectionChanged(this._selectedObject, previousObject, options);

        Logger.log(`[TraceScene] Selection changed: ${previousObject?.name ?? null} -> ${this._selectedObject?.name ?? null}`);
    }

    /**
     * Get the currently selected SceneObject.
     * @returns The selected SceneObject, or null if nothing is selected
     */
    public getSelectedObject(): SceneObject | null {
        return this._selectedObject;
    }

    /**
     * Emit a selection-changed event from the trace-viewer element.
     */
    private _emitSelectionChanged(object: SceneObject | null, previousObject: SceneObject | null, options?: SelectOptions): void {
        const viewer = this._eventSystem;
        if (!viewer) return;

        const event = new CustomEvent('trace-viewer:selection-changed', {
            detail: { object, previousObject, additive: options?.additive ?? false },
            bubbles: true,
            composed: true
        });
        viewer.dispatchEvent(event);
        Logger.log('[TraceScene] Emitted trace-viewer:selection-changed event');
    }

    // ============================================
    // PLAY MODE API
    // ============================================

    /**
     * Set play mode state.
     * - Play mode (true): Interactions are active, pointer events trigger actions
     * - Edit mode (false): Selection is active, pointer events select objects
     *
     * When entering play mode, 'start' interactions are fired on all objects.
     * Mesh pickability is updated based on mode:
     * - Play mode: Only collider meshes are pickable (for objects with colliders)
     * - Edit mode: Visual meshes are pickable
     *
     * @param enabled - true for play mode, false for edit mode
     */
    public setPlayMode(enabled: boolean): void {
        const wasPlayMode = this._playMode;
        this._playMode = enabled;
        this._systemValues.setPlayMode(enabled);
        this._occluderMode.setPlaying(enabled);

        Logger.log(`[TraceScene] Play mode: ${enabled ? 'enabled' : 'disabled'}`);

        // Clear selection when entering play mode
        if (enabled && !wasPlayMode) {
            this.select(null);
        }

        // Update mesh pickability based on mode
        this._updateMeshPickability(enabled);

        // Apply effective click-to-select: play mode forces on, edit mode
        // restores the consumer's preference.
        this._applyClickToSelect();

        // Play starts from what the runtime slots hold: no script and no rest
        // pose sees an editor pick. Here rather than in
        // Properties.onPlayModeChanged, which runs after the start
        // interactions below.
        if (enabled && !wasPlayMode) {
            for (const obj of this.sceneObjects) obj.getBehavior(Properties)?.setEditorState(undefined, true);
        }

        // Fire start interactions when entering play mode
        if (enabled && (!wasPlayMode || !this._playModeInitialized)) {
            this._playModeInitialized = true;
            this._fireStartInteractions();
        }

        // Stop script behaviors when exiting play mode
        if (!enabled && wasPlayMode) {
            // A start event still waiting to fire belongs to a run that is
            // now over — the graphs holding it are about to be torn down.
            this._cancelStartEvents();
            this._fireStopScripts();

            // Nothing in flight at Stop may keep writing over the restored
            // authored state: cancel transform tweens through their owner
            // (clears the merge bookkeeping too), and route property-channel
            // cancellation through each object's PropertyResolver — a
            // manager-level cancel fires no callbacks, so cancelling the
            // resolver's `props:*` channels behind its back would leave its
            // overlay frozen mid-fade (`isTransitioning` true forever,
            // `setStateAwaited` barriers never draining). Consumers that
            // stop without a restore commit (viewer-app, MCP, headless)
            // depend on this. A final cancelAll() still sweeps any unowned
            // channel (this runs before behaviors hear play-off; Pages
            // re-cleans its own pageProps channel there for the same
            // owner-must-know reason).
            //
            // Stop returns every object to Base (CANOPY_STATES §8.4) here,
            // not in Properties.onPlayModeChanged: that hook hears the
            // playmode-changed event, in no fixed order with Pages' stop, and
            // not at all without an event target.
            for (const obj of this.sceneObjects) {
                obj.cancelTransformTransition();
                const props = obj.getBehavior(Properties);
                props?.stopAllTransitions();
                props?.resetStates();
            }
            getTransitionManager(this.babylonScene).cancelAll();
        }

        // Start/stop default animations for objects that have animation clips
        // but no Animation behavior to manage them.
        this._updateUnmanagedAnimations(enabled);

        // Emit play mode changed event
        this._emitPlayModeChanged(enabled);

        // Entering play on a scene that already exists: the graphs just
        // compiled by the event above deferred their start, but nothing is
        // being built, so it can run now — the same reasoning as startScene().
        // Callers (the editor's play button, headless probes, the bench render
        // page) read state straight after setPlayMode(true) and expect start
        // effects to have landed, as they did when graphs started inline.
        if (enabled) this._flushStartEvents();
    }

    /**
     * Start or stop default animations for objects whose clips are not managed
     * by an Animation behavior.  When entering play mode the first clip is
     * looped; when leaving play mode all unmanaged clips are stopped and reset.
     */
    private _updateUnmanagedAnimations(playing: boolean): void {
        for (const obj of this.sceneObjects) {
            // Own clips only: a clip TRACE_animation_binding bound to the
            // object never autoplays — its playback is the runtime's.
            const clips = obj.ownAnimationClips;
            if (clips.size === 0) continue;
            if (obj.getBehavior("Animation")) continue;

            if (playing) {
                const firstGroup = clips.values().next().value;
                if (firstGroup) {
                    firstGroup.start(true);
                }
            } else {
                for (const group of clips.values()) {
                    group.stop();
                    group.reset();
                }
            }
        }
    }

    /** Reapply native pointer policy after a behavior or its meshes change. */
    public refreshMeshPickability(object: SceneObject): void {
        this._updateMeshPickability(this._playMode, object);
    }

    private _updateMeshPickability(playMode: boolean, object?: SceneObject): void {
        // Build a set of all SceneObject transforms (and their animation roots)
        // so we can stop traversal at child SceneObject boundaries.
        const sceneObjectTransforms = new Set<TransformNode>();
        for (const obj of this.sceneObjects) {
            sceneObjectTransforms.add(obj.transform);
            if (obj.animationRoot) {
                sceneObjectTransforms.add(obj.animationRoot);
            }
        }

        for (const sceneObject of object ? [object] : this.sceneObjects) {
            // Get only meshes that belong to THIS scene object, not to child SceneObjects.
            // getChildMeshes(false) traverses the full Babylon subtree, so we must exclude
            // meshes whose ancestor (between them and our transform) is another SceneObject.
            const meshes = sceneObject.transform.getChildMeshes(false).filter(mesh => {
                let node: TransformNode | null = mesh.parent as TransformNode | null;
                while (node && node !== sceneObject.transform) {
                    if (sceneObjectTransforms.has(node) && node !== sceneObject.animationRoot) {
                        return false; // This mesh belongs to a child SceneObject
                    }
                    node = node.parent as TransformNode | null;
                }
                return true;
            });

            const pickThrough = sceneObject.getBehavior(Renderer)?._getResolvedData().pickThrough === true;
            const boxCollider = sceneObject.getBehavior(BoxCollider);
            const sphereCollider = sceneObject.getBehavior(SphereCollider);
            // Also check for collider-suffixed meshes created by other behaviors (e.g. Video
            // picker). Own meshes only: a child object's collider is the child's, and counting
            // it here narrowed this object to a collider it does not have.
            const hasColliderMesh = meshes.some(m => m.name.endsWith(COLLIDER_MESH_SUFFIX));
            const hasCollider = boxCollider !== null || sphereCollider !== null || hasColliderMesh;
            // Per-object interaction facts, hoisted out of the per-mesh loop.
            // The listener walk is only relevant (and only computed) for
            // objects with no own interactive surface — everything else takes
            // an earlier pickability branch.
            const isGraphHost = playMode && this._isGraphHost(sceneObject);
            const hasPseudoStates = playMode && ((sceneObject.getBehavior(Properties))?.hasPseudoStates() ?? false);
            // Drag behaviors grab via a pointer pick on the object's own
            // meshes (DragToMove requires pick.hit), so their presence is an
            // interaction declaration exactly like a graph host's — without
            // this, a Moveable/Rotatable object with no collider is
            // unpickable in play mode and silently cannot be dragged.
            const hasDragBehavior = playMode
                && (sceneObject.getBehavior("Moveable") !== null || sceneObject.getBehavior("Rotatable") !== null);
            const underDescendantListener = playMode && !hasCollider && !isGraphHost && !hasPseudoStates
                && !hasDragBehavior && this._hasDescendantPointerListener(sceneObject);

            Logger.log(`[TraceScene] _updateMeshPickability for "${sceneObject.name}": hasCollider=${hasCollider}, meshCount=${meshes.length}`);

            for (const mesh of meshes) {
                const isColliderMesh = mesh.name.endsWith(COLLIDER_MESH_SUFFIX);
                const isSelectionMesh = mesh.name.endsWith(SELECTION_MESH_SUFFIX);
                const isControlsMesh = mesh.name.endsWith(CONTROLS_MESH_SUFFIX);

                // Clear stale hover flags when a policy is changed during play.
                mesh.enablePointerMoveEvents = false;
                if (playMode) {
                    if (pickThrough) {
                        mesh.isPickable = false;
                    } else if (isControlsMesh) {
                        // Controls meshes (e.g. video timeline) must stay pickable
                        // so their Babylon GUI texture receives pointer events.
                        mesh.isPickable = true;
                    } else if (isSelectionMesh) {
                        // Selection meshes are edit-mode only
                        mesh.isPickable = false;
                    } else if (hasCollider) {
                        // Play mode with collider: only collider mesh is pickable
                        mesh.isPickable = isColliderMesh;
                        if (isColliderMesh) {
                            Logger.log(`[TraceScene]   Collider mesh "${mesh.name}" isPickable=true`);
                            // Pseudo-states (:hover via onPointerEvent) and graph
                            // hover blocks both need move-picks on the one pickable
                            // surface, which is this mesh.
                            if (hasPseudoStates || isGraphHost) {
                                mesh.enablePointerMoveEvents = true;
                            }
                        }
                    } else if (isGraphHost) {
                        // FlowGraph handles picking via native Babylon event blocks
                        // (FlowGraphMeshPickEventBlock, FlowGraphPointerOverEventBlock).
                        // Leave meshes pickable so Babylon's POINTERPICK fires, and
                        // enable move-picks so the pointer-over block ever sees one —
                        // unlike ClickToSelect it has no fallback scene.pick (#748).
                        // Not a per-frame cost: Babylon only picks on move lazily,
                        // when an observer reads pointerInfo.pickInfo (ClickToSelect
                        // does, every move in play mode); the flag only decides which
                        // meshes that pick may hit.
                        mesh.isPickable = true;
                        mesh.enablePointerMoveEvents = true;
                    } else if (hasPseudoStates) {
                        // Properties with pseudo-states (:hover, :active, :selected) need
                        // meshes pickable so pointer events reach onPointerEvent().
                        mesh.isPickable = true;
                        // Enable pointer move events so Babylon populates pickInfo on POINTERMOVE
                        // (without this, Babylon skips move-picking unless constantlyUpdateMeshUnderPointer is set)
                        mesh.enablePointerMoveEvents = true;
                    } else if (hasDragBehavior) {
                        // Moveable/Rotatable grab via a pick on the object's own
                        // meshes — the behavior is the interaction declaration.
                        mesh.isPickable = true;
                    } else if (underDescendantListener) {
                        // Content under a descendant pointer listener (an
                        // `advanceOnTap` deck, an ancestor graph host) must be
                        // pickable so its taps register at all — otherwise they
                        // read as misses and can never bubble or fire the
                        // ancestor's graph blocks.
                        mesh.isPickable = true;
                    } else {
                        // Play mode without collider, BehaviorGraph, or pseudo-states: nothing is pickable
                        mesh.isPickable = false;
                    }
                } else {
                    if (isSelectionMesh) {
                        // Edit mode: selection meshes are pickable
                        mesh.isPickable = true;
                    } else {
                        // Edit mode: visual meshes are pickable, collider meshes are not.
                        // Move-picks are a play-mode want; clear them so a mesh carries
                        // what this mode set, not whatever the last mode left.
                        mesh.isPickable = !isColliderMesh;
                        mesh.enablePointerMoveEvents = false;
                    }
                }
            }
        }
    }

    /**
     * Toggle debug visualization for all colliders in the scene.
     * Useful for debugging collider placement and size.
     * @param visible - true to show colliders, false to hide
     */
    public setCollidersDebugVisible(visible: boolean): void {
        for (const sceneObject of this.sceneObjects) {
            const boxCollider = sceneObject.getBehavior(BoxCollider);
            const sphereCollider = sceneObject.getBehavior(SphereCollider);

            if (boxCollider) {
                boxCollider.setDebugVisible(visible);
                Logger.log(`[TraceScene] "${sceneObject.name}" BoxCollider debug visible: ${visible}`);
            }
            if (sphereCollider) {
                sphereCollider.setDebugVisible(visible);
                Logger.log(`[TraceScene] "${sceneObject.name}" SphereCollider debug visible: ${visible}`);
            }
        }
    }

    /**
     * Check if play mode is active.
     * @returns true if in play mode, false if in edit mode
     */
    public isPlayMode(): boolean {
        return this._playMode;
    }

    /** System value provider for expression evaluation */
    public getSystemValues(): SystemValueProvider {
        return this._systemValues;
    }

    /**
     * Store the consumer's preferred click-to-select state for edit mode.
     * Play mode forces click-to-select on regardless; exiting play mode
     * restores this preference.
     */
    public setEditModeClickToSelect(enabled: boolean): void {
        this._editModeClickToSelect = enabled;
    }

    /**
     * Apply the effective click-to-select state.
     * Play mode always enables it (behaviors need pointer events);
     * edit mode uses the consumer's preference.
     */
    private _applyClickToSelect(): void {
        const effective = this._playMode || this._editModeClickToSelect;
        this._eventSystem?.applyClickRouting?.(effective);
    }

    /**
     * Handle a pointer event from the input system.
     * In play mode, executes interactions. In edit mode, handles selection.
     *
     * @param type - Event type ('select' or 'hover')
     * @param object - The SceneObject that was targeted
     * @param isEnter - true for enter/down events, false for exit/up events
     */
    public handlePointerEvent(type: 'select' | 'hover' | 'active', object: SceneObject, isEnter: boolean, options?: SelectOptions): void {
        // Emit raw pointer event for observability
        this._emitPointerEvent(type, object.name, isEnter);

        // Play mode: execute interactions, script hooks, and update selection
        if (this._playMode) {
            // Select objects registered for runtime selection (e.g. Moveable, Properties :selected).
            // In XR, allow selecting any object so users can grab/move anything.
            // Tapping a non-selectable object clears the current selection (except in XR).
            if (type === 'select' && isEnter) {
                const isXR = this._eventSystem?.isXRActive?.() ?? false;
                const selectable = this.getRuntimeSelectionManager().isRegistered(object) || isXR;
                this.select(selectable ? object : null);
            }

            // Script behaviors and generic pointer event hook
            const behaviors = object.getBehaviors();
            for (const behavior of behaviors) {
                if (behavior instanceof ScriptBehavior) {
                    if (type === 'select') {
                        void behavior.fireSelect(isEnter);
                    } else if (type === 'hover') {
                        void behavior.fireHover(isEnter);
                    }
                    // 'active' is not forwarded to scripts (CSS :active only)
                }
                else {
                    behavior.onPointerEvent(type, isEnter);
                }
            }

            // ── Bubbling dispatch (subtree responders) ──
            // An unclaimed tap bubbles up the SceneObject ancestry to the
            // nearest subtree responder (e.g. a Pages deck with advanceOnTap).
            // Scoped to select-enter — the only event with a subtree consumer
            // today; the walk generalizes if hover/active ever need it.
            // Empty-space taps go through handlePointerMiss and never reach
            // here, so miss-side gestures (deselect, host-app UI) are
            // untouched.
            if (type === 'select' && isEnter) {
                this._bubbleUnclaimedTap(object);
            }
            return;
        }

        // Edit mode: handle selection (only for 'select' events)
        if (type === 'select' && isEnter) {
            this.select(object, options);
        }
    }

    /**
     * Handle clicking empty space — deselect in both edit and play mode.
     */
    public handlePointerMiss(): void {
        this.select(null);
    }

    /**
     * True when the object hosts a behavior graph (FlowGraph via the
     * BehaviorGraph behavior, or a loaded KHR_interactivity extension).
     * THE graph-host predicate for pickability: a host and everything under
     * it stay pickable whatever its graph listens for, since hover blocks
     * need picks as much as select blocks do. Tap claims use the narrower
     * {@link _selectGraphTargets}.
     */
    private _isGraphHost(object: SceneObject): boolean {
        return this._hostsLoaderGraph(object) || object.getBehavior(BehaviorGraph) !== null;
    }

    /** True when Babylon's glTF loader runs a graph here (a Model's embedded KHR_interactivity). */
    private _hostsLoaderGraph(object: SceneObject): boolean {
        return object.extensionsUsed?.includes('KHR_interactivity') === true;
    }

    /**
     * The objects whose taps fire a running graph's onSelect: every
     * compiled select target in the scene. That is the host unless the
     * event names another object, which can sit anywhere — a document-level
     * graph may target a node deep inside a deck. A graph Babylon's glTF
     * loader runs resolves its targets only while executing, so its host
     * stands in for all of them and its whole subtree claims.
     */
    private _selectGraphTargets(): Set<SceneObject> {
        const targets = new Set<SceneObject>();
        for (const host of this.sceneObjects) {
            if (this._hostsLoaderGraph(host)) targets.add(host);
            for (const target of host.getBehavior(BehaviorGraph)?.selectTargets() ?? []) targets.add(target);
        }
        return targets;
    }

    /**
     * THE tap-claim rule for the tapped object: true when something acts on
     * a select of it, so the tap must not bubble on to a descendant
     * listener (a Pages deck would advance on the same tap). Only real
     * select handlers claim:
     * - a graph onSelect listening on it (see {@link _selectGraphTargets}),
     * - runtime selection (Moveable, Rotatable, `:selected` states),
     * - a ScriptBehavior (its onSelect hook),
     * - a behavior whose `claimsSelect()` says so (tap-to-play Video).
     *
     * Pickability is broader on purpose. A bare collider, `:hover`/`:active`
     * states or a collider picker mesh make an object pickable so hover and
     * press visuals run, but nothing happens on select, so the tap bubbles.
     * Studio adds a collider for every interaction preset, including
     * hover-only ones, so a collider cannot mean "handles taps".
     * Invariant: every claimant is pickable — a claim only decides anything
     * under a listener, and content there is pickable (see
     * {@link _hasDescendantPointerListener}) — but pickable does not imply
     * claimant.
     */
    private _claimsOwnTap(object: SceneObject, selectTargets: ReadonlySet<SceneObject>): boolean {
        if (selectTargets.has(object)) return true;
        if (this.getRuntimeSelectionManager().isRegistered(object)) return true;
        return object.getBehaviors().some(b => b instanceof ScriptBehavior || b.claimsSelect());
    }

    /**
     * THE ancestry walk behind bubbling dispatch and pickability — one
     * implementation so the two can never disagree (pickable content with
     * no listener, or an unpickable listener). Walks from the object up:
     * an ANCESTOR for which `ancestorClaims` holds claims the tap for its
     * subtree, then each hop's descendant listeners are visited
     * nearest-first. Graph select blocks match their target's descendants
     * (`_IsDescendantOf`), so a tap on a child genuinely fires an
     * ancestor's graph and bubbling past it would double-fire.
     *
     * The callers pass different ancestor tests on purpose: bubbling stops
     * only at a select target (a hover- or start-only graph on a page must
     * not block tap-advance on its content), pickability at any graph host
     * (its hover blocks need picks on the whole subtree).
     *
     * @param ancestorClaims whether an ancestor hop ends the walk with a
     *   claim of its own (visit is not called for it).
     * @param visit called per listening behavior; return true to stop the
     *   walk (a claim).
     * @returns true when anything claimed (an ancestor or visit).
     */
    private _walkDescendantPointerListeners(
        object: SceneObject,
        ancestorClaims: (ancestor: SceneObject) => boolean,
        visit: (behavior: TraceBehavior, node: SceneObject) => boolean,
    ): boolean {
        for (let node: SceneObject | null = object; node !== null; node = node.getParent()) {
            if (node !== object && ancestorClaims(node)) return true;
            for (const behavior of node.getBehaviors()) {
                if (behavior.wantsDescendantPointerEvents() && visit(behavior, node)) return true;
            }
        }
        return false;
    }

    /**
     * Bubble an unclaimed play-mode tap up the SceneObject ancestry —
     * nearest listener wins; the tapped leaf's own select handlers claim
     * before anything bubbles.
     */
    private _bubbleUnclaimedTap(object: SceneObject): void {
        const selectTargets = this._selectGraphTargets();
        if (this._claimsOwnTap(object, selectTargets)) return;
        this._walkDescendantPointerListeners(
            object,
            ancestor => selectTargets.has(ancestor),
            behavior => behavior.onDescendantPointerEvent('select', true, object),
        );
    }

    /**
     * True when the object is (or sits under) a descendant pointer listener
     * or an ancestor graph host — such content must be pickable in play
     * mode or its taps read as misses and can never bubble (for graph
     * hosts this also closes the latent gap where a host's child
     * SceneObjects were unpickable, so its descendant-matching blocks
     * could never fire).
     */
    private _hasDescendantPointerListener(object: SceneObject): boolean {
        return this._walkDescendantPointerListeners(object, ancestor => this._isGraphHost(ancestor), () => true);
    }

    /**
     * Fire 'start' on all script behaviors.
     * BehaviorGraph handles its own start via event/onStart blocks.
     */
    private _fireStartInteractions(): void {
        Logger.log('[TraceScene] Firing start events');

        for (const sceneObject of this.sceneObjects) {
            for (const behavior of sceneObject.getBehaviors()) {
                if (behavior instanceof ScriptBehavior) {
                    behavior.fireStart();
                }
            }
        }
    }

    /**
     * Stop all script behaviors when exiting play mode.
     * Fires onDestroy and removes update observers.
     */
    private _fireStopScripts(): void {
        Logger.log('[TraceScene] Stopping script behaviors');

        for (const sceneObject of this.sceneObjects) {
            for (const behavior of sceneObject.getBehaviors()) {
                if (behavior instanceof ScriptBehavior) {
                    behavior.fireStop();
                }
            }
        }
    }

    /**
     * Emit a play mode changed event.
     */
    private _emitPlayModeChanged(enabled: boolean): void {
        const viewer = this._eventSystem;
        if (!viewer) return;

        const event = new CustomEvent('trace-viewer:playmode-changed', {
            detail: { playMode: enabled },
            bubbles: true,
            composed: true
        });
        viewer.dispatchEvent(event);
        Logger.log(`[TraceScene] Emitted trace-viewer:playmode-changed: ${enabled}`);
    }

    // ============================================
    // SAFE AREA INSETS
    // ============================================

    /**
     * Get the current viewport safe area insets (pixels from each canvas edge).
     * UI overlays (like AutoUI) should offset their positioning by these values
     * to avoid being obscured by editor panels, device notches, etc.
     */
    public get safeAreaInsets(): Readonly<SafeAreaInsets> {
        return this._safeAreaInsets;
    }

    /**
     * Set viewport safe area insets. The hosting application (e.g. trace-sandbox)
     * calls this to inform the viewer about UI regions that overlap the canvas.
     * Overlays such as AutoUI keep clear of them, and camera framing fits the
     * content into the rest of the canvas. Emits a 'trace-viewer:safe-area-changed' event.
     */
    public setSafeAreaInsets(insets: Partial<SafeAreaInsets>): void {
        this._safeAreaInsets = {
            left: insets.left ?? 0,
            top: insets.top ?? 0,
            right: insets.right ?? 0,
            bottom: insets.bottom ?? 0,
        };
        this._emitSafeAreaChanged();
    }

    // ============================================
    // GUI FOCUS
    // ============================================

    /**
     * Whether any Babylon GUI input control currently has focus.
     * When true the host application should suppress keyboard shortcuts.
     */
    public get hasGUIFocus(): boolean {
        return this._guiFocusCount > 0;
    }

    /** Called by behaviors when a Babylon GUI input gains focus. */
    public guiFocusAcquired(): void {
        this._guiFocusCount++;
    }

    /** Called by behaviors when a Babylon GUI input loses focus. */
    public guiFocusReleased(): void {
        this._guiFocusCount = Math.max(0, this._guiFocusCount - 1);
    }

    private _emitSafeAreaChanged(): void {
        const viewer = this._eventSystem;
        if (!viewer) return;

        const event = new CustomEvent('trace-viewer:safe-area-changed', {
            detail: { safeArea: { ...this._safeAreaInsets } },
            bubbles: true,
            composed: true,
        });
        viewer.dispatchEvent(event);
    }

    /**
     * Emit a raw pointer event for observability/debugging.
     */
    private _emitPointerEvent(type: string, objectName: string, isEnter: boolean): void {
        const viewer = this._eventSystem;
        if (!viewer) return;

        const event = new CustomEvent('trace-viewer:pointer', {
            detail: { type, object: objectName, isEnter },
            bubbles: true,
            composed: true
        });
        viewer.dispatchEvent(event);
    }

    public getCamera(): ArcRotateCamera | null {
        return this.camera;
    }

    /**
     * Get the shared AudioManager (lazy-created on first access).
     * Used by Synth and Audio behaviors for spatial audio output.
     */
    public getAudioManager(): AudioManager {
        if (!this._audioManager) {
            this._audioManager = new AudioManager(this.babylonScene);
        }
        return this._audioManager;
    }

    /**
     * Get the shared RuntimeSelectionManager (lazy-created on first access).
     * Used by behaviors that need to select objects on interaction at runtime.
     */
    public getRuntimeSelectionManager(): RuntimeSelectionManager {
        if (!this._runtimeSelectionManager) {
            this._runtimeSelectionManager = new RuntimeSelectionManager(this);
        }
        return this._runtimeSelectionManager;
    }

    /**
     * Get all SceneObjects as an array.
     */
    public getSceneObjects(): SceneObject[] {
        return [...this.sceneObjects];
    }

    /**
     * Resolve a path to a SceneObject.
     *
     * Supported path formats:
     * - "Name"             - Legacy: lookup by name (first match)
     * - "./Child"          - Relative: child of context
     * - "../Sibling"       - Relative: sibling via parent
     * - "../../Name"       - Relative: navigate up multiple levels
     * - "/Root/UI/Panel"   - Absolute: path from scene root
     * - "$self"            - Keyword: the context object itself
     * - "$parent"          - Keyword: direct parent of context
     * - "$root"            - Keyword: scene root object
     *
     * @param path - The path to resolve
     * @param context - Optional context SceneObject for relative paths
     * @returns The resolved SceneObject, or null if not found
     */
    public resolveSceneObject(path: string, context?: SceneObject): SceneObject | null {
        if (!path) return null;

        // ===== Keywords =====
        if (path === '$self') {
            return context ?? null;
        }
        if (path === '$parent') {
            return context ? context.getParent() : null;
        }
        if (path === '$root') {
            return this._sceneRoot ?? null;
        }

        // ===== Absolute path: /Root/UI/Panel =====
        if (path.startsWith('/')) {
            return this.resolveAbsolutePath(path.slice(1));
        }

        // ===== Relative to context: ./Child or ./Child/Grandchild =====
        if (path.startsWith('./')) {
            return this.resolveRelativePath(path.slice(2), context);
        }

        // ===== Parent navigation: ../Sibling or ../../Ancestor =====
        if (path.startsWith('../')) {
            return this.resolveParentRelativePath(path, context);
        }

        // ===== Legacy: bare name lookup (backwards compatible) =====
        return this.resolveLegacyPath(path, context);
    }

    /**
     * @deprecated Use `sceneObject.getParent()` instead.
     */
    public getParentOf(sceneObject: SceneObject): SceneObject | null {
        return sceneObject.getParent();
    }

    /**
     * Compute the full absolute path of a SceneObject from the scene root.
     * Example: "Scene/UI/Panel/Button"
     */
    public getFullPath(sceneObject: SceneObject): string {
        const parts: string[] = [];
        let current: SceneObject | null = sceneObject;
        while (current) {
            parts.unshift(current.name);
            current = current.getParent();
        }
        return parts.join('/');
    }

    /**
     * Compute a relative path from a context object to a target object.
     * Used for portable interaction targets that work when assets are imported into different scenes.
     *
     * Path formats:
     * - "$self"           - target is the same as context
     * - "./Child"         - target is a direct child of context
     * - "./Child/Grand"   - target is a descendant of context
     * - "../Sibling"      - target is a sibling of context
     * - "../../Name"      - target is in an ancestor's subtree
     *
     * @param target - The target object to create a path for
     * @param context - The context object (usually the object containing the interaction)
     * @returns A relative path string, or the target's name if no context provided
     */
    public getRelativePath(target: SceneObject, context: SceneObject | null): string {
        // If no context, fall back to target's name (legacy behavior)
        if (!context) {
            return target.name;
        }

        // Same object
        if (target === context) {
            return '$self';
        }

        // Build ancestor chains for both objects
        const contextChain = this.getAncestorChain(context);
        const targetChain = this.getAncestorChain(target);

        // Check if target is a descendant of context
        if (targetChain.length > contextChain.length) {
            let isDescendant = true;
            for (let i = 0; i < contextChain.length; i++) {
                if (targetChain[i] !== contextChain[i]) {
                    isDescendant = false;
                    break;
                }
            }
            if (isDescendant) {
                // Target is a descendant of context
                const relativeParts = targetChain.slice(contextChain.length).map(o => o.name);
                return './' + relativeParts.join('/');
            }
        }

        // Find common ancestor
        let commonLength = 0;
        for (let i = 0; i < Math.min(contextChain.length, targetChain.length); i++) {
            if (contextChain[i] === targetChain[i]) {
                commonLength = i + 1;
            } else {
                break;
            }
        }

        // Compute relative path: go up to common ancestor, then down to target
        const stepsUp = contextChain.length - commonLength;
        const upPart = '../'.repeat(stepsUp);
        const downParts = targetChain.slice(commonLength).map(o => o.name);
        return upPart + downParts.join('/');
    }

    /**
     * Get the ancestor chain of a SceneObject as an array (from root to object).
     * @internal
     */
    private getAncestorChain(sceneObject: SceneObject): SceneObject[] {
        const chain: SceneObject[] = [];
        let current: SceneObject | null = sceneObject;

        while (current) {
            chain.unshift(current);
            current = current.getParent();
        }

        return chain;
    }

    /**
     * Resolve an absolute path from the scene root.
     * Example: "Root/UI/Panel" (without leading /)
     */
    private resolveAbsolutePath(path: string): SceneObject | null {
        if (!path) return this._sceneRoot ?? null;

        const parts = path.split('/').filter(p => p !== '');
        if (parts.length === 0) return this._sceneRoot ?? null;

        // Start from scene root
        let current: SceneObject | null = this._sceneRoot ?? null;

        // If the first segment matches the root name, consume it
        // (getFullPath() includes the root, e.g. "Scene/Cube")
        let startIdx = 0;
        if (current && parts[0] === current.name) {
            startIdx = 1;
        }

        for (let i = startIdx; i < parts.length; i++) {
            // Try to find a SceneObject child with this name
            const childSceneObject = this.findChildSceneObject(current, parts[i]);
            if (childSceneObject) {
                current = childSceneObject;
            } else {
                // No SceneObject found - stop at SceneObject leaves
                return null;
            }
        }

        return current;
    }

    /**
     * Resolve a path relative to the context object.
     * Example: "Child/Grandchild" (without leading ./)
     */
    private resolveRelativePath(path: string, context?: SceneObject): SceneObject | null {
        if (!context) return null;
        if (!path) return context;

        const parts = path.split('/').filter(p => p !== '');
        if (parts.length === 0) return context;

        let current: SceneObject | null = context;

        for (const part of parts) {
            // Try to find a SceneObject child with this name
            const childSceneObject = this.findChildSceneObject(current, part);
            if (childSceneObject) {
                current = childSceneObject;
            } else {
                // No SceneObject found - stop at SceneObject leaves
                return null;
            }
        }

        return current;
    }

    /**
     * Resolve a parent-relative path (../ syntax).
     * Example: "../Sibling" or "../../Ancestor/Child"
     */
    private resolveParentRelativePath(path: string, context?: SceneObject): SceneObject | null {
        if (!context) return null;

        const parts = path.split('/');
        let current: SceneObject | null = context;

        let i = 0;
        // Navigate up for each ".."
        while (i < parts.length && parts[i] === '..') {
            current = current.getParent();
            if (!current) return null;
            i++;
        }

        // If there are remaining parts, resolve them relative to current
        if (i < parts.length) {
            const remainingPath = parts.slice(i).join('/');
            return this.resolveRelativePath(remainingPath, current);
        }

        return current;
    }

    /**
     * Legacy path resolution for backwards compatibility.
     * Searches all objects by name, then tries hierarchical path matching.
     */
    private resolveLegacyPath(path: string, _context?: SceneObject): SceneObject | null {
        // Direct name lookup (most common case) - find first match
        const directMatch = this.sceneObjects.find(obj => obj.name === path);
        if (directMatch) {
            return directMatch;
        }

        // Handle hierarchical paths (e.g., "Parent/Child/Target")
        const parts = path.split('/');
        if (parts.length === 1) {
            // Single name that wasn't found directly
            return null;
        }

        // For hierarchical paths, find by final name and verify hierarchy
        const targetName = parts[parts.length - 1];
        for (const sceneObject of this.sceneObjects) {
            if (sceneObject.name === targetName) {
                if (this.verifyHierarchyPath(sceneObject, parts)) {
                    return sceneObject;
                }
            }
        }

        return null;
    }

    /**
     * Find a child SceneObject by name.
     */
    private findChildSceneObject(parent: SceneObject | null, childName: string): SceneObject | null {
        if (!parent) {
            // If no parent, search root-level objects
            for (const sceneObject of this.sceneObjects) {
                if (sceneObject.name === childName && !sceneObject.transform.parent) {
                    return sceneObject;
                }
            }
            return null;
        }

        // Search for a SceneObject whose parent transform matches
        // (child may be parented to parent.transform or parent.animationRoot)
        for (const sceneObject of this.sceneObjects) {
            if (sceneObject.name === childName) {
                const parentTx = sceneObject.transform.parent;
                if (parentTx === parent.transform || (parent.animationRoot && parentTx === parent.animationRoot)) {
                    return sceneObject;
                }
            }
        }
        return null;
    }

    /**
     * Verify that a SceneObject matches a hierarchical path by checking its parent chain.
     */
    private verifyHierarchyPath(sceneObject: SceneObject, pathParts: string[]): boolean {
        let current: SceneObject | null = sceneObject;
        const reversedParts = [...pathParts].reverse();

        for (let i = 0; i < reversedParts.length; i++) {
            if (!current) return false;
            if (current.name !== reversedParts[i]) return false;

            if (i < reversedParts.length - 1) {
                // Walk up to parent using SceneObject-aware resolution
                current = current.getParent();
                if (!current) return false;
            }
        }

        return true;
    }

    /**
     * Find the SceneObject that owns a given transform node.
     * Useful for reverse-lookup when you have a Babylon.js node but need the SceneObject.
     *
     * @param node - The transform node to find the owner of
     * @returns The SceneObject that owns this node, or undefined if not found
     */
    public findSceneObjectByTransform(node: TransformNode | Node | null): SceneObject | undefined {
        if (!node) return undefined;
        // Positive-only ownership cache: this lookup is a linear scan, and it
        // now sits inside walks (child resolution, ancestor hops), turning
        // O(N) into O(N²) on scene-sized operations without it. A node's
        // owner never changes for the node's lifetime, but entries are
        // RE-VERIFIED on hit (O(1)) so a stale mapping (flattened animation
        // root, reload) falls through to the scan instead of lying.
        const cached = this._transformOwnerCache.get(node);
        if (cached && (cached.transform === node || cached.isAnimationRoot(node) || cached.isAnimationLayer(node))) {
            return cached;
        }
        for (const sceneObject of this.sceneObjects) {
            if (sceneObject.transform === node || sceneObject.isAnimationRoot(node) || sceneObject.isAnimationLayer(node)) {
                this._transformOwnerCache.set(node, sceneObject);
                return sceneObject;
            }
        }
        return undefined;
    }

    /**
     * @deprecated Use `sceneObject.getSiblings()` instead.
     */
    public getSiblings(sceneObject: SceneObject): SceneObject[] {
        return sceneObject.getSiblings();
    }

    /**
     * @deprecated Use `sceneObject.getSiblings()` and map to names instead.
     */
    public getSiblingNames(sceneObject: SceneObject): Set<string> {
        return new Set(sceneObject.getSiblings().map(s => s.name));
    }

    /**
     * Rename a SceneObject. Updates the name across all internal representations
     * (SceneObject.name, sceneData, Babylon TransformNode, animation root).
     *
     * Parent paths (`transform.parent`) are recomputed from the live scene graph
     * on export, so they automatically reflect the new name.
     *
     * @param sceneObject - The object to rename
     * @param newName - The new name (must be non-empty, no '/', sibling-unique)
     * @returns true on success, false if validation fails
     */
    public renameSceneObject(sceneObject: SceneObject, newName: string): boolean {
        // Validate
        if (!newName || newName.includes('/')) return false;
        if (sceneObject === this._sceneRoot) return false;

        const oldName = sceneObject.name;
        if (oldName === newName) return true;

        // Check sibling uniqueness (exclude self)
        const siblingNames = new Set(sceneObject.getSiblings().map(s => s.name));
        siblingNames.delete(oldName);
        if (siblingNames.has(newName)) return false;

        // Apply the rename
        sceneObject._setName(newName);

        // Emit events
        const viewer = this._eventSystem;
        if (viewer) {
            viewer.dispatchEvent(new CustomEvent('trace-viewer:object-renamed', {
                detail: { object: sceneObject, oldName, newName },
                bubbles: true,
                composed: true
            }));
        }
        this._emitSceneChanged([newName]);
        this._emitTopologyChanged();

        return true;
    }

    /**
     * Reparent preserving the object's WORLD pose: Babylon's
     * `transform.setParent` recomputes the local transform against the new
     * parent, so the object stays put on screen. Contrast with
     * {@link reparentSceneObject}, a raw slot assignment that KEEPS the local
     * transform — right when serialized locals are authored relative to the
     * intended parent (loads, undo restoration), wrong for interactive or
     * programmatic moves of live content, which visibly teleport under a
     * differently-posed parent.
     *
     * Routes through the setParent interceptor, which supplies sibling name
     * dedup, the reparent-policy veto (vetoed moves revert in place →
     * `moved: false`), and the topology signal (so the reference-identity
     * registry heals interaction-target paths). The recomputed local pose is
     * persisted into authored sceneData, in Play as in edit mode — without
     * that, a later state revert or reload would snap the object back to its
     * stale pre-move local.
     *
     * Content nests under the parent's animation root when one exists, same
     * as {@link SceneObject.setParent} — animations drive that intermediate
     * node, so children ride the animated pose.
     *
     * @returns `{ moved, renamed, newName }` — `moved` false when a policy
     * veto reverted the change; `renamed` true when sibling dedup renamed.
     * @throws when `newParent` is inside an imported scene-format reference
     * subtree (same contract as {@link reparentSceneObject}).
     */
    public reparentSceneObjectPreservingWorld(
        sceneObject: SceneObject,
        newParent: SceneObject,
    ): { moved: boolean; renamed: boolean; newName: string } {
        if (!this.canParentUnder(newParent)) {
            throw this._vetoReferenceSubtreeParent(sceneObject.name, newParent);
        }
        const oldName = sceneObject.name;
        const oldParentNode = sceneObject.transform.parent;

        const targetNode = newParent.animationRoot ?? newParent.transform;
        // Same-parent no-op: skip the move and the (not-cheap) authored-data
        // re-serialization below — nothing changed.
        if (oldParentNode === targetNode) {
            return { moved: true, renamed: false, newName: oldName };
        }
        sceneObject.transform.setParent(targetNode);

        // Interceptor may have vetoed (custom policy) — reverted in place.
        if (sceneObject.transform.parent === oldParentNode && oldParentNode !== targetNode) {
            return { moved: false, renamed: false, newName: oldName };
        }

        // Persist the recomputed local into authored sceneData and serialize
        // behaviors. The interceptor writes the local only in edit mode, and
        // already handled dedup + topology.
        sceneObject.refreshSceneData();
        const newName = sceneObject.name;
        return { moved: true, renamed: newName !== oldName, newName };
    }

    /**
     * Reparent a SceneObject under a new parent, auto-renaming if the name
     * conflicts with an existing sibling at the destination.
     *
     * @param sceneObject - The object to reparent
     * @param newParent - The new parent SceneObject
     * @returns `{ renamed, newName }` — `renamed` is true if a rename was needed
     */
    public reparentSceneObject(
        sceneObject: SceneObject,
        newParent: SceneObject,
    ): { renamed: boolean; newName: string } {
        if (!this.canParentUnder(newParent)) {
            throw this._vetoReferenceSubtreeParent(sceneObject.name, newParent);
        }

        const oldName = sceneObject.name;

        // Build the set of sibling names at the destination.
        // We collect names from all children of newParent, excluding the object
        // being moved (it may already be a child when reordering within the same parent).
        const siblingNames = new Set<string>();
        for (const sib of newParent.getChildren()) {
            if (sib !== sceneObject) siblingNames.add(sib.name);
        }

        const uniqueName = Utility.generateUniqueName(oldName, siblingNames);
        const renamed = uniqueName !== oldName;

        if (renamed) {
            sceneObject._setName(uniqueName);
        }

        sceneObject.setParent(newParent);

        if (renamed) {
            const viewer = this._eventSystem;
            if (viewer) {
                viewer.dispatchEvent(new CustomEvent('trace-viewer:object-renamed', {
                    detail: { object: sceneObject, oldName, newName: uniqueName },
                    bubbles: true,
                    composed: true,
                }));
            }
            this._emitSceneChanged([uniqueName]);
        }

        return { renamed, newName: uniqueName };
    }

    /**
     * Give each newcomer a name unique among its siblings. A document can name
     * two siblings alike (a glTF name is free text), so an editing-session load
     * and a dissolve run this over the objects they add: the later one in the
     * parent's child list (the outline order) becomes "Chair (1)". Objects
     * already in the scene keep their names. Content inside a scene reference
     * is skipped: it belongs to the referenced file, which the player loads as
     * written, so it keeps the file's names until a dissolve makes it this
     * scene's own.
     */
    private _dedupeSiblingNames(newcomers: readonly SceneObject[]): void {
        const isNew = new Set(newcomers);
        const parents = new Set(newcomers.map(object => object.getParent()));
        for (const parent of parents) {
            if (!parent || !this.canParentUnder(parent)) continue;
            const children = parent.getChildren();
            const all = new Set(children.map(child => child.name));
            const taken = new Set(children.filter(child => !isNew.has(child)).map(child => child.name));
            for (const child of children) {
                if (!isNew.has(child)) continue;
                if (taken.has(child.name)) {
                    child._setName(Utility.generateUniqueName(child.name, all));
                    all.add(child.name);
                }
                taken.add(child.name);
            }
        }
    }

    /**
     * Get names of all sibling SceneObjects based on a parent TransformNode.
     * Used internally for plugin-loaded scenes.
     */
    private getSiblingNamesByTransform(parentTransform: Node | null): Set<string> {
        if (!parentTransform) {
            // Root level
            return new Set(
                this.sceneObjects
                    .filter(obj => !obj.transform.parent)
                    .map(obj => obj.name)
            );
        }
        const parentObj = this.findSceneObjectByTransform(parentTransform);
        if (parentObj) {
            return new Set(parentObj.getChildren().map(c => c.name));
        }
        // Fallback: raw transform without a SceneObject (e.g. during initial load)
        const children = (parentTransform as TransformNode).getChildren<TransformNode>();
        const names = new Set<string>();
        for (const child of children) {
            const sceneObj = this.findSceneObjectByTransform(child);
            if (sceneObj) names.add(sceneObj.name);
        }
        return names;
    }

    /**
     * Push modified scene data to update a scene object.
     *
     * Note: The viewer takes ownership of the data by cloning it.
     * Callers should not mutate the object after passing it to this method.
     *
     * @param data - Scene object data to apply (will be cloned)
     * @returns The full behaviors array after refresh, or null if object not found
     */
    public async pushSceneData(data: SceneObjectData): Promise<BehaviorData[] | null> {
        // Use resolveSceneObject for lookup - supports both FQTP and legacy bare names
        const sceneObject = this.resolveSceneObject(data.name);
        if (sceneObject) {
            Logger.log("Pushed scene data to scene object:", data);

            // Delegate to SceneObject.setData, which handles cloning, reload, and refresh
            const behaviors = await sceneObject.setData(data);

            // Emit scene changed event
            this._emitSceneChanged([data.name]);

            return behaviors;
        }
        return null;
    }

    /**
     * Notify that a SceneObject was renamed (e.g. during interceptor-based
     * reparenting). Dispatches object-renamed event and emits scene-changed.
     * Called by SceneObject._resolveNameContention.
     */
    _notifyObjectRenamed(sceneObject: SceneObject, oldName: string, newName: string): void {
        const viewer = this._eventSystem;
        if (viewer) {
            viewer.dispatchEvent(new CustomEvent('trace-viewer:object-renamed', {
                detail: { object: sceneObject, oldName, newName },
                bubbles: true,
                composed: true,
            }));
        }
        this._emitSceneChanged([newName]);
        this._emitTopologyChanged();
    }

    /**
     * Emit a canonical scene-changed event from the trace-viewer element.
     * Called after any mutation that changes persisted scene data. Its
     * `sceneData` is serialized on the first read, once per event: a build
     * creates objects one at a time, and serializing the scene for each made
     * it quadratic. A listener reads it synchronously.
     *
     * @param changedObjectNames - Optional array of object names that changed
     */
    private _emitSceneChanged(changedObjectNames?: string[]): void {
        const viewer = this._eventSystem;
        if (!viewer) return;

        let sceneData: SceneObjectData[] | undefined;
        const serialize = () => (sceneData ??= this.getSceneDataList());
        const event = new CustomEvent('trace-viewer:scene-changed', {
            detail: {
                get sceneData() { return serialize(); },
                changedObjectNames
            },
            bubbles: true,
            composed: true
        });
        viewer.dispatchEvent(event);
        Logger.log('[TraceScene] Emitted trace-viewer:scene-changed event');
    }

    public getSceneDataList(): SceneObjectData[] {
        const sceneDataList: SceneObjectData[] = [];

        this.sceneObjects.forEach((sceneObject) => {
          const sceneData = sceneObject.getSceneObjectData();
          sceneDataList.push(sceneData);
        });

        return sceneDataList;
    }

    /**
     * The scene as a **document**: file-local parent paths and exactly one root
     * record, the document root (SCENE_ROOT_SPEC D8).
     *
     * The engine root is not in it and never was — it is a mount point, not
     * authored data — and now neither is a synthetic `"Scene"` record standing
     * in for it: the document root is a real object of the scene, so it
     * serializes like any other node, behaviors and all.
     *
     * A session whose engine root does not hold exactly one child (a legacy
     * flat `.zip`, an editor that has not adopted D8, an empty scene) has its
     * root SYNTHESIZED here, loudly — see {@link _ensureDocumentRootRecord}.
     * The live scene is untouched; an editor decides when to adopt one
     * ({@link ensureDocumentRoot}).
     */
    public getFullExportData(options: SubtreeExclusionOptions = {}): SceneObjectData[] {
        const built = this._buildDocumentExportData();
        return applySubtreeExclusion(built.data, built.sceneObjects, options.exclude).data;
    }

    /**
     * {@link getFullExportData} with the live object each record was built
     * from, for the export pipeline's asset passes. The synthesized root, when
     * there is one, has no live owner and pairs with `null`.
     *
     * @internal
     */
    public _buildDocumentExportData(): { data: SceneObjectData[]; sceneObjects: (SceneObject | null)[] } {
        const built = this._buildExportData();
        const data = built.data;
        const sceneObjects: (SceneObject | null)[] = [...built.sceneObjects];
        TraceScene.stripSceneRootPrefix(data);
        const synthesized = this._ensureDocumentRootRecord(data);
        if (synthesized) {
            data.unshift(synthesized);
            sceneObjects.unshift(null);
        }
        this._emitStrandedRootBehaviors(data[0]);
        this._clearAnchorHostPose(data, sceneObjects);
        return { data, sceneObjects };
    }

    /**
     * Keep the CMS image anchor out of the document root record (#933).
     *
     * Under D8 the CMS-managed `ImageAnchor` lives on the document root, a
     * serialized node, and two things of the anchor's leaked into the file with
     * it. Both are dropped here:
     *
     * - **The behavior.** It is `cmsManaged`: the CMS Scene record owns it and
     *   `loadCmsScene` rebuilds it from there, so it never belongs in the file
     *   (SCENE_ROOT_SPEC §5.2–5.3; `serializeBehaviors({ excludeCmsManaged })`).
     * - **The host pose.** The host's live transform is the desktop preview
     *   (`applyAnchorRotation`: `Rx(pitch)` plus a lift above the ground), not
     *   authored data. The marker preview hangs below the host, so where content
     *   sits relative to the marker never depends on it. Written, it is applied a
     *   second time by an XR reader that loads the host as saved.
     *
     * The two go together. A reload that finds the behavior already in the file
     * re-commits it, and `ImageAnchor._applySceneRootRotation` then counter-rotates
     * the children to keep their world pose from the SAVED host pose — so an
     * identity host next to a saved Horizontal anchor would tilt the content in
     * the editor. With neither in the file the anchor attaches fresh, as it does
     * for a `.zip`, and the editor poses the host exactly as before.
     */
    private _clearAnchorHostPose(data: SceneObjectData[], sceneObjects: (SceneObject | null)[]): void {
        const index = data.findIndex(d => !d.transform?.parent);
        const host = index >= 0 ? sceneObjects[index] : null;
        if (!host) return;
        // Filter the record rather than re-serialize it with
        // `serializeBehaviors({ excludeCmsManaged: true })`: by now the record
        // also carries the engine root's stranded behaviors
        // ({@link _emitStrandedRootBehaviors}) and `refreshSceneData`'s
        // null-cleaning, and a fresh serialization would drop both. The key is
        // the behavior's own serialized `@type` — the field the record holds.
        const managed = new Set(host.getBehaviors().filter(isCmsManagedBehavior).map(b => b.serialize()['@type']));
        if (managed.size === 0) return;
        const record = data[index];
        record.behaviors = (record.behaviors ?? []).filter(b => !managed.has(b['@type']));
        if (!managed.has('ImageAnchor')) return;
        record.transform = {
            ...record.transform,
            translation: [0, 0, 0],
            rotation: [0, 0, 0, 1],
            scale: [1, 1, 1],
        };
    }

    /**
     * Behaviors left on the ENGINE root go out on the document root.
     *
     * The engine root is a mount point and is never serialized (D8), so a
     * behavior attached there would simply vanish at save. Nothing in the
     * viewer puts one there any more — CMS metadata and `OpenOptions.behaviors`
     * land on the document root (§5.1) — but an editor that has not adopted D8
     * still might, and the save is not the place to discover it. So they
     * travel, onto the node that is allowed to carry them, and the
     * drop-that-wasn't is logged.
     *
     * CMS-managed behaviors (an `ImageAnchor`) are excluded, exactly as the
     * file path has always excluded them.
     */
    private _emitStrandedRootBehaviors(rootRecord: SceneObjectData | undefined): void {
        if (!rootRecord) return;
        const sceneRoot = this.getSceneRoot();
        if (!sceneRoot) return;
        sceneRoot.refreshSceneData();
        const stranded = sceneRoot.serializeBehaviors({ excludeCmsManaged: true }) ?? [];
        if (stranded.length === 0) return;

        Logger.warn(
            `[TraceScene] export: ${stranded.length} behavior(s) are attached to the ENGINE root `
            + `(${stranded.map(b => b['@type']).join(', ')}), which is a runtime mount point and is never `
            + `serialized (SCENE_ROOT_SPEC D8). Writing them on the document root "${rootRecord.name}" instead; `
            + 'an editor should author them there.',
        );
        rootRecord.behaviors = [...(rootRecord.behaviors ?? []), ...stranded];
    }

    /**
     * Give an export exactly one root record, synthesizing one when the
     * session does not have a document root (SCENE_ROOT_SPEC §4, D8).
     *
     * The written document always has one root; a session may not, because a
     * scene can be opened from a legacy flat archive or built by an editor
     * that has not adopted the rule. Rather than write a multi-root document
     * that every reader would then have to migrate — or silently drop
     * content — the writer groups what is there under one root, the same shape
     * `promoteToAsset`'s `alwaysGroup` produces: identity transform, no src, no
     * behaviors, named for the scene.
     *
     * Loud on purpose: a session that needs this is a session whose editor
     * still has to adopt D8.
     *
     * @returns the synthesized record, or undefined when the data already has
     *   exactly one root.
     */
    private _ensureDocumentRootRecord(data: SceneObjectData[]): SceneObjectData | undefined {
        const roots = data.filter(d => !d.transform?.parent);
        if (roots.length === 1) return undefined;

        const taken = new Set(data.map(d => d.name));
        let name = this.sceneName || SCENE_ROOT_NAME;
        for (let n = 2; taken.has(name); n++) name = `${this.sceneName || SCENE_ROOT_NAME}_${n}`;

        Logger.warn(
            `[TraceScene] export: this session has ${roots.length} top-level object(s), but a Trace document `
            + `has exactly one root node (SCENE_ROOT_SPEC D8). Grouping them under "${name}" in the EXPORTED `
            + 'document; the live scene is unchanged. An editor should call ensureDocumentRoot() so the '
            + 'session and the file agree.',
        );

        for (const record of data) {
            if (!record.transform?.parent) {
                record.transform = { ...(record.transform ?? {}), parent: name };
            } else {
                record.transform.parent = `${name}/${record.transform.parent}`;
            }
        }
        return { name, active: true, transform: {}, behaviors: [] };
    }

    /**
     * Get scene data ready for export.
     *
     * Returns only content objects (excludes the implicit scene root).
     * Walks the live scene-root hierarchy — orphans or detached SceneObjects
     * not parented under the scene root are excluded.
     *
     * Parent paths are absolute and include the engine root's prefix
     * (e.g. "/Scene/Cube"). What a document is WRITTEN from is
     * {@link getFullExportData}, which makes them file-local and guarantees
     * the one root record D8 requires; {@link stripSceneRootPrefix} is the
     * step in between, for the flat `.zip` list.
     *
     * `options.exclude` drops the same subtrees the writers drop
     * (CONTENT_MODEL_SPEC §2.9) — the reason a HOST needs it is the dirty
     * snapshot: an editor comparing this against its last saved snapshot sees
     * its editor-only subtree change and re-uploads a scene document whose
     * bytes the writer had already excluded. The spec's rule is that a
     * references-only edit writes one editor document and no scene document,
     * and this is what lets a host honour it. The filtering is by SceneObject
     * identity, which a host cannot do from the returned records alone: their
     * `transform.parent` is a path string with no live object behind it.
     */
    public getExportData(options: SubtreeExclusionOptions = {}): SceneObjectData[] {
        const built = this._buildExportData();
        return applySubtreeExclusion(built.data, built.sceneObjects, options.exclude).data;
    }

    /**
     * Build export data for content objects (descendants of scene root).
     * Returns parallel arrays of SceneObjectData and the SceneObjects they were
     * built from, so the export pipeline can read live behaviors/assets without
     * a name-keyed lookup (names are not unique).
     *
     * @internal
     */
    public _buildExportData(): { data: SceneObjectData[]; sceneObjects: SceneObject[] } {
        const sceneRoot = this.getSceneRoot();
        if (!sceneRoot) {
            return { data: [], sceneObjects: [] };
        }

        // Walk from sceneRoot — only its descendants are part of the scene.
        // Descendants of a scene-format reference node (.trace src) are
        // derived from that scene and omitted from export; children of asset
        // srcs (primitives, models) are authored and kept.
        const exportObjects = TraceScene._filterDescendantsOfReferenceNodes(sceneRoot.getDescendants());

        for (const so of exportObjects) {
            if (so.refreshSceneData) {
                so.refreshSceneData();
            }
        }

        const sorted = this.topologicalSortForExport(exportObjects);

        const data: SceneObjectData[] = [];
        for (const so of sorted) {
            const d = so.getSceneObjectData();

            // Always set parent explicitly — refreshSceneData/captureTransform
            // doesn't touch transform.parent, so stale values may linger.
            if (d.transform) {
                const parent = so.getParent();
                if (parent) {
                    d.transform.parent = this.getFullPath(parent);
                } else {
                    delete d.transform.parent;
                }
            }

            data.push(d);
        }

        return { data, sceneObjects: sorted };
    }

    /**
     * Get export data for a subtree rooted at the given SceneObject.
     *
     * Returns the root object and all its descendants, topologically sorted.
     * Parent paths are rewritten so the subtree root becomes top-level
     * (e.g. if root is "GroupA" at "/Scene/GroupA", a child at
     * "/Scene/GroupA/Child" gets parent="/GroupA").
     */
    public getExportDataForSubtree(subtreeRoot: SceneObject): SceneObjectData[] {
        return this._buildExportDataForSubtree(subtreeRoot).data;
    }

    /**
     * Same as {@link getExportDataForSubtree} but also returns the paired
     * SceneObject list so the export pipeline can read live behaviors without
     * a name-keyed lookup.
     *
     * @internal
     */
    public _buildExportDataForSubtree(subtreeRoot: SceneObject): { data: SceneObjectData[]; sceneObjects: SceneObject[] } {
        // Descendants of a scene-format reference node are derived and omitted
        // from export. The subtree root itself is always included even if it
        // has a src — exporting a single reference is the normal case.
        const subtreeObjects = [subtreeRoot, ...TraceScene._filterDescendantsOfReferenceNodes(subtreeRoot.getDescendants())];
        const subtreeSet = new Set(subtreeObjects);

        for (const so of subtreeObjects) {
            if (so.refreshSceneData) {
                so.refreshSceneData();
            }
        }

        const sorted = this.topologicalSortForExport(subtreeObjects);
        const subtreeRootFullPath = this.getFullPath(subtreeRoot);
        // Strip everything before the root's own name, e.g. "Scene/GroupA" → prefix "Scene/"
        const lastSlash = subtreeRootFullPath.lastIndexOf('/');
        const prefixToStrip = lastSlash >= 0 ? subtreeRootFullPath.slice(0, lastSlash + 1) : '';

        const data: SceneObjectData[] = [];
        for (const so of sorted) {
            const d = so.getSceneObjectData();

            if (d.transform) {
                const parent = so.getParent();
                if (parent && subtreeSet.has(parent)) {
                    // Parent is within the subtree — rewrite path relative to subtree
                    const fullParentPath = this.getFullPath(parent);
                    d.transform.parent = prefixToStrip
                        ? fullParentPath.slice(prefixToStrip.length)
                        : fullParentPath;
                } else {
                    // Root of the subtree or parent is outside — no parent
                    delete d.transform.parent;
                }
            }

            data.push(d);
        }

        return { data, sceneObjects: sorted };
    }

    /**
     * Export data for SEVERAL subtrees at once, optionally under one
     * synthesized group root — the promotion path (CONTENT_MODEL_SPEC §3.5,
     * SCENE_ROOT_SPEC §4).
     *
     * A component asset must have exactly one entry in `scenes[0].nodes[]`, so
     * a multi-object promotion has to be grouped. The group is created **here,
     * in the export records** — the live scene is not touched. Promotion is a
     * read of the scene, and an editor that wants the visible Figma-frame move
     * runs its own (undoable) group command first and then promotes the single
     * root it produced.
     *
     * Two things the single-root builder does not have to think about:
     *
     * - **Frames.** Each root's authored transform is local to *its own*
     *   parent, and a selection may span parents. The group node is at
     *   identity in the SCENE ROOT's frame, and each root is re-expressed in
     *   that frame, so the arrangement the author sees is the arrangement the
     *   asset carries — for a same-parent selection (the ordinary case) that
     *   is its local transform composed with the parent chain, and for a
     *   selection spanning parents it is the only answer that is not scrambled.
     * - **Names.** Parent paths are by name, so two roots both called "Cube"
     *   would make one path mean two nodes. The second is renamed in the
     *   RECORDS only (never in the live scene) and its descendants' paths are
     *   rewritten to match.
     *
     * A root that is a descendant of another root in the list is dropped: it is
     * already travelling, and including it twice would export it twice.
     *
     * @internal
     */
    public _buildExportDataForSubtrees(
        roots: readonly SceneObject[],
        groupName?: string,
    ): { data: SceneObjectData[]; sceneObjects: (SceneObject | null)[] } {
        const tops = roots.filter((root, i) =>
            roots.indexOf(root) === i
            && !roots.some(other => other !== root && TraceScene._isDescendantOf(root, other)));
        if (tops.length === 0) {
            throw new Error('Nothing to export: the subtree list is empty.');
        }

        const data: SceneObjectData[] = [];
        const sceneObjects: (SceneObject | null)[] = [];

        if (groupName) {
            // The structural root SCENE_ROOT_SPEC §4 asks for: a name, an
            // identity transform, no src, no behaviors. Nothing of the scene's
            // own leaks into it — it exists so the asset has one handle.
            data.push({
                name: groupName,
                transform: { translation: [0, 0, 0], rotation: [0, 0, 0, 1], scale: [1, 1, 1] },
            });
            sceneObjects.push(null);
        }

        const sceneRoot = this.getSceneRoot();
        const taken = new Set<string>();

        for (const root of tops) {
            const built = this._buildExportDataForSubtree(root);
            const rootIndex = built.sceneObjects.indexOf(root);
            if (rootIndex === -1) continue;
            const rootRecord = built.data[rootIndex];
            const authoredName = rootRecord.name;
            const unique = Utility.generateUniqueName(authoredName, taken);
            taken.add(unique);

            for (let i = 0; i < built.data.length; i++) {
                const record = built.data[i];
                const parentPath = record.transform?.parent;
                if (parentPath !== undefined) {
                    // Every in-subtree path is `<authoredName>` or
                    // `<authoredName>/…` (the builder strips everything above
                    // the root), so re-prefixing is a slice, not a search.
                    const tail = parentPath.slice(authoredName.length);
                    record.transform!.parent = groupName ? `${groupName}/${unique}${tail}` : `${unique}${tail}`;
                } else if (i === rootIndex) {
                    if (groupName) {
                        record.transform = { ...(record.transform ?? {}), parent: groupName };
                    }
                    if (tops.length > 1 || groupName) {
                        // Re-express the root in the scene-root frame — see the
                        // doc comment. A single ungrouped root keeps its own
                        // authored transform, which is what `subtreeToFolder`
                        // has always written.
                        this._writeSceneRootFrameTransform(record, root, sceneRoot);
                    }
                }
            }

            rootRecord.name = unique;
            data.push(...built.data);
            sceneObjects.push(...built.sceneObjects);
        }

        return { data, sceneObjects };
    }

    /** Whether `candidate` sits anywhere under `ancestor`. */
    private static _isDescendantOf(candidate: SceneObject, ancestor: SceneObject): boolean {
        for (let p = candidate.getParent(); p; p = p.getParent()) {
            if (p === ancestor) return true;
        }
        return false;
    }

    /**
     * Rewrite `record.transform`'s TRS as `object`'s transform in the SCENE
     * ROOT's frame — the frame the synthesized group node sits at identity in.
     *
     * Composed from the AUTHORED records up the parent chain rather than read
     * off the live Babylon world matrix: a behavior may be driving the live
     * node (layout, a state, an animation), and what promotion captures is
     * what the scene would save, not a frame of a running animation.
     */
    private _writeSceneRootFrameTransform(
        record: SceneObjectData,
        object: SceneObject,
        sceneRoot: SceneObject | null,
    ): void {
        let accumulated = Matrix.Identity();
        for (let p = object.getParent(); p && p !== sceneRoot; p = p.getParent()) {
            accumulated = accumulated.multiply(TraceScene._authoredMatrix(p));
        }
        const composed = TraceScene._authoredMatrix(object).multiply(accumulated);

        const translation = Vector3.Zero();
        const rotation = Quaternion.Identity();
        const scale = Vector3.One();
        composed.decompose(scale, rotation, translation);

        record.transform = {
            ...(record.transform ?? {}),
            translation: [translation.x, translation.y, translation.z],
            rotation: [rotation.x, rotation.y, rotation.z, rotation.w],
            scale: [scale.x, scale.y, scale.z],
        };
    }

    /** One SceneObject's authored local TRS as a matrix. */
    private static _authoredMatrix(object: SceneObject): Matrix {
        object.refreshSceneData?.();
        const t = object.getSceneObjectData().transform;
        const translation = t?.translation ?? [0, 0, 0];
        const rotation = t?.rotation ?? [0, 0, 0, 1];
        const scale = t?.scale ?? [1, 1, 1];
        return Matrix.Compose(
            new Vector3(scale[0], scale[1], scale[2]),
            new Quaternion(rotation[0], rotation[1], rotation[2], rotation[3]),
            new Vector3(translation[0], translation[1], translation[2]),
        );
    }

    /**
     * Drop any SceneObject whose chain to the (already-walked) list passes
     * through a node with a scene-format `src` (.trace/.tracex/…). Such a
     * node is a reference to an external scene, and its loaded descendants
     * are derived — re-exporting them would duplicate the referenced content.
     *
     * Asset srcs (builtin primitives, models, images, videos) never
     * materialize child SceneObjects, so any children found under them are
     * authored and must survive export.
     *
     * Operates purely on the provided list (no scene walk), so it respects
     * whatever scope the caller chose: full-scene export passes scene-root
     * descendants; subtree export passes subtree-root descendants. Order is
     * preserved.
     */
    private static _filterDescendantsOfReferenceNodes(objects: SceneObject[]): SceneObject[] {
        const set = new Set(objects);
        const dropped = new Set<SceneObject>();
        const result: SceneObject[] = [];
        for (const so of objects) {
            let walk: SceneObject | null = so.getParent();
            let drop = false;
            while (walk && set.has(walk)) {
                if (dropped.has(walk) || walk.isSceneReference) {
                    drop = true;
                    break;
                }
                walk = walk.getParent();
            }
            if (drop) {
                dropped.add(so);
            } else {
                result.push(so);
            }
        }
        return result;
    }

    /**
     * Strip a root's name from the parent paths in export data, making its
     * children top-level: absolute engine-root paths ("Scene/Cube") become the
     * file-local paths a document is written from ("Cube").
     *
     * Used with the engine root's name to turn {@link _buildExportData}'s
     * absolute paths into document paths, and with the document root's name by
     * the `.zip` writer, whose flat list has no root record at all.
     */
    public static stripSceneRootPrefix(data: SceneObjectData[], prefix: string = SCENE_ROOT_NAME): void {
        for (const node of data) {
            if (node.transform?.parent === prefix) {
                delete node.transform.parent;
            } else if (node.transform?.parent?.startsWith(prefix + '/')) {
                node.transform.parent = node.transform.parent.slice(prefix.length + 1);
            }
        }
    }

    /**
     * Sort scene objects topologically so parents come before children.
     * Ensures that during import, parent paths exist before children reference them.
     */
    private topologicalSortForExport(objects: SceneObject[]): SceneObject[] {
        const result: SceneObject[] = [];
        const visited = new Set<SceneObject>();
        const objectSet = new Set(objects);

        const visit = (obj: SceneObject) => {
            if (visited.has(obj)) return;
            visited.add(obj);

            // Visit parent first (if it exists and is in our list)
            const parent = obj.getParent();
            if (parent && objectSet.has(parent)) {
                visit(parent);
            }

            result.push(obj);
        };

        for (const obj of objects) {
            visit(obj);
        }

        return result;
    }

    private setupLights(): void {
        // Key directional light (main light source)
        // Roughly match direction from Unity-equivalent Euler angles [50, -30, 0]
        const keyLight = new DirectionalLight('keyLight', new Vector3(-0.5, -0.766, -0.406), this.babylonScene);
        keyLight.diffuse = new Color3(1, .96, .84); // soft yellow matching app
        keyLight.intensity = 3.0;

        // Load environment texture for lighting and reflections
        // Note: We don't create a skybox to preserve the transparent/custom background color
        this._applyEnvironmentImage();
    }

    /**
     * (Re)create the environment texture from this viewer's config:
     * `environmentImage` when it is set, else the shipped
     * {@link DEFAULT_ENVIRONMENT_IMAGE_URL}. An empty url means no environment
     * map.
     */
    private _applyEnvironmentImage(): void {
        // `??`, not `||`: an empty string is a deliberate "no environment map".
        const envImageUrl = this.config.environmentImage ?? DEFAULT_ENVIRONMENT_IMAGE_URL;

        // clearScene() calls this again for the next document. The map it
        // already has is the same file, often still downloading: keep it.
        const current = this.babylonScene.environmentTexture;
        const keep = !!envImageUrl && current instanceof HDRCubeTexture && current.url === envImageUrl;
        if (current && !keep) {
            // Dispose previous environment texture if present (prevents GPU leak on scene reuse)
            this.disposeEnvironmentTexture(current);
        }

        if (envImageUrl) {
            if (!keep) {
                let texture!: HDRCubeTexture;
                texture = new HDRCubeTexture(
                    envImageUrl,
                    this.babylonScene,
                    512,
                    false,
                    true,
                    true,
                    false,
                    null,
                    () => {
                        if (this.babylonScene.environmentTexture === texture) {
                            this.disposeEnvironmentTexture(texture);
                        }
                    },
                );
                this.babylonScene.environmentTexture = texture;
            }
            this.babylonScene.iblIntensity = 0.5;
        }
    }

    /**
     * Dispose an environment map, including one still downloading.
     *
     * Babylon's raw cube loader (`createRawCubeTextureFromUrl`) returns early
     * when its texture was disposed before the file arrived, without taking it
     * out of the scene's pending data. That entry then never leaves, so
     * `scene.isReady()` stays false for the life of the scene and anything
     * that waits on it (`whenReadyAsync`, `GLTF2Export` without
     * `exportWithoutWaitingForScene`) never returns. Take it out here.
     */
    private disposeEnvironmentTexture(texture: BaseTexture): void {
        const internal = texture.getInternalTexture();
        if (internal && !internal.isReady) this.babylonScene.removePendingData(internal);
        texture.dispose();
        if (this.babylonScene.environmentTexture === texture) {
            this.babylonScene.environmentTexture = null;
        }
    }

    /**
     * Point this viewer's environment (IBL) map at `url`, e.g. at a
     * deployment's own storage, after the viewer has started. A viewer started
     * without the `environment-image` attribute has already requested the
     * shipped default by then, so a deployment that must never reach that
     * cloud sets the attribute instead.
     *
     * This writes the field the attribute sets (`ConfigData.environmentImage`).
     * The attribute is read once, when the viewer starts, so it is only the
     * initial value: a call here replaces it, a later call replaces an earlier
     * one, and changing the attribute after start does nothing.
     *
     * - `null` unsets it, which is the shipped default; `''` is no environment
     *   map at all.
     * - The url already in effect is a no-op. The map is kept, even one still
     *   downloading, and is not requested again. A map whose download failed
     *   was disposed, so the same url after a failure requests it again.
     * - It applies immediately and holds across `clearScene()`: it is this
     *   viewer's setting, not the document's.
     * - Per viewer, not per page: it writes this instance's config, so two
     *   `<trace-viewer>` elements do not share it.
     */
    public setEnvironmentImage(url: string | null): void {
        this.config.environmentImage = url ?? undefined;
        this._applyEnvironmentImage();
    }

    /**
     * Enable or disable the shadow-receiving ground plane at y=0.
     */
    public setGroundShadowEnabled(enabled: boolean): void {
        this._groundShadowEnabled = enabled;

        if (enabled) {
            this._ensureContactShadow();
        } else {
            this._contactShadow?.dispose();
            this._contactShadow = null;
        }
    }

    public get groundShadowEnabled(): boolean {
        return this._groundShadowEnabled;
    }

    /**
     * Update the shadow ground plane height (scene-local Y).
     */
    public setGroundHeight(y: number): void {
        this._contactShadow?.setGroundHeight(y);
    }

    /**
     * Set the shadow ground plane position directly (world-space). Used by XR
     * where the scene root stays at identity and positioning is handled via
     * reference space offsets rather than scene root transforms. The shadow
     * is always gravity-aligned (no rotation parameter — see ContactShadow).
     */
    public setShadowPosition(position: Vector3): void {
        this._contactShadow?.setPosition(position);
    }

    /**
     * Create the ContactShadow if enabled and not yet created.
     * Deferred from construction so the Babylon scene is fully ready.
     * Subscribes to the scene root's world matrix observable so the
     * shadow anchor stays in sync without per-frame polling.
     */
    private _ensureContactShadow(): void {
        if (!this._groundShadowEnabled || this._contactShadow) return;
        this._contactShadow = new ContactShadow(
            this.babylonScene,
            this.engine,
            this.config.groundHeight,
        );

        // When the scene root moves, sync the shadow anchor to match
        if (this._sceneRoot) {
            const shadow = this._contactShadow;
            this._sceneRoot.transform.onAfterWorldMatrixUpdateObservable.add(() => {
                shadow.syncToTransform(this._sceneRoot!.transform);
            });
        }
    }

    /**
     * Place the shadow ground plane at the bottom of visible content so
     * origin-centered models don't have the shadow cutting through their
     * middle. Computed once at scene-load time from world-space mesh AABBs;
     * not recomputed on subsequent transform changes.
     */
    private _alignShadowToContentBottom(): void {
        if (!this._contactShadow || !this._sceneRoot) return;

        let minY = Infinity;
        for (const child of this._sceneRoot.getChildren()) {
            for (const mesh of child.transform.getChildMeshes(false)) {
                if (mesh.name.startsWith('__shadow')) continue;
                if (mesh.name.endsWith(COLLIDER_MESH_SUFFIX)) continue;
                if (mesh.name.endsWith(SELECTION_MESH_SUFFIX)) continue;
                if (!mesh.isVisible || !mesh.isEnabled()) continue;
                mesh.computeWorldMatrix(true);
                const box = mesh.getBoundingInfo().boundingBox;
                // Scaled to zero (a hidden build step) it draws nothing; its point sits wherever the step is parked.
                if (box.minimumWorld.equals(box.maximumWorld)) continue;
                if (box.minimumWorld.y < minY) minY = box.minimumWorld.y;
            }
        }

        if (isFinite(minY)) {
            // 0.002 epsilon matches the default GROUND_Y to avoid z-fighting
            // with the asset's lowest face.
            this.setGroundHeight(minY - 0.002);
        }
    }

    // ============================================
    // CORE LOADING PRIMITIVES
    // ============================================

    /**
     * Give a freshly created object its node identity — the one place that
     * decides (issue #837, SCENE_ROOT_SPEC §2.3 "Identity").
     *
     * Three rules, and they are the whole policy:
     *
     * - **A legacy session has none.** `_uidMintingActive` is false, so any
     *   identity the data arrived with is dropped rather than carried into a
     *   document that will never write it.
     * - **A node that was READ keeps what the document said** (`'loaded'`):
     *   its `node.uid`, or the `name` 2.1's fallback rule elected. That is what
     *   makes a load → save round trip preserve identity byte for byte. A node
     *   the document said nothing about is minted one here, which is how a 2.1
     *   document written before identity existed gains it on its next save.
     * - **A node created from DATA mints** (`'created'`) whenever the identity
     *   in that data is still LIVE somewhere in this scene. That is exactly the
     *   difference between a duplicate and a restore: a duplicate is made from
     *   an object that is still there, so it must not claim the same identity,
     *   while an undo re-creating a deleted object (or a play-mode snapshot
     *   restoring one) finds the identity free and keeps it — an identity that
     *   changed every time a user undid a delete would be no identity at all.
     *
     * The scene root is deliberately not stamped: in 2.1 the scene root IS the
     * scene, not a node, and no `nodes[]` entry is ever written for it.
     */
    private _stampNodeUid(object: SceneObject, mode: 'loaded' | 'created'): void {
        if (!this._uidMintingActive) {
            object._setUidInternal(undefined);
            this._mintedHere.delete(object);
            return;
        }
        const existing = object.uid;
        const keep = existing !== undefined
            && isValidUid(existing)
            && (mode === 'loaded' || !this._uidIsLive(existing, object));
        object._setUidInternal(keep ? existing : mintUid());
        if (keep) this._mintedHere.delete(object); else this._mintedHere.add(object);
    }

    /**
     * Objects whose identity this SESSION minted, rather than read off a
     * document (issue #838).
     *
     * The two are indistinguishable once stamped — a uid is a uid — but they
     * mean opposite things to a copy's link back to its source. A uid the
     * referenced document declared (or the 2.1 name fallback elected from it,
     * which is just as reproducible) still names that node in the asset, so
     * `Provenance.placedUids` can point at it. A uid minted here for a node
     * whose document said nothing — every node of a legacy `.trace`, which has
     * no identity at all — exists only in this session's copy of the placement,
     * and would be a different uid the next time the same asset is placed.
     * Recording it would be a link that cannot be followed.
     *
     * Weak on purpose: membership is a fact about an object's current identity
     * and must not keep a deleted object alive.
     */
    private readonly _mintedHere = new WeakSet<SceneObject>();

    /**
     * Placements {@link placeCopy} is building right now. The pieces nested in
     * one load the 2.1 way whatever the session is: the copy's nodes carry the
     * asset's own `Model`, `Renderer` and sizing on each piece, so a piece must
     * own its file, not grow a child that holds the meshes.
     */
    private readonly _copiesBuilding = new WeakSet<SceneObject>();

    /** Whether `object` is, or is under, a placement {@link placeCopy} is building. */
    private _isInCopyBeingBuilt(object: SceneObject): boolean {
        for (let current: SceneObject | null = object; current; current = current.getParent()) {
            if (this._copiesBuilding.has(current)) return true;
        }
        return false;
    }

    /** Whether another live object in this scene already claims `uid`. */
    private _uidIsLive(uid: string, except: SceneObject): boolean {
        return this.sceneObjects.some(object => object !== except && object.uid === uid);
    }

    /**
     * Fresh identity for every node an unpack just promoted into THIS document
     * (issue #837; SCENE_ROOT_SPEC §2.3 "Copy keeps the link").
     *
     * The promoted nodes used to belong to the referenced document and kept
     * its uids; they are now this document's nodes, and two documents' worth of
     * identity cannot share one file. So each gets a new one — and the pairing
     * is returned rather than discarded, because the OLD uid is the link back
     * to the source document that `Provenance.placedUids` records. The caller
     * (`_dissolveReference`) writes that stamp from the returned pairs.
     *
     * @returns one entry per promoted node that has an identity now, in walk
     *   order: the object, its new uid, and the uid it had in the source
     *   document (absent when it had none).
     */
    private _remintPromotedUids(
        promoted: readonly SceneObject[],
    ): Array<{ object: SceneObject; uid: string; sourceUid?: string }> {
        if (!this._uidMintingActive) return [];
        const reminted: Array<{ object: SceneObject; uid: string; sourceUid?: string }> = [];
        for (const object of promoted) {
            // Only an identity the SOURCE DOCUMENT gave this node is a link
            // back to it; one this session minted on the way in names nothing
            // outside this session (see {@link _mintedHere}).
            const sourceUid = this._mintedHere.has(object) ? undefined : object.uid;
            const uid = mintUid();
            object._setUidInternal(uid);
            this._mintedHere.add(object);
            reminted.push({ object, uid, ...(sourceUid !== undefined ? { sourceUid } : {}) });
        }
        return reminted;
    }

    /**
     * The link a copy keeps back to the asset it was copied from — the
     * `Provenance.placedUids` map (issue #838; SCENE_ROOT_SPEC §2.3 "Copy keeps
     * the link").
     *
     * `placedFrom` names the asset; this names the nodes, pairing each promoted
     * node's new identity with the one it had in the source document. Built
     * from the re-mint's own output because that is the only moment both halves
     * exist at once: a step later, the old uids are gone.
     *
     * The source side is an ADDRESS, not always a bare uid. A node that lives
     * inside a reference nested in the asset was never the asset document's
     * own node, so it is addressed the way an override will address it — the
     * uid chain from the outermost placement down, `/`-joined, which is
     * `referenceIdentityPath` in `core/referenceIdentity.ts`. Spelled out again
     * here rather than called: that function reads LIVE uids off the graph, and
     * by this point the live uids are the new ones. The chain is also cut at
     * the promoted subtree — what encloses the copy in THIS scene is this
     * scene's business and no part of the asset.
     *
     * A node the source gave no identity is left out, and so is one whose chain
     * runs through such a node — an address with a hole in it is not an
     * address. Nothing left to say ⇒ `undefined`, never an empty map: a copy of
     * a legacy asset records `placedFrom` alone rather than claiming a link
     * that cannot be followed.
     */
    private _placedUidsFor(
        reminted: ReadonlyArray<{ object: SceneObject; uid: string; sourceUid?: string }>,
    ): Record<string, string> | undefined {
        if (reminted.length === 0) return undefined;
        const promoted = new Set(reminted.map(entry => entry.object));
        const sourceUids = new Map<SceneObject, string | undefined>(
            reminted.map(entry => [entry.object, entry.sourceUid]),
        );

        /** The source-side address of a promoted node, or '' when it has none. */
        const sourceAddress = (object: SceneObject): string => {
            const chain: SceneObject[] = [];
            let current: SceneObject | null = object;
            let outermostPlacement = -1;
            // Up to the edge of what was promoted: beyond it is this scene.
            while (current && promoted.has(current)) {
                chain.push(current);
                if (current.isSceneReference) outermostPlacement = chain.length - 1;
                current = current.getParent();
            }
            const fromPlacement = chain.slice(0, outermostPlacement >= 0 ? outermostPlacement + 1 : 1);
            const segments: string[] = [];
            for (let i = fromPlacement.length - 1; i >= 0; i--) {
                const uid = sourceUids.get(fromPlacement[i]);
                if (!uid) return '';
                segments.push(uid);
            }
            return segments.join('/');
        };

        const placedUids: Record<string, string> = {};
        for (const { object, uid } of reminted) {
            const source = sourceAddress(object);
            if (source) placedUids[uid] = source;
        }
        return Object.keys(placedUids).length > 0 ? placedUids : undefined;
    }

    /**
     * A scene still called "Scene" takes the name of the first object a load
     * that opens the scene brings, unless the session already has its document
     * root. An import, a dropped file or a generated part placed under that
     * root is not what the scene is called (see `establishesScene` in
     * {@link _loadFromBlob}).
     */
    private _nameSceneAfter(name: string | undefined, establishesScene: boolean): void {
        if (establishesScene && this.sceneName === "Scene" && name && !this.getSessionDocumentRoot()) {
            this.sceneName = name;
        }
    }

    /**
     * Load multiple scene objects with proper batching
     * @param sceneDataList - Array of scene data
     */
    private async loadSceneObjects(sceneDataList: SceneObjectData[], extraBehaviors?: BehaviorData[], mountParent?: SceneObject, establishesScene = false): Promise<SceneObject[]> {
        // Merge extra behaviors into the root object's data before construction
        if (extraBehaviors?.length && sceneDataList.length > 0) {
            const root = sceneDataList[0];
            root.behaviors = [...(root.behaviors ?? []), ...extraBehaviors];
        }

        // Pre-pass: resolve cross-object V1 Trigger→Action references on raw data
        // before constructing SceneObjects
        SceneCreation.migrateV1CrossObjectBehaviors(sceneDataList);

        // Phase 1: Create all objects
        // Names are NOT required to be globally unique - duplicates are allowed in different branches
        // Use resolveSceneObject() with full paths for unambiguous lookups
        const newSceneObjects: SceneObject[] = [];

        for (const sceneData of sceneDataList) {
            const sceneObject = SceneCreation.initializeSceneObject(
                sceneData,
                this.babylonScene,
                this.resources,
                this.config,
                this,
                this._eventSystem || undefined
            );

            // Node identity: what the data said, or a fresh one (issue #837).
            this._stampNodeUid(sceneObject, 'loaded');

            this._nameSceneAfter(sceneData.name, establishesScene);

            this.sceneObjects.push(sceneObject);
            newSceneObjects.push(sceneObject);
        }

        // Phase 2: Set up parent hierarchy BEFORE loading behaviors
        // This ensures transforms are applied with correct parent context
        // Objects without explicit parents are parented to the scene root
        for (const sceneObject of newSceneObjects) {
            const parentPath = sceneObject._getParentPath();
            if (parentPath) {
                // Find parent - supports both FQTP and legacy bare name references
                const parent = this.resolveSceneObject(parentPath);
                if (parent) sceneObject.setParent(parent);
            } else {
                // No explicit parent: the caller's mount point, else the
                // engine root (the mount point of last resort).
                const target = mountParent ?? this._sceneRoot;
                if (target) sceneObject.setParent(target);
            }
        }

        // Phase 3: Apply all behaviors (asset behaviors load before dependents per-object)
        // Bracketed with Phase 4 so a deferred start event can't fire in the
        // middle of a load — see {@link _deferStartEvent}.
        this._beginConstruction();
        try {
            await Promise.all(
                newSceneObjects.map(so => SceneCreation.applyComponents(so, so.name))
            );

            // Phase 4: Attach behaviors (hierarchy already established in Phase 2)
            for (const sceneObject of newSceneObjects) {
                sceneObject._attachBehaviors();
            }
        } finally {
            this._endConstruction();
        }

        // The Phase-2 setParent calls fired topology signals BEFORE behaviors
        // existed — re-emit now that they do, so topology subscribers (the
        // reference-identity registry) can see the loaded graphs. Mirrors the
        // create-completion emit in createSceneObject/createSceneObjectFromData.
        this._emitTopologyChanged();

        return newSceneObjects;
    }

    /**
     * Rewrite subasset references after the ResourceManager re-homed a nested
     * scene's assets under a scene-scoped folder. `remap` maps original asset key
     * (e.g. `assets/panel.glb`) to its scoped key (`assets/F40Slide1/panel.glb`).
     *
     * Three surfaces reference those keys and must all be rewritten:
     *   - `node.src` — the object's own asset (Model/Image/Video). Carries a `./`
     *     prefix, preserved by remapAssetRef.
     *   - `node.behaviors` — asset paths embedded in behavior data (Renderer
     *     material overrides, Frame/ImageAnchor/Animation src, …), resolved
     *     straight off authored data at load time. Walked recursively.
     *   - `result.scripts` — script `@type` → asset-path map.
     *
     * A style sheet names its files in its own bytes; `remapSheetAssetRefs`
     * rewrites those after this runs.
     */
    private _applyAssetKeyRemap(result: LoadedSceneResult, remap: Map<string, string>): void {
        for (const node of result.nodes) {
            node.src = remapAssetRef(node.src, remap);
            remapBehaviorAssetRefs(node.behaviors, remap);
        }

        if (result.scripts) {
            for (const [typeName, assetPath] of Object.entries(result.scripts)) {
                // assetPath is always a defined string here, so remapAssetRef
                // never returns undefined — the non-null assertion is safe.
                result.scripts[typeName] = remapAssetRef(assetPath, remap)!;
            }
        }
    }

    /**
     * Load scene objects from a plugin's LoadedSceneResult.
     *
     * This is the new loading path where plugins have already:
     * - Parsed the scene format
     * - Loaded GLB assets into the Babylon scene
     * - Created TransformNodes with proper hierarchy
     *
     * We just need to wrap them in SceneObjects and attach behaviors.
     *
     * @param result - The LoadedSceneResult from a plugin's loadWithResult()
     * @param progressTracker - Optional progress tracker for UI updates
     */
    private async loadFromPluginResult(
        result: LoadedSceneResult,
        progressTracker?: IProgressTracker,
        parent?: SceneObject,
        extraBehaviors?: BehaviorData[],
        uprightImport = false,
        establishesScene = false,
    ): Promise<SceneObject[]> {
        // The whole load — including the nested scenes it recurses into — is
        // one construction, so no graph's start event fires against the
        // half-built result. See {@link _deferStartEvent}; startScene() drains
        // the queue once the content is in.
        this._beginConstruction();
        try {
            return await this._loadFromPluginResultInner(result, progressTracker, parent, extraBehaviors, uprightImport, establishesScene);
        } finally {
            this._endConstruction();
        }
    }

    private async _loadFromPluginResultInner(
        result: LoadedSceneResult,
        progressTracker?: IProgressTracker,
        parent?: SceneObject,
        extraBehaviors?: BehaviorData[],
        uprightImport = false,
        establishesScene = false,
    ): Promise<SceneObject[]> {
        // External behaviors replace in-file behaviors on the ROOT ENTITY
        // (SCENE_ROOT_SPEC §5.1–5.2): the document root of a document, the
        // single node of a single asset. Replaced here, before any behavior is
        // instantiated, so the replace is a replace and not two live sets.
        // Content with no single root (a legacy flat `.zip`) has no root
        // entity — its caller applies them to the mount point instead.
        const rootTarget = result.documentRoot
            ?? (result.rootNodes?.length === 1 ? result.rootNodes[0] : undefined);
        if (extraBehaviors?.length && rootTarget) {
            rootTarget.behaviors = extraBehaviors;
            Logger.log(`[TraceScene] loadFromPluginResult: external behaviors replaced on ${rootTarget.name}: ${extraBehaviors.map(b => b['@type']).join(', ')}`);
        }

        // Unity-era constraints compile here, BEFORE any SceneObject exists.
        // The compile reads world matrices off the hierarchy the plugin just
        // built, and its whole point is that no behavior ever sees the legacy
        // data — a legacy `Follow` in particular must never reach a
        // constructor (see LegacyConstraints.ts).
        migrateLegacyConstraints(result.nodes as LegacyConstraintNode[]);

        // Names are unique among siblings, not across the scene. An editing
        // session dedupes them below, once the hierarchy stands; a playing
        // scene and a reference's content keep the file's names.
        const newSceneObjects: SceneObject[] = [];

        for (let i = 0; i < result.nodes.length; i++) {
            const loadedNode = result.nodes[i];

            // Create SceneObject from pre-loaded transform
            // src path (e.g., "./assets/model.glb") is used by behaviors to load assets from ResourceManager
            //
            // …and an AUTHORING load opens the rig rather than the bake. This
            // is the convergent point for every scene format — .trace, .zip,
            // .tracex and .glxf all arrive here — which is why the restore
            // lives on it rather than beside the one in `initializeSceneObject`
            // (that path handles the scene root, single assets and
            // create/duplicate, and no scene format takes it).
            const src = authoringSrc(loadedNode.src, loadedNode.behaviors, this, loadedNode.name, loadedNode.uid);
            const sceneObject = SceneObject.fromLoadedTransform(
                loadedNode.transform,
                loadedNode.name,
                this.babylonScene,
                this.resources,
                this.config,
                this,
                {
                    behaviors: loadedNode.behaviors as BehaviorData[] | undefined,
                    active: loadedNode.active,
                    src,
                    resetAssetTransform: loadedNode.resetAssetTransform,
                    sceneRef: loadedNode.sceneRef,
                    // Informational, from `files[k].name` (spec §2.1): the
                    // human name of an otherwise hash-named file, for an
                    // editor to label the node with.
                    srcName: loadedNode.srcName,
                    // The plugin may have instantiated this node's file
                    // already; if so nothing here may load it a second time.
                    assetInstantiated: loadedNode.assetInstantiated,
                    // Whatever the document node carried that Trace did not
                    // author, so the next save writes it back (issue #834).
                    foreign: loadedNode.foreign,
                    // …and what it said about identity (issue #837).
                    uid: loadedNode.uid,
                    // TRACE_animation_binding: bound once the instance is mounted
                    // (SceneCreation.applyComponents).
                    animationBinding: loadedNode.animationBinding,
                },
                this._eventSystem || undefined
            );

            // Keep the document's identity, or mint one for a node that had
            // none (issue #837).
            this._stampNodeUid(sceneObject, 'loaded');

            this._nameSceneAfter(loadedNode.name, establishesScene);

            // A folder document names its own files with relative URIs
            // (spec §2.3). `src` keeps the authored relative form so a re-save
            // is a document write and a folder copy needs no rewrite; the
            // absolute URL it resolves to is registered here so asset-owning
            // behaviors can fetch the bytes. A rig swapped in for a bake keeps its own.
            if (loadedNode.srcUrl && src && src === loadedNode.src) {
                this.resources.registerAssetUrl(src, loadedNode.srcUrl);
            }
            // …and the file's original human name beside it (spec §2.1), so a
            // load → save round trip puts `files[k].name` back rather than
            // silently dropping it. Registered independently of `srcUrl`: a
            // bundle's files come out of the archive, not off the network.
            if (loadedNode.srcName && src) {
                this.resources.registerAssetName(src, loadedNode.srcName);
            }
            // A binding source is a folder file like a node's own `src`: its
            // bytes are fetched from where the document names them, and a save
            // back into the folder names it rather than re-uploading it.
            for (const [sourceSrc, url] of loadedNode.animationSourceUrls ?? []) {
                this.resources.registerAssetUrl(sourceSrc, url);
            }
            // A file the loader instantiated brings its clips with it. They
            // become the object's clips exactly as a Model-loaded file's do,
            // before any behavior attaches — so an `Animation` behavior finds
            // them by name, and a node without one loops its first clip in
            // play mode (`_updateUnmanagedAnimations`). Without this an
            // inline-loaded file's clips reached no behavior at all.
            if (loadedNode.assetInstantiated && loadedNode.animationGroups?.length) {
                sceneObject.setAnimationClips(loadedNode.animationGroups);
            }

            this.sceneObjects.push(sceneObject);
            newSceneObjects.push(sceneObject);
        }

        // Parent top-level objects (no parent within the loaded scene)
        // to the explicit parent if provided, otherwise to the scene root.
        //
        // `uprightImport` (declared by the entry point, only `loadAsset`):
        // when a user imports into a scene whose root is tilted (image
        // anchors tilt it), counter-rotate the new content so it appears
        // upright instead of tilted. Only a direct user import wants this —
        // this method also runs when opening a saved scene, and runs again
        // (recursively) for every node whose `src` references another scene
        // file, to rebuild that content beneath it. On those calls the
        // parent's rotation is something the user already chose, and
        // "correcting" it would silently undo their edit.
        const parentTarget = parent ?? this._sceneRoot;
        if (parentTarget) {
            for (const sceneObject of newSceneObjects) {
                if (!sceneObject.transform.parent) {
                    sceneObject.setParent(parentTarget);
                    if (uprightImport) {
                        this._preserveAuthoredWorldRotation(sceneObject, parentTarget);
                    }
                }
            }
        }
        if (!this._playMode) this._dedupeSiblingNames(newSceneObjects);

        // The load window opens HERE, at parenting, not at `applyComponents`
        // below: between the two there is a nested-asset download to await,
        // and an import lands these objects in a scene that is already live.
        // Anything reading them inside that gap would see objects that declare
        // nothing yet (SceneObject.isLoadingBehaviors).
        // Closed in the `finally` beside the loads themselves.
        for (const sceneObject of newSceneObjects) sceneObject._beginBehaviorLoad();
        try {

        // Count nested scene assets that need downloading (these are the heavy
        // items). A library reference (spec §3.4) is one of these even though
        // its URL has no extension — see SceneObject.isSceneReference.
        const nestedSceneObjects = newSceneObjects.filter(so => so.isSceneReference);

        // Step budget: every object gets 1 step from applyComponents (the Promise.all below).
        // Nested scenes get 2 extra steps: download + build — so 3 total per nested object.
        const NESTED_EXTRA_STEPS = 2; // 1 download + 1 build, equally weighted
        const nestedWeight = nestedSceneObjects.length * NESTED_EXTRA_STEPS;
        const totalSteps = nestedWeight + newSceneObjects.length;
        progressTracker?.onStart(totalSteps);
        let completedSteps = 0;

        // Load nested scene assets (.trace, .tracex, etc.) referenced by src URL
        // These are often the heaviest downloads (e.g. 40MB nested .trace files).
        // Report byte-level progress within each nested download + building step.
        for (const sceneObject of nestedSceneObjects) {
            try {
                Logger.log(`[TraceScene] Loading nested scene asset: ${sceneObject.src} for ${sceneObject.name}`);

                // Create a sub-progress-tracker for the nested scene's building phase.
                // Download occupies the first step, building occupies the second.
                // Note: onComplete is intentionally a no-op here — completion is tracked
                // by the outer completedSteps counter, not by the nested tracker.
                const nestedBuildTracker: IProgressTracker | undefined = progressTracker
                    ? {
                        onStart: (_total: number) => {
                            // Nested building starts after download step
                        },
                        onProgress: (loaded: number, total: number) => {
                            const buildFraction = total > 0 ? loaded / total : 0;
                            // Past download (step 1), fractionally through build (step 2)
                            const overall = completedSteps + 1 + buildFraction;
                            progressTracker.onProgress(overall, totalSteps);
                        },
                        onComplete: () => { /* handled by outer completedSteps increment */ },
                    }
                    : undefined;

                await this._loadFromURL(
                    sceneObject.src,
                    undefined,
                    sceneObject,
                    progressTracker
                        ? (loaded, total) => {
                            // Download progress within the first step; loaded can
                            // overshoot total (compressed transfers) — clamp so we
                            // don't bleed into the next step's range.
                            const stepProgress = total > 0 ? Math.min(1, loaded / total) : 0;
                            progressTracker.onProgress(completedSteps + stepProgress, totalSteps);
                        }
                        : undefined,
                    nestedBuildTracker
                );
            } catch (error) {
                Logger.error(`[TraceScene] Failed to load nested scene asset ${sceneObject.src} for ${sceneObject.name}:`, error);
            }
            completedSteps += NESTED_EXTRA_STEPS;
            progressTracker?.onProgress(completedSteps, totalSteps);
        }

        // Pre-pass: resolve cross-object V1 Trigger→Action references (e.g. vessel.trace)
        SceneCreation.migrateV1CrossObjectBehaviorsOnObjects(newSceneObjects);

        // Apply all behaviors (Model, Image, Video, Animation, Frame, Renderer, etc.)
        // Behaviors download referenced assets (models, videos, images) during load().
        // Note: completion order is non-deterministic (unlike the sequential nested-scene
        // loop above), but completedSteps++ is safe since JS is single-threaded.
        await Promise.all(
            newSceneObjects.map(so =>
                SceneCreation.applyComponents(so, so.name).then(() => {
                    completedSteps++;
                    progressTracker?.onProgress(completedSteps, totalSteps);
                })
            )
        );

        // Attach behaviors to Babylon's behavior system
        for (const sceneObject of newSceneObjects) {
            sceneObject._attachBehaviors();
        }
        } finally {
            // Counted, so this closes the window opened at parenting and
            // leaves the per-object one `applyComponents` already closed.
            for (const sceneObject of newSceneObjects) sceneObject._endBehaviorLoad();
        }

        // Hierarchy-setup setParent calls fired topology signals BEFORE
        // behaviors existed — re-emit now that they do (see loadSceneObjects).
        this._emitTopologyChanged();

        progressTracker?.onComplete();

        return newSceneObjects;
    }

    /**
     * Put an imported file's bytes in the ResourceManager and record everything
     * that later has to find them again: the deduplicated asset key, the human
     * name, the URL it was fetched from and the folder that URL sits in.
     *
     * Shared by the three owners of an imported file — {@link loadSingle3DAsset},
     * which gives a model its own object, {@link _fillReferenceWithFile}, which
     * gives it to the placement that referenced it (SCENE_ROOT_SPEC §2.3 row 1),
     * and the image/video path of `_loadFromBlob` — so they cannot register
     * different things about the same bytes. Identical bytes imported again
     * share one key, and the later import's name and URLs win, as they always
     * have for models.
     *
     * @returns the asset key the bytes ended up under (deduplication may hand
     *   back a key an earlier import already claimed).
     */
    private async _registerImportedFile(
        fileName: string,
        blob: Blob,
        sourceUrl?: string,
        assetBaseUrl?: string,
    ): Promise<string> {
        // Store the blob in ResourceManager with deduplication
        // This enables export to include the asset and Model to load it. The
        // key keeps the RESOLVED name, which carries the kind (§3.4), so the
        // model loader reading the key back picks the same plugin.
        const actualPath = await this.resources.addAssetWithDedup(`assets/${fileName}`, blob);
        // What the user dropped (spec §2.1). Recorded here because it is the
        // one moment the original name exists: a host that uploads these bytes
        // to `POST /files` re-`src`s the node to the name that answers with, and
        // without this the name is gone by the first save.
        this.resources.registerAssetName(actualPath, fileName);
        if (sourceUrl) {
            this.resources.registerUrlMapping(sourceUrl, actualPath);
        }
        if (assetBaseUrl) {
            // Lets a text .gltf resolve its .bin/texture refs against where it
            // was fetched from, even though it loads from the local blob.
            this.resources.registerAssetBaseUrl(actualPath, assetBaseUrl);
            // And the FILE, not just its directory: a save back into this same
            // folder names the file rather than re-uploading bytes the folder
            // already holds (spec §2.2, #830). `assetBaseUrl` is the directory
            // of the RESOLVED response URL and `fileName` its last segment, so
            // this reconstructs what was fetched — including through the
            // redirect a library reference takes.
            this.resources.registerAssetSourceUrl(actualPath, `${assetBaseUrl}${fileName}`);
        }
        return actualPath;
    }

    /**
     * A reference that resolved to a FILE — a model, image, video or audio
     * clip: the placement owns it (SCENE_ROOT_SPEC §2.3, row 1 of the rule
     * table) — no child object.
     *
     * A second SceneObject would put the file BESIDE the node that referenced
     * it, where a scene-side `Renderer` or `MorphStretch` on the placement can
     * never reach it (`getOwnedMeshes` is own-meshes-only, by design), and
     * where an `Image` the document already authored would render it twice.
     * Import already built one node for a file; this is reopen agreeing with it.
     *
     * Writing behavior *data* rather than attaching a live behavior is what
     * makes this safe, and only because of where we are in the load:
     * `loadFromPluginResult` constructs and parents every SceneObject, then
     * walks the nested references — this call — and only afterwards turns
     * authored data into live behaviors, so the entry added here is
     * instantiated with the rest of them.
     *
     * `_standInFileSrc` records the file the placement now names in its own
     * right. Two things read it: `Model.load`, which otherwise refuses to load
     * a scene reference (a reference that resolved to a DOCUMENT must never be
     * handed to the model loader), and unpack, which copies the bytes into this
     * scene's folder when the reference is dissolved.
     */
    private async _fillReferenceWithFile(
        placement: SceneObject,
        fileName: string,
        fileExtension: string,
        blob: Blob,
        sourceUrl?: string,
        assetBaseUrl?: string,
    ): Promise<SceneObject> {
        const actualPath = await this._registerImportedFile(fileName, blob, sourceUrl, assetBaseUrl);
        // Where the bytes live in the REFERENCED folder, so unpack can bring
        // them across ("Copy to scene", spec §2.6). The blob is already here;
        // this is the address, not a second download.
        if (assetBaseUrl) this.resources.registerAssetUrl(actualPath, `${assetBaseUrl}${fileName}`);
        placement._standInFileSrc = `./${actualPath}`;

        // The resolved FILE decides the behavior, not the reference URL:
        // `<api>/assets/{guid}/file` carries no extension to read (spec §3.4).
        ensureBehaviorDataForExtension(fileExtension, placement);
        Logger.log(`[TraceScene] Reference ${placement.name} resolved to a file (${fileName}) — the placement owns it`);
        return placement;
    }

    /**
     * Load a single 3D asset (GLB, SPZ, PLY, SPLAT) directly using ImportMeshAsync.
     * Creates a LoadedSceneResult and uses loadFromPluginResult for consistent handling.
     * Stores the blob in ResourceManager with deduplication for export support.
     *
     * @param blobUrl - The blob URL of the asset
     * @param name - The name for the scene object
     * @param fileName - The original filename (for asset storage)
     * @param blob - The asset blob (for storage and deduplication)
     * @param fileExtension - The RESOLVED extension (glb, gltf, spz, ply, splat) — from the fetched file's name
     * @param progressTracker - Optional progress tracker
     * @param sourceUrl - Optional original HTTP URL to preserve as `src`
     */
    private async loadSingle3DAsset(
        _blobUrl: string,
        name: string,
        fileName: string,
        blob: Blob,
        fileExtension: string,
        progressTracker?: IProgressTracker,
        sourceUrl?: string,
        extraBehaviors?: BehaviorData[],
        assetBaseUrl?: string,
        uprightImport = false,
        parent?: SceneObject,
        establishesScene = false,
    ): Promise<SceneObject | null> {
        progressTracker?.onStart(1);

        const actualPath = await this._registerImportedFile(fileName, blob, sourceUrl, assetBaseUrl);

        // Use original HTTP URL as src when available, so export can choose to
        // pack or keep as external reference. The blob is still stored locally
        // for cached loading via the URL→asset-key mapping.
        const src = sourceUrl ?? `./${actualPath}`;

        // Create a transform node for the scene object
        const transform = new TransformNode(name, this.babylonScene);

        // Build LoadedSceneResult - Model behavior will handle actual mesh loading
        const loadedNode: LoadedSceneResult['nodes'][0] = {
            transform,
            name,
            src,
            active: true,
        };
        const loadedResult: LoadedSceneResult = {
            nodes: [loadedNode],
            rootNodes: [loadedNode],
            nodeMap: new Map([[name, loadedNode]]),
        };

        // What the node IS comes from the file the fetch RESOLVED to, not from
        // `src`. A library reference is `<api>/assets/{guid}/file`: the "src
        // as template" pass in `applyComponents` reads no extension off it, so
        // the placement got no Model, rendered nothing, and saved that way.
        // Merged into the caller's behaviors rather than seeded on the node,
        // because `loadFromPluginResult` REPLACES the node's behaviors with
        // those; and by the rule `applyComponents` uses (an authored Model
        // wins), so a `src` that names its format ends up with the same data.
        const behaviors = withImpliedBehaviorData(
            extraBehaviors,
            impliedBehaviorDataForExtension(fileExtension),
        );

        // Use standard plugin result handling - Model loads the mesh
        Logger.log(`[TraceScene] loadSingle3DAsset: ${name} passing ${behaviors?.length ?? 0} behaviors to loadFromPluginResult`);
        const sceneObjects = await this.loadFromPluginResult(loadedResult, progressTracker, parent, behaviors, uprightImport, establishesScene);

        Logger.log(`[TraceScene] Loaded single ${fileExtension} asset: ${name} (src: ${src})`);
        return sceneObjects[0] ?? null;
    }

    // ============================================
    // ASSET FETCHING
    // ============================================

    /**
     * Fetch asset from URL and return blob with metadata
     */
    private async fetchAsset(url: string, fetchOptions?: AssetFetchOptions): Promise<{blob: Blob, fileName: string, extension: string, assetBaseUrl: string}> {
        const { blob, fileName, assetBaseUrl } = await this.fetchAssetWithProgress(url, undefined, fetchOptions);
        const extension = Utility.getFileExtensionFromSrc(fileName);
        return { blob, fileName, extension, assetBaseUrl };
    }


    // ============================================
    // PUBLIC API
    // ============================================

    /**
     * Load a DOCUMENT from `url` and mount its **root** under `parent` — the
     * editor-content path (CONTENT_MODEL_SPEC §2.9, SCENE_ROOT_SPEC §5.4).
     *
     * This is the nested path — the one a reopen takes — reached by supplying a
     * `parent`, and that is what makes the result **ordinary content of this
     * session** rather than a placement (§2.3): no `src` is stamped with the
     * URL, `isSceneReference` stays false, `canParentUnder` allows children
     * under the mounted root, and `referenceAncestor()` is null inside it.
     * Nothing is lifted and nothing is deleted — the document's root IS the
     * subtree the caller asked for, and it comes back as the return value.
     *
     * Loading BY URL (rather than from a blob) is what makes the document's
     * relative `assets/…` URIs resolve against its own folder and register
     * there, so a later save of that subtree back into the same folder names
     * the files it already holds instead of re-uploading them
     * (CONTENT_MODEL_SPEC §2.2, {@link SceneExporter.subtreeToFolder} with
     * `destination: 'same'`).
     *
     * The keys those files register under are the document's own relative URIs,
     * unscoped — this content belongs to the session, it is not a nested
     * reference. Content addressing is what makes that safe across two
     * documents in one session: a name shared by both folders is a name derived
     * from the same bytes.
     *
     * @param url - the document's address.
     * @param parent - where to mount the document root. Defaults to the
     *   session's document root, then the engine root. Never left unset
     *   internally: without a parent this would be a top-level IMPORT, which is
     *   a placement.
     * @param name - a name for the mounted content. Inert on this path by
     *   design: what the user sees is the document root's own authored name.
     * @returns the document root, or the first loaded object for a document
     *   with no single root.
     */
    public async attachDocumentFromUrl(
        url: string,
        parent?: SceneObject,
        name?: string,
    ): Promise<SceneObject | null> {
        const mountParent = this._documentMountParent(parent, 'attachDocumentFromUrl');
        const { blob, fileName, assetBaseUrl } = await this.fetchAsset(url);
        const sourceUrl = url.startsWith('http') ? url : undefined;
        const { sceneObject, documentRoot } = await this._loadFromBlob(
            blob,
            fileName,
            name,
            undefined,
            mountParent,
            sourceUrl,
            undefined,
            assetBaseUrl,
            false,
            // NOT scoped: an editor document's files keep the keys the document
            // names them by, so the round trip back into its own folder is a
            // document write and nothing else (spec §2.1, §2.9).
            false,
        );
        return documentRoot ?? sceneObject;
    }

    /**
     * {@link attachDocumentFromUrl} for a document with no URL: a bundle held
     * in memory, such as the editor half of a packed `.glz`
     * (`readEditorBundle`, spec §4). The root mounts under `parent` the same
     * way, as ordinary content of this session.
     *
     * Its files register under keys scoped to this load, because a bundle's
     * entry names are human: `assets/photo.png` here and in the session's own
     * folder may be different bytes. There is no folder to round-trip into,
     * so the next folder save uploads them like any other new file.
     *
     * @param blob - the document's bytes, in a format `fileName` names.
     * @param fileName - picks the reader, e.g. `editor.glz`.
     * @param parent - as for {@link attachDocumentFromUrl}.
     */
    public async attachDocumentFromBlob(
        blob: Blob,
        fileName: string,
        parent?: SceneObject,
    ): Promise<SceneObject | null> {
        const mountParent = this._documentMountParent(parent, 'attachDocumentFromBlob');
        const { sceneObject, documentRoot } = await this._loadFromBlob(
            blob,
            fileName,
            /* name */ undefined,
            /* progressTracker */ undefined,
            mountParent,
            /* sourceUrl */ undefined,
            /* extraBehaviors */ undefined,
            /* assetBaseUrl */ undefined,
            /* openAsScene */ false,
            /* scopeAssets */ true,
        );
        return documentRoot ?? sceneObject;
    }

    /** Where an attached document mounts: never unset, or it would be an import. */
    private _documentMountParent(parent: SceneObject | undefined, caller: string): SceneObject {
        this.initializeSceneRoot();
        const mountParent = parent ?? this._importMountParent() ?? this._sceneRoot ?? undefined;
        if (!mountParent) {
            throw new Error(`${caller}: the scene has no root to mount the document under`);
        }
        return mountParent;
    }

    /**
     * Load an asset from a URL (GLB, JPG, PNG, MP4, .trace, .real)
     * @param url - URL to the asset
     * @param name - Optional custom name for the scene object (defaults to filename)
     * @param behaviors - Optional behaviors to include during construction
     * @param options - Optional import options (see {@link ImportOptions})
     * @returns The first loaded SceneObject, or null if nothing was loaded
     */
    public async loadAsset(url: string, name?: string, behaviors?: BehaviorData[], options?: ImportOptions): Promise<SceneObject | null>;

    /**
     * Load an asset from a Blob (GLB, JPG, PNG, MP4, .trace, .real)
     * @param blob - The asset blob
     * @param fileName - The file name with extension (e.g., "model.glb") - determines asset type
     * @param name - Optional custom name for the scene object (defaults to fileName without extension)
     * @param behaviors - Optional behaviors to include during construction
     * @param options - Optional import options (see {@link ImportOptions})
     * @returns The first loaded SceneObject, or null if nothing was loaded
     */
    public async loadAsset(blob: Blob, fileName: string, name?: string, behaviors?: BehaviorData[], options?: ImportOptions): Promise<SceneObject | null>;

    // Implementation dispatcher. The optional tail is read by shape
    // ({@link readImportArgs}), never by position.
    public async loadAsset(
        urlOrBlob: string | Blob,
        fileNameOrObjectName?: string,
        ...rest: ImportArg[]
    ): Promise<SceneObject | null> {
        if (typeof urlOrBlob === 'string') {
            // URL overload: loadAsset(url, name?, behaviors?, options?)
            const objectName = fileNameOrObjectName;
            const { behaviors, options } = readImportArgs(rest);
            // `assetBaseUrl` is the RESOLVED response URL's directory (after
            // any redirect) and it is not optional here: a library reference
            // (spec §3.4) 302s to a folder document whose `files[].uri` are
            // relative to the folder it landed in. Dropping the base left
            // `assets/<hash>.glb` resolving against nothing.
            const { blob, fileName, assetBaseUrl } = await this.fetchAsset(urlOrBlob);
            const sourceUrl = urlOrBlob.startsWith('http') ? urlOrBlob : undefined;
            // Public import into an existing scene is additive → scope subassets
            // (of a .trace); no-op for single-asset imports (GLB/image/video).
            const { sceneObject, wrapsFile } = await this._loadFromBlob(blob, fileName, objectName, undefined, undefined, sourceUrl, behaviors, assetBaseUrl, false, true, true);
            return this._unpackImportedScene(sceneObject, fileName, objectName, behaviors, options, wrapsFile);
        } else {
            // Blob overload: loadAsset(blob, fileName, name?, behaviors?, options?)
            const fileName = fileNameOrObjectName;
            if (!fileName) {
                throw new Error('fileName is required when loading from Blob');
            }
            const { name, behaviors, options } = readImportArgs(rest);
            const { sceneObject, wrapsFile } = await this._loadFromBlob(urlOrBlob, fileName, name, undefined, undefined, undefined, behaviors, undefined, false, true, true);
            return this._unpackImportedScene(sceneObject, fileName, name, behaviors, options, wrapsFile);
        }
    }

    /**
     * Apply the `unpack` import option to a freshly imported scene-format file.
     *
     * Decides only whether there is anything to unpack; the dissolve itself is
     * {@link unpackReference}, which a host also calls directly on a reference
     * that has been sitting in the scene for a week.
     *
     * @param wrapsFile - Whether `wrapper` really is the file's stand-in (see
     *   {@link _loadFromBlob}). False for a file with several root objects:
     *   there is no stand-in to remove — the objects came in as the scene's
     *   own already, and `wrapper` is just the first of them, so unpacking it
     *   would promote its child over a real authored object and delete it.
     */
    private async _unpackImportedScene(
        wrapper: SceneObject | null,
        fileName: string,
        name?: string,
        extraBehaviors?: BehaviorData[],
        options?: ImportOptions,
        wrapsFile?: boolean,
    ): Promise<SceneObject | null> {
        if (!wrapper || !options?.unpack) return wrapper;
        // What was placed has to BE a reference for there to be anything to
        // unpack. The filename answers for an archive (`.trace`/`.tracex`/…);
        // a **library reference** has no extension at all — its URL is
        // `<api>/assets/{guid}/file` (spec §3.4) — so the wrapper's own
        // identity answers for that: the loader's manifest classification
        // (`isSceneReference`) or the asset-file-URL shape (`contentRef`).
        if (!isSceneFormatRef(fileName) && !wrapper.isSceneReference && !wrapper.contentRef) return wrapper;
        // Nothing stood in for the file — nothing to unpack.
        if (!wrapsFile) return wrapper;

        // Gated above, not by `unpackReference`: a `.trace` imported from a
        // BLOB has no `src` at all, so nothing on the object says it stands in
        // for a file — only this call site knows (`fileName`, `wrapsFile`).
        const result = await this._dissolveReference(wrapper, {
            ...(name !== undefined ? { name } : {}),
            ...(extraBehaviors ? { behaviors: extraBehaviors } : {}),
            ...(options.intoFolder ? { intoFolder: options.intoFolder } : {}),
        });
        return result.object;
    }

    /**
     * Dissolve a placed reference **in place**, so its content becomes this
     * scene's own (issue #730, spec §2.6 / §3.4).
     *
     * A reference is one node standing in for a file — a `.trace`/`.tracex`
     * archive, or a library asset whose `src` is `<api>/assets/{guid}/file`.
     * Unpacking removes the stand-in and keeps the content:
     *
     * - **The file held one object.** That object takes the stand-in's place:
     *   same parent, same world transform, the caller's behaviors moved onto
     *   it. Selection identity necessarily moves to it — there is no longer a
     *   node for the reference.
     * - **The file held several, or none.** The stand-in stays, minus its file
     *   reference, as an ordinary group. Parent, local transform and object
     *   identity are all unchanged, so a host's selection survives untouched.
     *
     * Either way `Provenance.placedFrom` is stamped with the URL the reference
     * carried — the only record left that this content came from somewhere,
     * and the reason the stamp happens on unpack and nowhere else (spec §3.4).
     *
     * ### The bytes
     *
     * Content that lives in the *referenced* folder is not this scene's to
     * save: `assets/<hash>.glb` is relative to the asset's folder, and naming
     * it absolutely is what the manifest rules refuse (§2.5). This is most
     * visible for a §3.1 leaf wrapper — one node whose whole content is a GLB
     * in the asset's folder — where dropping the reference used to leave the
     * meshes on screen and the next save writing an empty group.
     *
     * Pass `intoFolder` and each such file is fetched, handed to the caller's
     * uploader, and the node re-`src`ed to the relative uri that comes back:
     * that is "Copy to scene", and it is a copy because freezing a live
     * reference is a deliberate gesture, never a side effect of a save.
     * Without an uploader the unpack still happens and the affected files come
     * back on {@link UnpackReferenceResult.orphaned} — loud, not silent.
     *
     * @param reference - the placed reference. Not a reference ⇒ a no-op with
     *   `unpacked: false`.
     */
    public async unpackReference(
        reference: SceneObject,
        options: UnpackReferenceOptions = {},
    ): Promise<UnpackReferenceResult> {
        if (!reference.isSceneReference && !reference.contentRef) {
            Logger.warn(
                `[TraceScene] unpackReference: "${reference.name}" is not a reference — nothing to unpack.`,
            );
            return { object: reference, unpacked: false, copied: new Map(), orphaned: [] };
        }
        return this._dissolveReference(reference, options);
    }

    /**
     * Place a COPY of a library asset under `parent` (#950 §4): the asset's
     * nodes become this scene's own, and the copy keeps its link to the
     * asset as `Provenance.placedFrom`.
     *
     * The reference-placement path and the unpack gesture in one step, with
     * four differences a wrapper needs:
     *
     * - an explicit parent — the copy is built there, not at the import
     *   mount point;
     * - an optional kept root `uid`;
     * - `placedFrom` only, no `placedUids`: nothing reads it, and a copy
     *   whose pieces are later deleted would keep entries pointing at
     *   nothing;
     * - no upright counter-rotation — the caller sets the root's transform.
     *
     * References nested in the asset stay references (its pieces keep their
     * library `src`). Load first, then commit: when the asset, or any
     * reference inside it, does not load, everything this call built is
     * removed and this throws. Only that: what else has been put under the
     * parent while the asset was on its way is not the call's to take back. An
     * asset that is not a document (a bare GLB, an image) is refused before
     * anything is built.
     *
     * Any session, a legacy one included. The placement rule is a migration
     * gate for REFERENCES: a legacy session keeps its stand-in import because
     * a reference left in place there is what its old writers and the
     * converting save expect. A copy leaves no reference at its root — the
     * placement is dissolved into the parent before this returns — so it is
     * built the one way, the way reopen builds a document. Pieces nested in
     * the asset are built that way too, in every session: a piece that is a
     * bare GLB owns its file (the asset's own Model, Renderer and sizing on
     * it act on its meshes), and a piece with a document of its own stays a
     * reference. A legacy session writes no identity, so a kept `uid` is not
     * stamped there.
     *
     * @returns the copy's root.
     */
    public async placeCopy(url: string, parent: SceneObject, options: PlaceCopyOptions = {}): Promise<SceneObject> {
        if (!/^https?:\/\//i.test(url)) {
            throw new Error(`placeCopy: "${url}" is not an absolute asset URL`);
        }
        if (options.uid !== undefined && !isValidUid(options.uid)) {
            throw new Error(`placeCopy: "${options.uid}" is not a valid uid`);
        }
        if (!this.canParentUnder(parent)) {
            throw this._vetoReferenceSubtreeParent(options.name ?? url, parent);
        }

        // A failure takes back what this call built and nothing else: the
        // parent's other children are not its own. A user's object, or the
        // placement of a wrap running at the same time, can land there while
        // the asset is on its way.
        const target: CopyPlacement = { parent, made: [] };
        const rollback = (): void => {
            for (const node of target.made) {
                if (!node.transform.isDisposed()) this.deleteSceneObject(node);
            }
        };

        let placement: SceneObject | null;
        try {
            const { blob, fileName, assetBaseUrl } = await this.fetchAsset(url);
            ({ sceneObject: placement } = await this._loadFromBlob(
                blob, fileName, options.name, undefined, undefined, url, undefined, assetBaseUrl,
                false, true, false, target,
            ));
        } catch (error) {
            rollback();
            throw error;
        }
        if (!placement || !placement.isSceneReference || placement.getParent() !== parent) {
            rollback();
            throw new Error(`placeCopy: "${url}" did not resolve to a document`);
        }
        // A nested reference that failed to load was logged and skipped
        // (the nested-scene loop swallows per piece); a copy missing a piece
        // is not a copy of the asset.
        const missing = placement.getDescendants()
            .filter(o => o.isSceneReference && o._standInFileSrc === undefined && o.getChildren().length === 0);
        if (missing.length > 0) {
            rollback();
            throw new Error(`placeCopy: ${missing.map(o => `"${o.name}"`).join(', ')} in "${url}" did not load`);
        }

        // Every step from here on can still fail (an unknown behavior type, a
        // throwing dissolve), and the copy is already under the parent —
        // with a kept uid, a second holder of the old wrapper's identity.
        // The dissolve may lift the placement's only child out to stand in
        // its place, so what it holds now is what a failure has to find.
        target.made.push(...placement.getChildren());
        // Its pieces have loaded; nothing nested is still on the way.
        this._copiesBuilding.delete(placement);
        try {
            const result = await this._dissolveReference(placement, {
                ...(options.name !== undefined ? { name: options.name } : {}),
                ...(options.intoFolder ? { intoFolder: options.intoFolder } : {}),
            }, false);
            if (!result.unpacked || !result.object) {
                throw new Error(`placeCopy: a reparent policy refused to unpack "${url}"`);
            }
            const root = result.object;
            // The given behaviors are built from data like any others: in a
            // load window, whose close tells the scene the root is complete.
            root._beginBehaviorLoad();
            try {
                if (options.uid !== undefined && this._uidMintingActive) root._setUidInternal(options.uid);
                for (const data of options.behaviors ?? []) {
                    const { '@type': type, ...overrides } = data;
                    await root.setBehavior(type, overrides);
                }
            } finally {
                root._endBehaviorLoad();
            }
            return root;
        } catch (error) {
            rollback();
            throw error;
        }
    }

    /**
     * Give a reference to a DOCUMENT that was rebuilt from data the content its
     * document derives.
     *
     * `createSceneObjectFromData` builds the node (its `src`, behaviors,
     * transform) and stops there. A reference is a node plus whatever its
     * document derives beneath it, and that second half is built by the
     * nested-scene load a document open or a placement runs, which a node made
     * from data never runs. It comes back as an ordinary node that happens to
     * carry a URL: nothing under it, not a scene reference, and a save writes
     * it as a plain file. Undo and redo of a delete or a create that holds such
     * a node are exactly that path, so they call this after the rebuild.
     *
     * Not for a reference that resolved to a single file (a bare GLB): its
     * placement owns the file, its `Model` is in its own data, and the rebuild
     * loads the geometry without help. Handing such a node here would fetch the
     * file again to learn what the caller already recorded.
     *
     * The node is marked a reference first, so a fetch that fails leaves what a
     * document open leaves for a nested load that failed (a reference with no
     * content, still written as a link) rather than a plain node. The failure
     * is logged, not thrown: a piece that cannot load right now must not stop
     * an undo from putting the rest of the scene back. A load that fails
     * part-way leaves the same: the reference held nothing when this began, so
     * what stands under it afterwards is what the load built, and it is deleted.
     * A reference is whole or empty, never half.
     *
     * Does nothing when the node already holds derived content, so it is safe
     * to call twice.
     *
     * @returns whether the reference is whole: true when its content was
     *   built, or when there was nothing to build; false when the load failed,
     *   which is logged as well. The caller decides what a piece that stays
     *   empty means; this does not throw for it.
     * @internal The undo commands that rebuild a reference (`DeleteObjectCommand`, `CreateObjectCommand`).
     */
    public async _hydrateReference(reference: SceneObject): Promise<boolean> {
        if (!reference.src || reference.getChildren().length > 0) return true;
        reference._setSceneRefInternal(true);
        // The window a nested load runs in (see `_placeReferencedDocument`):
        // whoever reads the reference waits for it, and is told when it closes.
        reference._beginBehaviorLoad();
        try {
            await this._loadFromURL(reference.src, undefined, reference);
            return true;
        } catch (error) {
            Logger.error(`[TraceScene] Failed to rebuild reference ${reference.src} for ${reference.name}:`, error);
            for (const built of reference.getChildren()) this.deleteSceneObject(built);
            return false;
        } finally {
            reference._endBehaviorLoad();
        }
    }

    /**
     * The dissolve itself, with no "is this a reference?" gate.
     *
     * Split from {@link unpackReference} because the import path has already
     * answered that question from information the object does not carry: a
     * `.trace` imported from a Blob has no `src`, so nothing on it says it
     * stands in for a file — only the filename and `wrapsFile` do.
     */
    private async _dissolveReference(
        reference: SceneObject,
        options: UnpackReferenceOptions = {},
        recordPlacedUids = true,
    ): Promise<UnpackReferenceResult> {
        const copied = new Map<string, string>();
        const orphaned: string[] = [];

        const parent = reference.getParent();
        const children = reference.getChildren();
        // The reference's `src` is the only record that this content came from
        // somewhere, and unpack is about to drop it. Capture it before either
        // branch does. Absolute http(s) only — see `ProvenanceData.placedFrom`.
        const placedFrom = /^https?:\/\//i.test(reference.src) ? reference.src : undefined;

        // Collapse the stand-in into its single child ONLY when the stand-in
        // contributes nothing of its own.
        //
        // A packaging wrapper is exactly that: a structural root a format put
        // there, with a name and nothing else, and getting rid of it is what
        // unpack is for. But a promoted component's root IS the content
        // (SCENE_ROOT_SPEC §4) — it carries the component's behaviors, and it
        // can perfectly well have one child. Collapsing that one dropped every
        // behavior on it, silently: the old rule read "one child" and nothing
        // else, so `Group(Rotate) > Chair` unpacked to `Chair`, un-rotated.
        //
        // "Of its own" excludes the caller's own behaviors — those were put on
        // the wrapper by `loadFromPluginResult` on the way in and the collapse
        // branch below moves them onto the child, so they are not a reason to
        // keep it — and Provenance, which unpack itself is about to write.
        const callerSupplied = new Set((options.behaviors ?? []).map(data => data['@type']));
        const standsForNothingElse = reference.getBehaviors()
            .every(behavior => behavior.name === 'Provenance' || callerSupplied.has(behavior.name));
        if (parent && children.length === 1 && standsForNothingElse) {
            const placed = children[0];
            // What the promoted object is called, before the move: the reparent
            // below uniquifies against the destination's children, and the
            // stand-in is still one of them.
            const promotedName = placed.name;
            // A host reparent policy can VETO the move, and the veto is a
            // return value, not a throw. Unheeded, the object stays under the
            // wrapper and the delete below takes it with the wrapper's
            // descendants — unpack would erase the very thing it promotes.
            // Leave the import as a reference instead; that is the state the
            // scene is already in.
            const { moved } = this.reparentSceneObjectPreservingWorld(placed, parent);
            if (!moved) {
                Logger.warn(`[TraceScene] unpack: a reparent policy refused to move "${placed.name}" out of its file wrapper — leaving the import as a reference`);
                return { object: reference, unpacked: false, copied, orphaned };
            }

            // The stand-in goes NOW, before the promoted object is renamed:
            // it has given up its only child, everything read off it was read
            // above, and while it is still in the scene it is a sibling
            // claiming a name — placing a reference under the caller's name and
            // unpacking it produced "Chair (1)", the stand-in standing in the
            // way of its own replacement. (Delete takes a node's descendants
            // with it, which is exactly why this may not happen before the
            // reparent above succeeds.)
            this.deleteSceneObject(reference);

            // …and with it the name contention it caused. A placement named for
            // the document's root — the default, since that is what the user
            // saw — made its own content come out as "chair.glb (1)" on the way
            // past it. Give the name back now that nothing else holds it.
            if (placed.name !== promotedName
                && !placed.getSiblings().some(sibling => sibling !== placed && sibling.name === promotedName)) {
                this.renameSceneObject(placed, promotedName);
            }

            // The caller's behaviors were applied to the wrapper on the way in
            // (loadFromPluginResult puts them on the file's root node) — carry
            // them over before the wrapper goes away.
            for (const data of options.behaviors ?? []) {
                const { '@type': type, ...overrides } = data;
                await placed.setBehavior(type, overrides);
            }

            if (options.name) {
                const taken = new Set(placed.getSiblings().map(s => s.name));
                taken.delete(placed.name);
                this.renameSceneObject(placed, Utility.generateUniqueName(options.name, taken));
            }

            // The promoted nodes are this document's now, so they get this
            // document's identities (issue #837) — and the pairing with the
            // uids they had in the source document is stamped beside the URL
            // they came from, in one Provenance write (issue #838).
            // The content is this scene's own now, so its siblings get names of
            // their own, as on an editing load.
            this._dedupeSiblingNames(placed.getDescendants());
            const reminted = this._remintPromotedUids([placed, ...placed.getDescendants()]);
            const placedUids = recordPlacedUids ? this._placedUidsFor(reminted) : undefined;
            if (placedFrom || placedUids) {
                await placed.setBehavior('Provenance', {
                    ...(placedFrom ? { placedFrom } : {}),
                    ...(placedUids ? { placedUids } : {}),
                });
            }

            await this._adoptReferencedFolderFiles(
                placed,
                options.intoFolder,
                copied,
                orphaned,
            );

            return { object: placed, unpacked: true, copied, orphaned };
        }

        // Several objects (or none): keep the wrapper, minus its file
        // reference, as an ordinary group — named for what was placed, not for
        // the file's own root ("Scene"), so it reads right in an outliner.
        // Its identity, parent and local transform are untouched, which is
        // what lets a host keep its selection across the gesture.
        // The file the ex-reference node names in its OWN right, if the copy
        // below brings it across — a §3.1 leaf wrapper's GLB. Read before the
        // copy, which clears the record once it has used it.
        const ownFile = reference._standInFileSrc;
        await this._adoptReferencedFolderFiles(
            reference,
            options.intoFolder,
            copied,
            orphaned,
        );

        // A leaf whose bytes came across now names them itself, relatively, and
        // that `src` is the whole point of the copy. Otherwise the reference
        // URL goes, because the node is no longer standing in for anything.
        // Everything BELOW the ex-reference came out of the referenced
        // document and is this document's now — same re-mint as the collapse
        // branch (issue #837). The node itself is not promoted: it was always
        // this scene's own, and its identity does not change because its file
        // reference went away.
        const reminted = this._remintPromotedUids(reference.getDescendants());
        const placedUids = recordPlacedUids ? this._placedUidsFor(reminted) : undefined;

        const adoptedOwnFile = ownFile !== undefined && copied.has(ownFile);
        if (!adoptedOwnFile) reference._setSrcInternal('');
        reference._setSceneRefInternal(false);
        this._dedupeSiblingNames(reference.getDescendants());
        if (placedFrom || placedUids) {
            await reference.setBehavior('Provenance', {
                ...(placedFrom ? { placedFrom } : {}),
                ...(placedUids ? { placedUids } : {}),
            });
        }
        if (options.name && reference.name !== options.name) {
            const taken = new Set(reference.getSiblings().map(s => s.name));
            taken.delete(reference.name);
            this.renameSceneObject(reference, Utility.generateUniqueName(options.name, taken));
        }
        return { object: reference, unpacked: true, copied, orphaned };
    }

    /**
     * Bring every folder file the unpacked subtree still names across into
     * this scene's folder — the byte half of "Copy to scene" (spec §2.6).
     *
     * A node reaches its file two ways after a reference is dissolved: by its
     * own relative `src` (an ordinary node inside the referenced document), or
     * by {@link SceneObject._standInFileSrc} (the §3.1 leaf wrapper, whose
     * authored `src` became the reference URL). Both are relative to the
     * REFERENCED folder, and `ResourceManager.resolveAssetUrl` is what knows
     * where that folder actually is — a relative path with no mapping was
     * never a folder file and is left alone.
     *
     * Deduplicated by source uri: two nodes sharing a mesh upload it once.
     *
     * Only the DISSOLVED document's own files. A reference nested inside it
     * stays a reference (SCENE_ROOT_SPEC §2.3): its own `src` is considered
     * (a relative archive the document carries is one of its files, and an
     * absolute library URL resolves to nothing here and is left alone), but
     * never its stand-in file or anything under it, which belong to the
     * folder IT references. Walking into them uploaded every nested library
     * piece into this scene's folder and re-`src`ed it to the copy, so the
     * piece lost its library link (#950 §4).
     */
    private async _adoptReferencedFolderFiles(
        root: SceneObject,
        target: UnpackFolderTarget | undefined,
        copied: Map<string, string>,
        orphaned: string[],
    ): Promise<void> {
        const entries: Array<{ object: SceneObject; source: string }> = [];
        const walk = (object: SceneObject, isRoot: boolean): void => {
            const nested = !isRoot
                && (object.isSceneReference || object.contentRef !== null || object._standInFileSrc !== undefined);
            const source = nested ? object.src : (object._standInFileSrc || object.src);
            if (source) entries.push({ object, source });
            if (nested) return;
            for (const child of object.getChildren()) walk(child, false);
        };
        walk(root, true);

        for (const { object, source } of entries) {
            const sourceUrl = this.resources.resolveAssetUrl(source);
            if (!sourceUrl) continue;

            if (!target) {
                if (!orphaned.includes(source)) orphaned.push(source);
                continue;
            }

            let uri = copied.get(source);
            if (uri === undefined) {
                try {
                    const response = await fetch(sourceUrl);
                    if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
                    // The referenced folder's own `files[k].name` if it had
                    // one; the (hash) basename otherwise, which is all the
                    // uploader needs. Only a REAL name is recorded against the
                    // new uri: a hash is not a name, and inventing one would
                    // put noise in `files[k].name` forever (spec §2.1 — the
                    // field is omitted when nothing knows the original).
                    const known = this.resources.getAssetName(source);
                    const fileName = known ?? source.slice(source.lastIndexOf('/') + 1);
                    uri = await target.upload(await response.blob(), fileName);
                    this.resources.registerAssetName(uri, known);
                } catch (error) {
                    Logger.warn(
                        `[TraceScene] unpack: could not copy "${source}" (${sourceUrl}) into this scene's folder`,
                        error,
                    );
                    if (!orphaned.includes(source)) orphaned.push(source);
                    continue;
                }
                copied.set(source, uri);
            }

            object._setSrcInternal(uri);
            if (source === object._standInFileSrc) object._standInFileSrc = undefined;
            // The bytes are the same bytes; keep this session resolving them
            // from where they already are rather than re-downloading from the
            // destination folder we just wrote them to.
            this.resources.registerAssetUrl(uri, sourceUrl);
        }

        if (orphaned.length > 0) {
            Logger.warn(
                `[TraceScene] unpack: ${orphaned.length} file(s) belong to the referenced folder and were not `
                + 'copied into this scene, so this scene cannot save them. Pass `intoFolder` (the "Copy to scene" '
                + `uploader) to bring the bytes across: ${orphaned.join(', ')}`,
            );
        }
    }

    /**
     * Internal: Load asset from URL
     */
    private async _loadFromURL(
        url: string,
        name?: string,
        parent?: SceneObject,
        onDownloadProgress?: (loaded: number, total: number) => void,
        progressTracker?: IProgressTracker
    ): Promise<SceneObject | null> {
        // A nested reference keeps its authored relative `src` (spec §2.3), so
        // its bytes come from where the containing document says: the archive
        // that carried them, else the document's folder. Fetching the relative
        // path verbatim would address the page origin instead.
        const heldBlob = this.resources.isLocal(url) ? this.resources.getAsset(url) : undefined;
        const resolvedUrl = this.resources.resolveAssetUrl(url) ?? url;
        const { blob, fileName, assetBaseUrl } = heldBlob
            ? { blob: heldBlob, fileName: url.substring(url.lastIndexOf('/') + 1), assetBaseUrl: undefined }
            : onDownloadProgress
                ? await this.fetchAssetWithProgress(resolvedUrl, onDownloadProgress)
                : await this.fetchAsset(resolvedUrl);
        // Preserve HTTP source URL so export can choose to pack or keep as reference
        const sourceUrl = resolvedUrl.startsWith('http') ? resolvedUrl : undefined;
        // Nested scene-format references are additive → scope their subassets.
        const { sceneObject } = await this._loadFromBlob(blob, fileName, name, progressTracker, parent, sourceUrl, undefined, assetBaseUrl, false, true)
            // The parent can be deleted while the download runs (a second undo, a
            // Delete), and the load builds under it all the same: nothing owns that,
            // so it is deleted again, and the object returned may be one of those.
            .finally(() => { if (parent?.transform.isDisposed()) this._deleteBeneath(parent); });
        return sceneObject;
    }

    /**
     * Delete every scene object that stands beneath `node`'s transform. Walks
     * the transforms upward from each object, not downward from `node`: a
     * disposed node no longer lists the children a late load parented to it.
     */
    private _deleteBeneath(node: SceneObject): void {
        const beneath = (object: SceneObject): boolean => {
            for (let at = object.transform.parent; at; at = at.parent) if (at === node.transform) return true;
            return false;
        };
        for (const built of this.sceneObjects.filter(beneath)) {
            if (!built.transform.isDisposed()) this.deleteSceneObject(built);
        }
    }

    /**
     * Place a referenced DOCUMENT: the placement first, the document's nodes
     * beneath it (SCENE_ROOT_SPEC §2.3, row 2 of the rule table).
     *
     * This is the import half of "import and reopen build the same tree". The
     * reopen half is the nested-scene path in {@link loadFromPluginResult},
     * which already does exactly this — the reference SceneObject exists and
     * the document loads with `parent` set to it. Import had no such node, so
     * it made the document's own root BE the reference (`wrapsFile` /
     * `stampWrapperSrc` below), and the save then wrote the root's behaviors
     * onto the scene's node: the copy that shadows the root's own declaration
     * on the next open (the ESPN Remote bug, §2.3 "What is wrong today").
     *
     * What the placement carries is only what the SCENE put there:
     *
     * - `name` — the caller's, else the document's single root name (what the
     *   user saw before this change), else the file's base name;
     * - the identity transform. The document's nodes keep their own and
     *   compose under it;
     * - the scene-reference flag, stamped BEFORE the content loads, so the
     *   load treats what it builds below as the referenced file's (it keeps
     *   the file's names). The load parents that content directly, never
     *   through `canParentUnder`, so the early flag refuses nothing;
     * - `src` — the reference URL — stamped AFTER the content is built: it is
     *   what the nested-scene loop keys on, so stamping it up front would
     *   re-fetch the same document forever;
     * - the caller's `behaviors`, which are the scene's statement about the
     *   placement (an import-time `MorphStretch`, a `Rotate`). They are NOT
     *   pushed down onto the document's root — that is the copy this change
     *   exists to stop.
     *
     * There is nothing scene-level left to drop or warn about: under D8 the
     * document's own data is on its root NODE, which arrives beneath the
     * placement with everything it says about itself intact (§2.3). A foreign
     * multi-root document simply puts all its roots under the placement.
     */
    private async _placeReferencedDocument(
        result: LoadedSceneResult,
        fileName: string,
        sourceUrl: string,
        name?: string,
        progressTracker?: IProgressTracker,
        extraBehaviors?: BehaviorData[],
        uprightImport = false,
        copy?: CopyPlacement,
    ): Promise<{ sceneObject: SceneObject | null; wrapsFile?: boolean }> {
        // The placement is a node of THIS scene, so it lands where a new node
        // lands: under the document root when the session has one (D8), or
        // where {@link placeCopy} was told to build it.
        const parentTarget = copy?.parent ?? this._importMountParent() ?? this._sceneRoot ?? undefined;
        const baseName = name
            ?? (result.rootNodes.length === 1 ? result.rootNodes[0].name : undefined)
            ?? fileName.replace(/\.[^/.]+$/, '');
        const placementName = Utility.generateUniqueName(
            baseName,
            this.getSiblingNamesByTransform(parentTarget?.transform ?? null),
        );

        const placement = SceneCreation.initializeSceneObject(
            {
                name: placementName,
                src: '',
                active: true,
                transform: {},
                behaviors: extraBehaviors ? structuredClone(extraBehaviors) : [],
            },
            this.babylonScene,
            this.resources,
            this.config,
            this,
            this._eventSystem || undefined,
        );
        this.sceneObjects.push(placement);
        // The placement is a node of THIS document — it is what the scene
        // saves, and what an override would address — so it gets this
        // session's identity like any other new node (issue #837).
        this._stampNodeUid(placement, 'created');
        if (parentTarget) placement.setParent(parentTarget);
        copy?.made.push(placement);
        if (copy) this._copiesBuilding.add(placement);

        // The placement is parented above and built at the bottom of this
        // block, with the whole nested document loaded in between — so its
        // load window opens at the parenting, not at `applyComponents`.
        // Without that, an import into a live scene spends the nested
        // download looking like a node that declares nothing (F16).
        // A reference before its content arrives, so the load treats what it
        // builds below as the referenced file's (it keeps the file's names).
        placement._setSceneRefInternal(true);
        placement._beginBehaviorLoad();
        try {
            // The document, verbatim, beneath it — the nested path, with no
            // `extraBehaviors` (they belong to the placement, above) and no
            // `uprightImport` (that is the placement's, below).
            await this.loadFromPluginResult(result, progressTracker, placement, undefined, false);

            await SceneCreation.applyComponents(placement, placement.name);
        } finally {
            placement._endBehaviorLoad();
        }
        placement._attachBehaviors();

        // Keep-upright on import is a property of the PLACEMENT: the document's
        // nodes compose under it, so correcting it corrects the whole placement
        // exactly once (nested-ref-hydration-rotation.test.ts pins that the
        // content itself is never counter-rotated).
        if (uprightImport && parentTarget) {
            this._preserveAuthoredWorldRotation(placement, parentTarget);
        }

        placement._setSrcInternal(sourceUrl);

        Logger.log(`[TraceScene] Placed reference "${placementName}" → ${sourceUrl} with ${result.rootNodes.length} document root(s) beneath it`);
        this._emitTopologyChanged();

        return { sceneObject: placement, wrapsFile: true };
    }

    /**
     * Internal: Load asset from Blob
     * @param blob - The asset blob
     * @param fileName - The file name with extension
     * @param name - Optional custom name for the scene object
     * @param progressTracker - Optional progress tracker (typically provided during initial scene load)
     * @param parent - Optional parent SceneObject for loaded content (defaults to scene root)
     * @param sourceUrl - Optional original HTTP URL; when set, preserved as `src` so export can choose to pack or keep as reference
     * @param openAsScene - True when this file is being opened AS the scene
     *   (`loadFromURL`/`loadFromBlob`). Nothing is absorbed either way — the
     *   document's own root is a node and stays one (SCENE_ROOT_SPEC D8) — but
     *   a FOREIGN multi-root document gets a synthesized root only on this path
     *   (§2), and the caller gets that root back to apply CMS metadata to.
     * @param uprightImport - True only for a direct user import (`loadAsset`): apply the
     *   upright-on-import adjustment to the new top-level content. False for scene opens
     *   and nested scene-reference loads (see loadFromPluginResult).
     * @param copy - Set only by {@link placeCopy}: the parent to build the
     *   placement under, which the load also uses to report what it has built.
     * @returns The first loaded SceneObject, the document root (§2.2), and
     *   `wrapsFile` — see {@link _unpackImportedScene}
     */
    private async _loadFromBlob(blob: Blob, fileName: string, name?: string, progressTracker?: IProgressTracker, parent?: SceneObject, sourceUrl?: string, extraBehaviors?: BehaviorData[], assetBaseUrl?: string, openAsScene?: boolean, scopeAssets?: boolean, uprightImport = false, copy?: CopyPlacement): Promise<{ sceneObject: SceneObject | null; documentRoot?: SceneObject | null; wrapsFile?: boolean }> {
        Logger.log(`[TraceScene] _loadFromBlob: ${fileName} with ${extraBehaviors?.length ?? 0} extra behaviors`);

        // Ensure scene root exists before loading any objects
        this.initializeSceneRoot();

        // What the file IS comes from the name the fetch RESOLVED to, never
        // from the `src` it is placed by: a library reference is
        // `<api>/assets/{guid}/file` and names no format (CONTENT_MODEL_SPEC
        // §3.4). Every decision below — format, implied behavior, loader —
        // takes it from here.
        const fileExtension = Utility.getFileExtensionFromSrc(fileName);
        this._sourceFormat = fileExtension.toLowerCase();

        // A scene-ESTABLISHING load — `loadFromURL`/`loadFromBlob`/`openAsRoot`,
        // i.e. neither a nested reference (`parent`) nor an additive import
        // (`scopeAssets`) — is the one that says what this SESSION is
        // (see {@link sceneSourceFormat}).
        const establishesScene = !parent && !scopeAssets;
        if (establishesScene) this._sceneSourceFormat = this._sourceFormat;

        // Handle scene format files using plugin registry. `.gltf`/`.glb` name
        // a plain model as often as a scene document, so they route on
        // CONTENT, never on the extension (migration plan D2): sniff once,
        // here, and hand the parsed JSON straight to the plugin.
        let formatConfig = getSceneFormatConfig(fileExtension);
        let documentJson: Record<string, unknown> | undefined;
        let archiveBytes: Uint8Array | undefined;
        if (!formatConfig && isGltfExtension(fileExtension)) {
            documentJson = await readSceneDocumentJson(blob);
            if (documentJson && isSceneDocument(documentJson)) {
                formatConfig = getSceneDocumentFormatConfig();
            } else {
                documentJson = undefined;
            }
        }
        // A BUNDLE is a zip with `scene.gltf` at its root (spec §4), and the
        // extension is a hint rather than the authority: bundles arrive as
        // `.glz`, as a legacy `.tracex`, as `.zip` because a user renamed one,
        // and — through a redirect or a content-addressed URL — as nothing at
        // all. So ask the bytes. The sniff is a central-directory walk, not a
        // parse, and it runs only where the extension did not already settle
        // it or settled it as the legacy Unity `.zip`, which this correctly
        // distinguishes: that archive has no `scene.gltf`.
        if (!formatConfig || fileExtension === 'zip') {
            archiveBytes = toZipBytes(await blob.arrayBuffer());
            if (isBundleArchive(archiveBytes, SCENE_DOCUMENT_ARCHIVE_ENTRIES)) {
                if (formatConfig) {
                    Logger.log(`[TraceScene] "${fileName}" is a bundle (scene document at the archive root), not a legacy .${fileExtension}`);
                }
                formatConfig = getBundleFormatConfig();
                this._sourceFormat = 'glz';
                if (establishesScene) this._sceneSourceFormat = 'glz';
            }
        }
        if (formatConfig) {
            const plugin = formatConfig.createPlugin();
            const data = documentJson
                ?? archiveBytes
                ?? (formatConfig.dataType === 'text'
                    ? await blob.text()
                    : await blob.arrayBuffer());
            const rootUrl = formatConfig.needsRootUrl ? (assetBaseUrl || '') : '';

            // Every file the document lists lives in its folder (spec §2.1),
            // not only the ones a node owns: a Properties sheet or a particle
            // texture is named only inside behavior data. Register them all so
            // those relative refs resolve against the document, as node `src`s
            // do (see loadFromPluginResult).
            if (documentJson && rootUrl && Array.isArray(documentJson.files)) {
                for (const file of documentJson.files as Array<{ uri?: unknown }>) {
                    const uri = file?.uri;
                    if (typeof uri !== 'string' || !this.resources.isLocal(uri)) continue;
                    const folderUrl = resolveAgainst(uri, rootUrl);
                    if (folderUrl !== uri) this.resources.registerAssetUrl(uri, folderUrl);
                }
            }

            // A standalone document opened from a blob: let its `files[].uri`
            // refs resolve to sibling blobs registered in the ResourceManager
            // (companion files of a single-file open) before falling back to
            // rootUrl fetches. The .tracex subclass carries its own bundled
            // asset map, which still takes precedence. (Alias avoids `instanceof`
            // narrowing `plugin`, which would union the loadWithResult return type.)
            const documentPlugin = plugin instanceof GltfSceneLoaderPlugin ? plugin : null;
            documentPlugin?.setExternalAssetLookup((uri) => this.resources.resolveExternalAssetBlob(uri));
            // Whether a FOREIGN multi-root document gets a document root
            // invented for it (SCENE_ROOT_SPEC §2): only when it is the scene.
            documentPlugin?.setOpenAsScene(!!openAsScene);

            const result = await plugin.loadWithResult(
                this.babylonScene,
                data,
                rootUrl,
                undefined,
                fileName,
            );

            // Extract source info from legacy .zip files for round-trip
            // export. Keyed on the PLUGIN, not the extension: a `.zip` whose
            // bytes are a bundle was just routed to the bundle reader, which
            // has no `info.json` to give.
            if (plugin instanceof TraceZipSceneLoaderPlugin) {
                this.sourceInfoFile = plugin.getSourceInfoFile() ?? undefined;
            }

            // Store asset blobs in ResourceManager for behaviors to access.
            // Subasset filenames are only unique *within* a scene archive, so two
            // separate nested .trace files can carry same-named-but-different
            // assets. A nested scene (parent !== undefined) re-homes its assets
            // under a scene-scoped folder so they can't collide; its `src`/script
            // references are rewritten to the scoped keys. The top-level scene
            // keeps its assets at `assets/<name>` so export round-trips unchanged.
            if (result.assetBlobs) {
                // `scopeAssets` is set by ADDITIVE loads — a nested scene-format
                // reference (the nested-scene loop) or a public loadAsset/import of
                // a .trace into an existing scene. These re-home their subassets
                // under a per-source folder so same-named assets from different
                // scenes can't collide. Scene-ESTABLISHING loads (loadFromURL/
                // loadFromBlob/openAsRoot) leave it off and keep bare `assets/<name>`
                // keys, so the scene's own assets export/round-trip unchanged.
                if (scopeAssets) {
                    // sourceUrl (when present) identifies the source so repeat
                    // placements of the same .trace reuse one folder + the container
                    // cache; fall back to the fileName-derived label otherwise.
                    const remap = this.resources.mergeSceneAssets(result.assetBlobs, name ?? fileName, sourceUrl);
                    this._applyAssetKeyRemap(result, remap);
                    await remapSheetAssetRefs(result.nodes, this.resources, remap);
                } else {
                    for (const [path, assetBlob] of result.assetBlobs) {
                        this.resources.addAsset(path, assetBlob);
                    }
                }
                Logger.log(`[TraceScene] Stored ${result.assetBlobs.size} assets in ResourceManager`);
            }

            // The document's foreign half, held for the next save (issue #834).
            // Only a scene-ESTABLISHING load owns it: an import (`scopeAssets`)
            // or a nested reference (`parent`) is content from ANOTHER
            // document, and its extensions are not this scene's to re-emit.
            if (!parent && !scopeAssets && result.foreignDocument) {
                this._foreignDocument = result.foreignDocument;
            }

            // Compile scripts from the scene's scripts map
            if (result.scripts) {
                this._scriptsMap = result.scripts;
                for (const [typeName, assetPath] of Object.entries(result.scripts)) {
                    const scriptBlob = this.resources.getAsset(assetPath);
                    if (scriptBlob) {
                        const source = await scriptBlob.text();
                        this._scriptRegistry.register(typeName, source);
                    } else {
                        Logger.warn(`[TraceScene] Script asset not found: ${assetPath} for @type "${typeName}"`);
                    }
                }
            }

            // Where content lands when nothing else claims it. An IMPORT goes
            // under the DOCUMENT ROOT — the editor's default parent, and the
            // node a reopen would put it under, so import and reopen build the
            // same tree (SCENE_ROOT_SPEC §2.3, D8). A scene-ESTABLISHING open
            // mounts the document root itself under the engine root, and a
            // nested load fills in the node that referenced it.
            const mountParent = parent ?? (openAsScene ? undefined : this._importMountParent());

            // ── Import into a 2.1 session: build the tree reopen builds ──
            //
            // SCENE_ROOT_SPEC §2.3: a node with a `src` instantiates whatever
            // that `src` resolves to, and a document's nodes arrive UNDER the
            // node that references them, unchanged. So the placement is made
            // first and the document loads beneath it through the very path a
            // reopen takes (`loadFromPluginResult(result, …, parent)`) — no
            // stand-in, nothing copied up, `scenes[n].extras.trace` applied
            // nowhere.
            //
            // Gated on three things:
            //  - a TOP-LEVEL import (`!parent`, `!openAsScene`): with a
            //    `parent` this IS the reopen path already, and with
            //    `openAsScene` the file is being opened AS the scene;
            //  - a reference URL to place. A blob import names no file the next
            //    open could resolve, so there is no reference to be the
            //    placement of — the content becomes the scene's own, as today;
            //  - the session (see {@link placementRuleActive}), which is the
            //    migration gate: a legacy session keeps the stand-in import
            //    below, unchanged, until #816 deletes it. It is decided by the
            //    session alone — a `.trace` asset placed in a 2.1 session
            //    follows the rule like anything else.
            //  A copy ({@link placeCopy}) takes this path in every session:
            //  it is dissolved before the call returns, so no reference stays.
            const importsReference = !parent && !openAsScene && !!sourceUrl;
            if (importsReference && (this.placementRuleActive || copy)) {
                return await this._placeReferencedDocument(
                    result,
                    fileName,
                    sourceUrl,
                    name,
                    progressTracker,
                    extraBehaviors,
                    uprightImport,
                    copy,
                );
            }

            // Whether the load lands under one object that stands in for the
            // file. A file with several roots has no such object — its content
            // arrives as ordinary top-level objects of this scene, and
            // `sceneObjects[0]` is simply the first of them.
            //
            // Only for a TOP-LEVEL load (`!parent`). On the nested-scene path
            // the stand-in ALREADY EXISTS — it is the reference SceneObject
            // being filled in, and it already carries the URL — so the
            // document's own root is its content, not a second stand-in for the
            // same file. Stamping it there made the document's root claim to be
            // a reference to the document it belongs to, which
            // `referenceAncestor` and unpack both then had to disbelieve
            // (SCENE_ROOT_SPEC §2.3: a document's nodes arrive unchanged).
            const loneRoot = !parent && result.rootNodes.length === 1
                ? result.rootNodes[0]
                : undefined;

            // An ARCHIVE's structural root: one parentless node with no file of
            // its own, purely a grouping node (`.trace`/`.zip`/`.tracex`, and a
            // document that still carries the legacy synthetic `"Scene"` node).
            const structuralRoot = !!loneRoot && !loneRoot.src;

            // A DOCUMENT's own root, which does carry a `src`. The §3.1 wrapper
            // the server generates for a leaf upload is exactly this: one node,
            // `externalAsset` → `files[0].uri = "assets/<hash>.glb"`. That node
            // IS the stand-in for the document — the `assets/…` path is the
            // document's internal business, not the identity of the thing the
            // user placed — so it takes the document's URL below and the
            // relative path stays where it belongs, inside the folder.
            //
            const srcBearingRoot = !!loneRoot
                && !structuralRoot
                && documentJson !== undefined;

            // Whether that lone node stands in for the file at all, or is
            // content in its own right. Asked twice below — once for the
            // stand-in, once for the session's root — so it is decided once.
            const standsInForFile = structuralRoot || srcBearingRoot;

            // A stand-in only exists on a top-level, non-session load, for the
            // §2.3 reason above.
            const wrapsFile = !openAsScene && standsInForFile;

            // Track whether we should stamp the source URL onto the wrapper
            // root after construction. We do this *after* loadFromPluginResult
            // rather than mutating the loaded-node up front, because that
            // function treats `src` ending in a scene-format extension as a
            // nested-scene reference to fetch — which would recursively reload
            // the same .trace forever. Stamping post-construction (on the
            // SceneObject) gives the wrapper its reference identity for export
            // without confusing the loader's nested-asset path.
            const stampWrapperSrc = !!sourceUrl && wrapsFile;

            // The same answer, asked of the session: a lone top-level node that
            // carries a file is content, not the session's root. That is a
            // legacy archive's lone record (the archive states no root of its
            // own), or a document's only node: one file with behaviors (a v4
            // server wrapper, or a leaf's first edit saved without an adopt;
            // CONTENT_MODEL_SPEC §3.1). Promoting it
            // would make it undeletable, parent every import inside it, and
            // drop it from the next `.zip` save.
            const loneFileContent = !!loneRoot?.src
                && ((!result.documentRoot && !standsInForFile) || result.nodes.length === 1);

            // …so that node is not the root ENTITY either, and external root
            // behaviors (CMS metadata, `OpenOptions.behaviors`) are not its
            // to replace: the node keeps its own set, and they go to the root
            // created for them below (SCENE_ROOT_SPEC §5.1). Replacing the
            // model's set would delete the `Model` that holds its file.
            const rootEntityBehaviors = openAsScene && loneFileContent ? undefined : extraBehaviors;

            const sceneObjects = await this.loadFromPluginResult(result, progressTracker, mountParent, rootEntityBehaviors, uprightImport, establishesScene);

            // A reference that resolves to a document owns no file (§2.3,
            // row 2). The asset primitive it kept from when it resolved to one
            // would load the document as that file. This edits data only: on a
            // nested load the placement's behaviors are built after this, and
            // `_hydrateReference` rebuilds a node whose data was saved after a
            // load that already dropped it.
            if (parent) dropAssetPrimitiveData(parent);

            Logger.log(`[TraceScene] Loaded .${fileExtension} with ${result.nodes.length} nodes via plugin`);

            // The document root, as a live object: the reader normalized the
            // document to one root before any of these existed (§3), and the
            // SceneObjects were built in node order, so the index is the
            // mapping. It is what CMS metadata lands on (§5.1) — never the
            // engine root, which is a mount point.
            // A legacy `.trace` states no document root of its own, but its
            // single structural node IS one (§3): one root node, one document
            // root. A flat Unity `.zip` has several and gets none — its
            // session adopts one when an editor asks for it
            // ({@link ensureDocumentRoot}) or the writer synthesizes one.
            const rootNode = result.documentRoot
                ?? (result.rootNodes.length === 1 ? result.rootNodes[0] : undefined);
            const documentRootIndex = rootNode ? result.nodes.indexOf(rootNode) : -1;
            const documentRoot = documentRootIndex >= 0 ? sceneObjects[documentRootIndex] ?? null : null;
            // This session's root, if the load established the session (§2.2)
            // and the file has one at all ({@link loneFileContent}).
            if (openAsScene && documentRoot && !loneFileContent) this._sessionDocumentRoot = documentRoot;

            // A file whose lone top-level node is content, opened with
            // root-entity data (§5.1): that data needs a root entity, so the
            // engine creates the document root above the node — the one an
            // editor's adopt would create — and it takes the whole set there
            // (§5.2). The node stays content under it, with its own behaviors,
            // and publish reads the same root (§5.3). Without such data the
            // file is left as it states itself: no root.
            if (openAsScene && loneFileContent && documentRoot && extraBehaviors?.length) {
                const created = this._createDocumentRoot([documentRoot]);
                await this._replaceRootBehaviors(created, extraBehaviors);
            }

            // A NAMED import names what arrived. There is no URL here for a
            // placement to stand in for (§2.3: a blob names no file the next
            // open could resolve), so the content becomes the scene's own — and
            // the node the caller is naming is the document's root.
            if (name && !parent && !openAsScene && !sourceUrl && documentRoot) {
                this.renameSceneObject(
                    documentRoot,
                    Utility.generateUniqueName(
                        name,
                        new Set(documentRoot.getSiblings().map(o => o.name)),
                    ),
                );
            }

            // Stamp the original URL onto the wrapper so export emits a reference
            // instead of inlining the imported subtree. Done post-construction
            // (rather than via LoadedSceneNode.src) so the loader's nested-asset
            // auto-fetch doesn't recursively reload the same .trace.
            if (stampWrapperSrc && sourceUrl) {
                const standIn = sceneObjects[0];
                // What the node itself named, before the reference URL takes
                // its place. For a §3.1 leaf wrapper that is
                // `assets/<hash>.glb` in the ASSET's folder — the only handle
                // on the bytes "Copy to scene" has to bring across when the
                // reference is later dissolved (see `unpackReference`).
                if (standIn && standIn.src && standIn.src !== sourceUrl) {
                    standIn._standInFileSrc = standIn.src;
                }
                standIn?._setSrcInternal(sourceUrl);
                // …and it IS a scene reference, whatever the URL's last
                // segment looks like. A `.trace` says so by extension; a
                // library reference is `<api>/assets/{guid}/file` and has no
                // extension at all (spec §3.4), so without this the placed
                // object failed `isSceneReference` and every rule keyed on it
                // — reference-subtree parenting, export filtering, "open
                // asset" — treated a live library link as ordinary content.
                standIn?._setSceneRefInternal(true);
            }

            return { sceneObject: sceneObjects[0] ?? null, documentRoot, wrapsFile };
        }

        // A copy is made of a document's nodes. A single file (a GLB, an image, a
        // video) has none: it loads as an object of its own, on a path that does
        // not report it to `copy`, so the call would have nothing it could take
        // back. Refused here, before anything is built.
        if (copy) {
            throw new Error(`placeCopy: "${sourceUrl ?? fileName}" did not resolve to a document`);
        }

        // Handle single assets
        if (!DROPPABLE_ASSET_EXTENSIONS.has(fileExtension)) {
            throw new Error(`Unsupported file type: ${fileExtension}. Supported: ${[...DROPPABLE_ASSET_EXTENSIONS].join(', ')}`);
        }

        // Where a single asset lands, by the same rule as a document's nodes
        // above: under the document root for an import, under the node that
        // referenced it for a nested load, under the engine root otherwise.
        const assetMountParent = parent ?? (openAsScene ? undefined : this._importMountParent());

        // Ensure unique name among siblings
        const siblingNames = this.getSiblingNamesByTransform(
            (assetMountParent ?? this._sceneRoot)?.transform ?? null,
        );
        const objectName = Utility.generateUniqueName(name || fileName.replace(/\.[^/.]+$/, ''), siblingNames);

        // A REFERENCE that resolved to a file: the placement owns it
        // (SCENE_ROOT_SPEC §2.3, row 1), whatever kind of file it is. A legacy
        // session keeps a model as a child of the placement instead (see
        // {@link placementRuleActive}), except inside a copy being placed,
        // whose pieces carry their own Model and Renderer.
        if (parent && (this.placementRuleActive || !ALL_3D_EXTENSIONS.has(fileExtension) || this._isInCopyBeingBuilt(parent))) {
            return { sceneObject: await this._fillReferenceWithFile(parent, fileName, fileExtension, blob, sourceUrl, assetBaseUrl) };
        }

        // 3D assets use direct ImportMeshAsync path (no model behavior needed)
        if (ALL_3D_EXTENSIONS.has(fileExtension)) {
            const blobUrl = URL.createObjectURL(blob);
            try {
                // With a `parent`, a legacy session's reference resolved to
                // this file: the model belongs under the stand-in.
                const so = await this.loadSingle3DAsset(blobUrl, objectName, fileName, blob, fileExtension, progressTracker, sourceUrl, extraBehaviors, assetBaseUrl, uprightImport, assetMountParent, establishesScene);
                return { sceneObject: so };
            } finally {
                URL.revokeObjectURL(blobUrl);
            }
        }

        // Images and videos: Image, Video and AudioClip behaviors on a new node
        const assetPath = await this._registerImportedFile(fileName, blob, sourceUrl, assetBaseUrl);
        const src = sourceUrl ?? `./${assetPath}`;

        const sceneData = sceneObjectPlaceHolder(src, objectName);
        // What the node IS comes from the name the fetch RESOLVED to, not from
        // the `src` it is placed by — the same rule {@link _fillReferenceWithFile}
        // applies, and for the same reason. A library media reference is
        // `<api>/assets/{guid}/file` (spec §3.4): `applyComponents`' "src as
        // template" pass reads no extension off it, so without this the
        // placement arrives with no Image/Video/AudioClip, renders nothing,
        // and SAVES that way — a node whose src is a jpeg and whose document
        // says it is nothing at all. Seeded as data, so the behavior is
        // instantiated with the rest of them (and an authored one still wins).
        sceneData.behaviors = impliedBehaviorDataForExtension(fileExtension);
        const sceneObjects = await this.loadSceneObjects([sceneData], extraBehaviors, assetMountParent, establishesScene);

        // Image/video: same upright-on-import adjustment as
        // loadFromPluginResult (see the comment there).
        const imported = sceneObjects[0];
        const uprightTarget = assetMountParent ?? this._sceneRoot;
        if (uprightImport && imported && uprightTarget && imported.getParent() === uprightTarget) {
            this._preserveAuthoredWorldRotation(imported, uprightTarget);
        }

        Logger.log(`Loaded ${fileExtension} asset: ${fileName} as ${objectName}`);
        return { sceneObject: imported ?? null };
    }

    /**
     * Load assets from a URL and start the scene
     * Handles both single assets and .trace ZIP files
     * @param url - URL to load from
     * @param progressTracker - Optional progress tracker for UI updates
     */
    public async loadFromURL(url: string, progressTracker?: IProgressTracker, sceneRootBehaviors?: BehaviorData[]): Promise<void> {
        if (!url) {
            throw new Error('Please provide a valid URL');
        }

        try {
            const { blob, fileName, assetBaseUrl } = await this.fetchAsset(url);

            // The document is opened AS the scene: its root node is mounted
            // under the engine root, and nothing is absorbed (SCENE_ROOT_SPEC
            // §2.2, D8).
            await this._loadFromBlob(blob, fileName, undefined, progressTracker, undefined, undefined, sceneRootBehaviors, assetBaseUrl, true);

            await this._applyExternalRootBehaviors(sceneRootBehaviors);

            await this.startScene();

        } catch (error) {
            // A load the dispose cut short is a teardown, not an error to log.
            if (!this.babylonScene.isDisposed) Logger.error("Error loading from URL:", error);
            throw error;
        }
    }

    /**
     * Load a scene from a Blob with progress tracking.
     * This is the blob equivalent of loadFromURL - clears the scene and loads new content.
     * @param blob - The scene blob data
     * @param fileName - The file name with extension (e.g., "scene.trace")
     * @param progressTracker - Optional progress tracker for UI updates
     * @param sceneRootBehaviors - Optional behaviors to apply to the scene root after loading
     * @param assetBaseUrl - Optional directory URL the blob was fetched from, used to
     *   resolve relative external references (a .glxf's assets, a .gltf's .bin/textures)
     */
    public async loadFromBlob(blob: Blob, fileName: string, progressTracker?: IProgressTracker, sceneRootBehaviors?: BehaviorData[], assetBaseUrl?: string): Promise<void> {
        if (!blob || !fileName) {
            throw new Error('Please provide a valid blob and fileName');
        }

        try {
            // Opened AS the scene — see loadFromURL.
            await this._loadFromBlob(blob, fileName, undefined, progressTracker, undefined, undefined, sceneRootBehaviors, assetBaseUrl, true);

            await this._applyExternalRootBehaviors(sceneRootBehaviors);

            await this.startScene();
        } catch (error) {
            if (!this.babylonScene.isDisposed) Logger.error("Error loading from Blob:", error);
            throw error;
        }
    }

    /**
     * Fetch an asset from a URL with optional download progress tracking.
     * Uses streaming response body to report byte-level progress when Content-Length is available.
     */
    public async fetchAssetWithProgress(
        url: string,
        onProgress?: (loaded: number, total: number) => void,
        fetchOptions?: AssetFetchOptions,
    ): Promise<{ blob: Blob; fileName: string; assetBaseUrl: string }> {
        // A plain guest fetch passes NO init at all — the posture the
        // anonymous asset endpoints are designed around (spec §3.4), and one
        // the suite pins. An init object is built only when there is something
        // to put in it: the host's `fetchInit` (auth headers) or a revalidating
        // open. `no-cache` there = revalidate, not re-download — the
        // conditional request 304s when nothing moved (see
        // {@link AssetFetchOptions.revalidate}).
        const init: RequestInit | undefined = fetchOptions?.revalidate
            ? { ...(this.fetchInit ?? {}), cache: 'no-cache' }
            : (this.fetchInit ?? undefined);
        const response = await fetch(url, init);
        // An answer, including an error status, that arrives after the dispose is a teardown, not a failed load.
        if (this.babylonScene.isDisposed) throw disposedError();
        if (!response.ok) {
            throw new Error(`Failed to fetch asset: ${response.status} ${response.statusText} (${url})`);
        }

        // Base URL for resolving a document's relative `files[].uri` — and the
        // file name that decides the format — come from the RESOLVED response
        // URL, not the request URL. A library reference is
        // `<api>/assets/{id}/file`, which 302s to the folder document
        // (spec §3.4); resolving against the request would address the API
        // host and lose both the extension and the folder.
        const absoluteUrl = response.url || new URL(url, window.location.href).href;
        const urlPath = absoluteUrl.split('?')[0];
        const lastSlash = urlPath.lastIndexOf('/');
        const assetBaseUrl = lastSlash >= 0 ? urlPath.substring(0, lastSlash + 1) : '';
        const contentLength = response.headers.get('Content-Length');
        const total = contentLength ? parseInt(contentLength, 10) : 0;
        const startedAt = performance.now();

        let blob: Blob;
        if (onProgress && response.body) {
            // Stream the response to track download progress. The reader
            // yields DECODED bytes while Content-Length may be the compressed
            // transfer size (or plain wrong for external sources), so `loaded`
            // can overshoot `total` — report raw values and let consumers
            // decide how to display; never pre-allocate from Content-Length.
            const reader = response.body.getReader();
            let loaded = 0;
            const chunks: BlobPart[] = [];

            try {
                for (;;) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    chunks.push(value);
                    loaded += value.length;
                    onProgress(loaded, total);
                }
            } finally {
                reader.releaseLock();
            }

            const contentType = response.headers.get('Content-Type');
            blob = new Blob(chunks, contentType ? { type: contentType } : undefined);
            if (total > 0 && loaded !== total) {
                // Expected for compressed transfers: we count decoded bytes,
                // Content-Length is the transfer size. A real truncation shows
                // up downstream as a parse failure, so debug is enough here.
                Logger.debug(`[TraceScene] Decoded ${loaded} bytes vs Content-Length ${total} for ${url} (likely compressed transfer)`);
            }
        } else {
            blob = await response.blob();
        }

        const fileName = urlPath.split('/').pop() || 'asset';
        const seconds = (performance.now() - startedAt) / 1000;
        Logger.log(`[TraceScene] Downloaded ${fileName}: ${(blob.size / 1048576).toFixed(1)} MB in ${seconds.toFixed(1)}s`);
        // Every load resumes here after its download; stop before any of them builds into a disposed scene.
        if (this.babylonScene.isDisposed) throw disposedError();
        return { blob, fileName, assetBaseUrl };
    }

    // ============================================
    // SCENE OBJECT MANAGEMENT
    // ============================================

    /**
     * Create a new empty SceneObject.
     * Objects are parented to the implicit scene root by default.
     * If a parent is explicitly specified, the new object becomes a child of that parent.
     *
     * @param name - Optional name for the new object (auto-generated if not provided)
     * @param parent - Optional parent SceneObject ref. If not provided, parents to scene root.
     *                 Callers holding only a path resolve it via resolveSceneObject() first.
     * @param src - Optional source identifier stored on the created object's data.
     * @returns The created SceneObject
     */
    public async createSceneObject(name?: string, parent?: SceneObject, src?: string): Promise<SceneObject> {
        // Ensure scene root exists
        this.initializeSceneRoot();

        // Parent is a SceneObject REF, never a name: names are only
        // sibling-unique, so a name lookup (global first-match) can land on
        // a same-named node elsewhere in the scene. Callers holding only a
        // path resolve it explicitly via resolveSceneObject() first.
        // Default: scene root.
        const parentObject = parent ?? this._sceneRoot;

        if (parentObject && !this.canParentUnder(parentObject)) {
            throw this._vetoReferenceSubtreeParent(name || 'NewObject', parentObject);
        }

        // Ensure sibling-unique name (not global uniqueness)
        const siblingNames = this.getSiblingNamesByTransform(parentObject?.transform ?? null);
        const uniqueName = Utility.generateUniqueName(name || 'NewObject', siblingNames);

        // Create scene data for empty object
        const sceneData: SceneObjectData = {
            name: uniqueName,
            src: src || '',
            active: true,
            transform: {
                parent: parentObject?.name,
            },
            behaviors: [],
        };

        // Create the SceneObject using the same pattern as loadSceneObjects
        const sceneObject = SceneCreation.initializeSceneObject(
            sceneData,
            this.babylonScene,
            this.resources,
            this.config,
            this,
            this._eventSystem || undefined
        );

        this.sceneObjects.push(sceneObject);

        // A brand-new node in a 2.1 session gets a brand-new identity (#837).
        this._stampNodeUid(sceneObject, 'created');

        // Set parent (defaults to scene root if no explicit parent), then
        // preserve the new object's authored world rotation under a rotated
        // parent. Empty-create has no authored rotation, so world rotation
        // becomes identity — an image-anchor scene root pitched for a
        // Horizontal marker no longer tips spawned content along with it.
        if (parentObject) {
            sceneObject.setParent(parentObject);
            this._preserveAuthoredWorldRotation(sceneObject, parentObject);
        }

        // Attach behaviors (empty for new objects, but keeps pattern consistent)
        sceneObject._attachBehaviors();

        // Emit scene changed event
        this._emitSceneChanged([uniqueName]);
        // Creation "commits" only once behaviors are attached — the mid-create
        // setParent fired a topology signal too, but before this object's
        // graphs existed for subscribers to see.
        this._emitTopologyChanged([sceneObject]);

        Logger.log(`[TraceScene] Created new SceneObject: ${uniqueName}${parentObject ? ` (child of ${parentObject.name})` : ''}`);

        return sceneObject;
    }

    /**
     * Create a SceneObject from full SceneObjectData.
     * Used for duplication and other operations that need to create fully-configured objects.
     *
     * @param data - The SceneObjectData to create from (will be cloned)
     * @param parent - Optional parent SceneObject
     * @returns The created SceneObject. Its name is `data.name` made unique
     *   among its siblings, so read the name off the object.
     */
    public async createSceneObjectFromData(data: SceneObjectData, parent?: SceneObject): Promise<SceneObject> {
        if (parent && !this.canParentUnder(parent)) {
            throw this._vetoReferenceSubtreeParent(data.name, parent);
        }
        return this._restoreSceneObjectFromData(data, parent);
    }

    /**
     * @internal {@link createSceneObjectFromData} without the question whether
     * `parent` may hold authored children. For an undo that puts a node back
     * where it stood (`DeleteObjectCommand`): it was there, so the answer was
     * already given. Never for a new node.
     */
    public async _restoreSceneObjectFromData(data: SceneObjectData, parent?: SceneObject): Promise<SceneObject> {
        // Ensure scene root exists
        this.initializeSceneRoot();

        const authored = structuredClone(data);
        // Sibling names are unique, as on every other create path: data copied
        // from a sibling, or a generated node list that repeats a name, lands
        // as "Chair (1)". A restore whose name is still free keeps it.
        authored.name = Utility.generateUniqueName(
            authored.name,
            this.getSiblingNamesByTransform((parent ?? this._sceneRoot)?.transform ?? null),
        );
        // A duplicate is not a copy of the asset (issue #838): the node it was
        // made from is still in the scene, so this one is about to be minted a
        // brand-new identity, and `placedUids` — keyed by the identity the
        // UNPACKED node has — would map identities this object does not have
        // onto source nodes it was never made from. `placedFrom` stays: the
        // content really did come from that asset. A RESTORE (the source is
        // gone, the identity is free) keeps the whole record, because it is the
        // same node coming back.
        if (authored.uid !== undefined && this.sceneObjects.some(object => object.uid === authored.uid)) {
            for (const behavior of authored.behaviors ?? []) {
                if (behavior['@type'] === 'Provenance') delete (behavior as ProvenanceData).placedUids;
            }
        }

        const sceneObject = SceneCreation.initializeSceneObject(
            authored,
            this.babylonScene,
            this.resources,
            this.config,
            this,
            this._eventSystem || undefined
        );

        this.sceneObjects.push(sceneObject);

        // Identity: a DUPLICATE (the source is still in the scene) mints a new
        // one, a restore of something deleted keeps what it had — see
        // {@link _stampNodeUid} (issue #837).
        this._stampNodeUid(sceneObject, 'created');

        // Set parent. Unlike createSceneObject / loadFromPluginResult, no
        // counter-rotation here: serialized SceneObjectData already carries the
        // baked-in local rotation (the original create-time counter-rotation,
        // if any, was persisted). Re-applying would double the correction —
        // duplicates would drift relative to their source.
        if (parent) {
            sceneObject.setParent(parent);
        } else if (this._sceneRoot) {
            sceneObject.setParent(this._sceneRoot);
        }

        // Load the object (processes src, creates behaviors)
        // reload() internally calls _attachBehaviors(), so no extra call needed here.
        //
        // Bracketed: this awaits real I/O (an asset, a font), and a deferred
        // start event must not mistake that gap for "the scene is finished" —
        // see {@link _deferStartEvent}.
        this._beginConstruction();
        try {
            await sceneObject.reload();
        } finally {
            this._endConstruction();
        }

        // Creation "commits" only once behaviors exist — the mid-create
        // setParent above fired a topology signal, but before this object's
        // graphs were attached for subscribers to capture.
        this._emitTopologyChanged([sceneObject]);

        return sceneObject;
    }

    /**
     * Counter-rotate `sceneObject` against `parent`'s world rotation so the
     * object's authored *local* rotation reads as world rotation. When the
     * parent is identity in world space, this is a no-op.
     *
     * Used at create/import time: a fresh empty object has identity local
     * (so its world rotation becomes identity), and an imported asset
     * keeps whatever rotation the asset baked into its root transform.
     * Either way, the rotated parent (e.g. an image-anchor scene root
     * pitched for a Horizontal marker) doesn't tip the new content along
     * with it. Position is not adjusted — local (0,0,0) collapses to the
     * parent's origin in world space, which is the right anchor-relative
     * spawn point.
     */
    private _preserveAuthoredWorldRotation(sceneObject: SceneObject, parent: SceneObject): void {
        parent.transform.computeWorldMatrix(true);
        const parentWorldRot = parent.transform.absoluteRotationQuaternion;
        if (Quaternion.IsIdentity(parentWorldRot)) return;
        const authoredLocal = sceneObject.transform.rotationQuaternion
            ?? Quaternion.FromEulerVector(sceneObject.transform.rotation);
        sceneObject.transform.rotationQuaternion =
            Quaternion.Inverse(parentWorldRot).multiply(authoredLocal);
    }

    /**
     * Delete a SceneObject from the scene.
     * Also deletes all child objects recursively.
     *
     * @param target - SceneObject to delete, or transform path string
     * @returns true if deleted successfully, false if object not found
     */
    public deleteSceneObject(target: SceneObject | string): boolean {
        const sceneObject = typeof target === 'string'
            ? this.resolveSceneObject(target)
            : target;

        if (!sceneObject) {
            Logger.warn(`[TraceScene] Cannot delete: object not found`);
            return false;
        }

        // Collect all objects to delete (this object + descendants)
        const objectsToDelete: SceneObject[] = [sceneObject, ...sceneObject.getDescendants()];
        // Where the topology changed. Unknown when a pending move's report,
        // with the parent it left, dies with the object, or when the parent
        // is no scene object.
        const leftParent = sceneObject.getParent();
        const changedAt = objectsToDelete.some(o => o._reparentPending) || (!leftParent && sceneObject.transform.parent)
            ? undefined
            : leftParent ? [leftParent] : [];

        // Clear selection if deleting selected object or its ancestor
        if (this._selectedObject && objectsToDelete.includes(this._selectedObject)) {
            this.select(null);
        }

        // Delete in reverse order (children first)
        for (let i = objectsToDelete.length - 1; i >= 0; i--) {
            const objToDelete = objectsToDelete[i];

            // destroy() detaches behaviors BEFORE disposing the transform —
            // several (Text, ParticleSystem) own scene-level resources
            // (render observers, renderers) that live on the Babylon scene,
            // not the transform; disposing only the transform left them
            // alive and rendering (deleted Text kept drawing its paragraph).
            // Same teardown path clearScene() uses.
            objToDelete.destroy();

            // Remove from our array
            const index = this.sceneObjects.indexOf(objToDelete);
            if (index !== -1) {
                this.sceneObjects.splice(index, 1);
            }

            Logger.log(`[TraceScene] Deleted SceneObject: ${objToDelete.name}`);
        }

        // Emit scene changed event
        this._emitSceneChanged(objectsToDelete.map(o => o.name));
        this._emitTopologyChanged(changedAt);

        return true;
    }

    /**
     * @deprecated Use `sceneObject.getDescendants()` instead.
     */
    public getDescendants(sceneObject: SceneObject): SceneObject[] {
        return sceneObject.getDescendants();
    }

    /**
     * @deprecated Use `sceneObject.getChildren()` instead.
     */
    public getChildren(sceneObject: SceneObject): SceneObject[] {
        return sceneObject.getChildren();
    }

    /**
     * Open a single asset as the whole scene: clears the scene, loads the
     * asset, and starts it. The asset's own object is mounted under the engine
     * root, and `behaviors` are applied to that object.
     *
     * @param url - URL to the asset (GLB, JPG, PNG, MP4)
     * @param behaviors - Optional behaviors to apply
     * @param prefetched - Bytes the caller already downloaded for this URL
     */
    public async openAsRoot(
        url: string,
        behaviors?: BehaviorData[],
        onDownloadProgress?: (loaded: number, total: number) => void,
        progressTracker?: IProgressTracker,
        prefetched?: { blob: Blob; fileName: string; assetBaseUrl: string },
        fetchOptions?: AssetFetchOptions,
    ): Promise<void> {
        Logger.log(`[TraceScene] openAsRoot: ${url} with ${behaviors?.length ?? 0} extra behaviors: ${behaviors?.map(b => b['@type']).join(', ') ?? 'none'}`);

        // Fetch the asset (with optional download progress) unless the caller
        // already has the bytes — TraceViewer.open downloads a `.gltf` before
        // it can sniff which loader it belongs to.
        const { blob, fileName, assetBaseUrl } = prefetched
            ?? (onDownloadProgress
                ? await this.fetchAssetWithProgress(url, onDownloadProgress, fetchOptions)
                : await this.fetchAsset(url, fetchOptions));

        // Clear scene (disposes everything and creates fresh empty root)
        this.clearScene();

        // The single-asset path: the asset's own object is the root entity a
        // caller's `behaviors` land on (§5.1, row 2), and `extraBehaviors`
        // put them there. Nothing is dropped and nothing needs warning
        // about — a document that reaches here keeps its root node like any
        // other content (D8).
        await this._loadFromBlob(blob, fileName, undefined, progressTracker, undefined, undefined, behaviors, assetBaseUrl, false);

        // Start the scene (camera framing, render loop, etc.)
        await this.startScene();

        Logger.log(`[TraceScene] Opened asset as root: ${fileName}`);
    }

    /**
     * Clear all scene objects and reset the scene for new content.
     * Preserves the Babylon Scene object (and its render loop) so that
     * external consumers like the Inspector stay alive across transitions.
     */
    public clearScene(): void {
        // Reset play mode and rendering state so they re-initialize on next load
        this._playModeInitialized = false;
        this._renderingStarted = false;

        // Clear selection
        this.select(null);

        // Collect all object names for event emission
        const objectNames = this.sceneObjects.map(obj => obj.name);

        // Destroy all SceneObjects in reverse order (children before root)
        // so behaviors detach cleanly before their parent transforms are disposed.
        for (let i = this.sceneObjects.length - 1; i >= 0; i--) {
            this.sceneObjects[i].destroy();
        }
        this._sceneRoot = null;
        this._sessionDocumentRoot = null;
        this.sceneObjects = [];

        // Clear blob resources
        this.resources.clearAssets();

        // Clear script registry
        this._scriptRegistry.clear();
        this._scriptsMap = undefined;

        // Belongs to the document that was open, not to the next one.
        this._foreignDocument = undefined;

        // …and so does what the scene was OPENED from: whatever comes next is a
        // new session until a scene-establishing load says otherwise (§2.3).
        this._sceneSourceFormat = '';

        // Dispose runtime selection manager
        if (this._runtimeSelectionManager) {
            this._runtimeSelectionManager.dispose();
            this._runtimeSelectionManager = null;
        }

        // Dispose audio manager (closes AudioContext, stops listener sync)
        if (this._audioManager) {
            this._audioManager.dispose();
            this._audioManager = null;
        }

        // Dispose contact shadow (releases observers + GPU resources)
        if (this._contactShadow) {
            this._contactShadow.dispose();
            this._contactShadow = null;
        }

        // Safety-net: dispose orphaned Babylon nodes/resources not tracked by SceneObjects.
        [...this.babylonScene.effectLayers].forEach(el => el.dispose());
        [...this.babylonScene.particleSystems].forEach(ps => ps.dispose());
        [...this.babylonScene.animationGroups].forEach(ag => ag.dispose());
        [...this.babylonScene.meshes].forEach(m => m.dispose());
        [...this.babylonScene.transformNodes].forEach(tn => tn.dispose());
        [...this.babylonScene.materials].forEach(m => m.dispose());
        // Not the environment map: it is the viewer's lighting, not content,
        // and setupLights() below keeps it when the file is the same.
        // Disposed here it was requested a second time, and while the first
        // request was in flight its pending entry leaked (see
        // disposeEnvironmentTexture).
        const environment = this.babylonScene.environmentTexture;
        [...this.babylonScene.textures].filter(t => t !== environment).forEach(t => t.dispose());
        [...this.babylonScene.lights].forEach(l => l.dispose());

        // Reset scene-level caches that reference disposed objects.
        // Depth renderers: keyed by camera id and lazily created by enableDepthRenderer().
        // The texture sweep above disposed their render targets, so the cached instances
        // are stale. Clearing the map ensures the next enableDepthRenderer() creates fresh ones.
        const depthRenderers = this.babylonScene._depthRenderer;
        if (depthRenderers) {
            for (const key of Object.keys(depthRenderers)) {
                delete depthRenderers[key];
            }
        }
        // environmentBRDFTexture: PBR materials lazily create a BRDF LUT texture
        // and store it on the scene. If we disposed it above but leave the reference,
        // new PBR materials receive a dead texture and silently fail to render.
        // @ts-expect-error Babylon types this as non-nullable, but we must clear the stale reference
        this.babylonScene.environmentBRDFTexture = null;
        this.babylonScene.resetCachedMaterial();
        this.babylonScene.freeProcessedMaterials();
        this.babylonScene.freeActiveMeshes();
        this.babylonScene.freeRenderingGroups();

        // Re-setup lights and environment texture
        this.setupLights();

        // Reinitialize a fresh empty scene root
        this.initializeSceneRoot();

        // Emit scene changed event
        if (objectNames.length > 0) {
            this._emitSceneChanged(objectNames);
        }

        Logger.log(`[TraceScene] Cleared scene (removed ${objectNames.length} objects)`);
    }

    /**
     * Dispose all resources and the underlying Babylon Scene.
     * After calling this, the TraceScene instance is no longer usable.
     */
    public dispose(): void {
        this._cancelStartEvents();
        this.clearScene();
        this._occluderMode.dispose();
        this.babylonScene.dispose();
    }

    /**
     * Report XR session state. An XR session is always "playing" for
     * occluders: depth-only rendering switches on for the session even when
     * the editor is otherwise in edit mode, and back off when it ends.
     */
    public setXRSessionActive(active: boolean): void {
        this._occluderMode.setXRActive(active);
    }

    /** True while occluder materials render depth-only (play mode or an XR session). */
    public isOccluderDepthOnly(): boolean {
        return this._occluderMode.depthOnly;
    }

    /**
     * Frame the camera to fit the scene content.
     *
     * NOTE: We intentionally skip scene.whenReadyAsync() here and elsewhere.
     * PBRMaterial.isReady(mesh) returns false even after multiple render passes,
     * so whenReadyAsync() hangs indefinitely on PBR-heavy scenes. Babylon
     * compiles shaders on-demand and renders correctly without waiting.
     */
    public frameScene(): void {
        if (this.camera) {
            SceneCamera.calculateSceneBoundingBox(this.babylonScene, this.camera, this.config, this._safeAreaInsets);
        }
    }

    /**
     * Attach camera controls, render the first frame, and start the render
     * loop. Separated from startScene() so that callers can load content
     * without immediately rendering (e.g. preloading in a hidden element).
     * Idempotent — subsequent calls are no-ops.
     */
    public startRendering(): void {
        if (this._renderingStarted) return;
        this._renderingStarted = true;

        if (this.camera) {
            this.camera.attachControl(this.canvas, true);
        }

        this.babylonScene.render();

        this.engine.stopRenderLoop();
        this.engine.runRenderLoop(() => {
            if (this.babylonScene && this.camera) {
                // Self-heal the active camera: Babylon parks scene.activeCamera
                // on an RTT helper (ContactShadow's ortho cam, thumbnail cams)
                // during the render-target phase and restores it afterwards —
                // but an exception anywhere mid-frame skips the restore and the
                // scene then renders from the helper forever (a top-down
                // "collapsed" view). Helper cameras are never a valid frame
                // camera, so reclaim before rendering.
                const active = this.babylonScene.activeCamera;
                if (active !== this.camera && active?.name.startsWith('__')) {
                    this.babylonScene.activeCamera = this.camera;
                }
                this.babylonScene.render();
            }
        });
    }

    /**
     * Initialise the scene after content has been loaded: scene root, background,
     * pickability, and start interactions. Does NOT start rendering — call
     * startRendering() separately (or let TraceViewer.open() handle it).
     */
    public async startScene() {
        // Initialize the implicit scene root (if not already done)
        this.initializeSceneRoot();

        Logger.log("Files loaded successfully!");
        Logger.log(`alpha is ${this.alpha}`);

        if (this.backgroundColor) {
            if (!this.alpha) this.alpha = 1;
            const color = Color3.FromHexString(this.backgroundColor);
            this.babylonScene.clearColor = new Color4(color.r, color.g, color.b, this.alpha);
        } else {
            this.alpha = this.alpha || 0.01;
            this.babylonScene.clearColor = new Color4(0, 0, 0, this.alpha);
        }

        // Initialize mesh pickability based on current mode
        this._updateMeshPickability(this._playMode);

        // Apply click routing based on current mode.
        this._applyClickToSelect();

        // The scene is built, so anything a graph deferred to the settle-timer
        // can run NOW — before the first frame of the newly loaded scene, and
        // before scripts' start, which is the order graphs and scripts had when
        // a graph started inline on attach. Every caller of startScene() is a
        // top-level "content is loaded" point (loadFromURL, loadFromBlob,
        // openAsRoot, the empty-scene path); nested loads never reach it.
        this._flushStartEvents();

        // Fire start interactions if play mode is active (default for viewer-app).
        if (this._playMode && !this._playModeInitialized) {
            this._playModeInitialized = true;
            this._fireStartInteractions();
        }

        this._alignShadowToContentBottom();
    }

}
